import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Animated, Easing, TextInput, Image, Dimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import NavigationHeader from '../components/NavigationHeader';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient, GradientOverlay } from '../components/Gradients';
import { ScreenshotIcon, SmallLogoIcon } from './HomeScreen';
import MaskedView from '@react-native-masked-view/masked-view';
import GradientButton from '../components/GradientButton';
import BetSlipModal from '../components/BetSlipModal';
import LegBetCard from '../components/LegBetCard';
import SGPCard from '../components/SGPCard';
import BetSlipSubmissionScreen from '../components/BetSlipSubmissionScreen';
import SavedRequestsModal from '../components/SavedRequestsModal';
import QueryLimitModal from '../components/QueryLimitModal';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { db, auth } from '../firebaseConfig';
import Constants from 'expo-constants';
import { useAuth } from '../context/AuthContext';
import { collection, addDoc, serverTimestamp, doc, setDoc, getDocs, query, orderBy, limit, where, deleteDoc } from 'firebase/firestore';
import { getTeamLogo, getSportsbookLogo } from '../data/logos';
import { queryWithCacheFallback, queryCachedSGPs } from '../services/cacheService';

// Mock data structure for SGP legs
const mockSgpData = {
    totalLegs: 3,
    legs: [
        {
            id: 1,
            title: "Kevin Durant Points",
            description: "Bet on the total points scored by single player",
            options: [
                {
                    id: 1,
                    title: "NBA: Kevin Durant to score 20+ points",
                    description: "Bet on the total points scored by single player",
                    amount: "+$100",
                    odds: 1.85
                },
                {
                    id: 2,
                    title: "NBA: Kevin Durant to score 25+ points",
                    description: "Bet on the total points scored by single player",
                    amount: "+$150",
                    odds: 2.20
                },
                {
                    id: 3,
                    title: "NBA: Kevin Durant to score 35+ points",
                    description: "Bet on the total points scored by single player",
                    amount: "+$50",
                    odds: 3.50
                }
            ]
        },
        {
            id: 2,
            title: "LeBron James Assists",
            description: "Bet on the total assists by single player",
            options: [
                {
                    id: 4,
                    title: "NBA: LeBron James to get 8+ assists",
                    description: "Bet on the total assists by single player",
                    amount: "+$120",
                    odds: 1.90
                },
                {
                    id: 5,
                    title: "NBA: LeBron James to get 10+ assists",
                    description: "Bet on the total assists by single player",
                    amount: "+$200",
                    odds: 2.50
                },
                {
                    id: 6,
                    title: "NBA: LeBron James to get 12+ assists",
                    description: "Bet on the total assists by single player",
                    amount: "+$80",
                    odds: 4.00
                }
            ]
        },
        {
            id: 3,
            title: "Lakers Team Total",
            description: "Bet on the team's total points scored",
            options: [
                {
                    id: 7,
                    title: "NBA: Lakers to score 110+ points",
                    description: "Bet on the team's total points scored",
                    amount: "+$90",
                    odds: 1.75
                },
                {
                    id: 8,
                    title: "NBA: Lakers to score 120+ points",
                    description: "Bet on the team's total points scored",
                    amount: "+$180",
                    odds: 2.10
                },
                {
                    id: 9,
                    title: "NBA: Lakers to score 130+ points",
                    description: "Bet on the team's total points scored",
                    amount: "+$60",
                    odds: 3.20
                }
            ]
        }
    ]
};

const MAX_LEGS = 3;

// Helper: calculate combined SGP math for selected legs
const calculateSgpMath = (selectedLegs, stake = 10, betMode = 'sgp') => {
    if (!selectedLegs || selectedLegs.length === 0) return null;
    
    // Extract probabilities and prices (use BEST odds from all books)
    const legs = selectedLegs.map((leg, idx) => {
        // Calculate best odds from all available books (same logic as LegBetCard display)
        let price = null;
        if (leg?.bookOdds && Object.keys(leg.bookOdds).length > 0) {
            const allOdds = Object.values(leg.bookOdds).filter(o => o !== null && o !== undefined);
            if (allOdds.length > 0) {
                const best = allOdds.reduce((best, current) => {
                    if (current > 0 && best > 0) return current > best ? current : best;
                    if (current < 0 && best < 0) return current > best ? current : best;
                    if (current > 0) return current;
                    if (best > 0) return best;
                    return current > best ? current : best;
                }, allOdds[0]);
                price = typeof best === 'number' ? best : parseFloat(String(best).replace(/[^-0-9.]/g, ''));
                console.log(`[calculateSgpMath] Leg ${idx}: Using best odds from ${allOdds.length} books: ${price} (from ${allOdds.join(', ')})`);
            }
        }
        // Fallback to raw price if bookOdds not available
        if (price === null) {
            price = leg?.raw?.price ?? leg?.raw?.american_price ?? null;
            console.log(`[calculateSgpMath] Leg ${idx}: Fallback to raw.price: ${price}`);
        }
        
        const aiProb = leg?.raw?.ai_prob ?? leg?.ai_prob ?? null;
        let implied = null;
        if (typeof price === 'number' && !isNaN(price)) {
            implied = price > 0 ? (100 / (price + 100)) : ((-price) / ((-price) + 100));
        }
        return { price, aiProb, implied, fixtureId: leg?.raw?.fixture_id || leg?.raw?.game_id };
    });
    
    // For SGPx mode, separate SGP legs (same fixture) from straight legs (different fixtures)
    let sgpLegs = legs;
    let straightLegs = [];
    
    if (betMode === 'sgpx' && legs.length > 1) {
        // Group legs by fixture
        const fixtureGroups = {};
        legs.forEach((leg, idx) => {
            const fixId = leg.fixtureId;
            if (!fixtureGroups[fixId]) fixtureGroups[fixId] = [];
            fixtureGroups[fixId].push({ ...leg, originalIndex: idx });
        });
        
        const fixtureIds = Object.keys(fixtureGroups);
        if (fixtureIds.length > 1) {
            // Find the largest group (SGP legs) and treat others as straight
            const largestGroup = Object.values(fixtureGroups).reduce((max, group) => 
                group.length > max.length ? group : max, []);
            sgpLegs = largestGroup;
            straightLegs = legs.filter((_, idx) => 
                !largestGroup.some(leg => leg.originalIndex === idx));
        }
    }
    
    // Calculate SGP probability (multiply SGP legs)
    const sgpProb = sgpLegs.every(l => l.aiProb) 
        ? sgpLegs.reduce((acc, l) => acc * l.aiProb, 1) 
        : null;
    
    // Calculate straight leg probabilities (individual)
    const straightProbs = straightLegs.map(l => l.aiProb).filter(p => p !== null);
    
    // Combined probability (SGP * straight legs)
    const independentProb = sgpProb && straightProbs.length > 0
        ? sgpProb * straightProbs.reduce((acc, p) => acc * p, 1)
        : sgpProb;
    
    // Calculate SGP odds
    const sgpDecimalOdds = sgpLegs.map(l => {
        if (l.price === null || isNaN(l.price)) return null;
        return l.price > 0 ? (1 + l.price / 100) : (1 + 100 / (-l.price));
    });
    const sgpCombinedDecimal = sgpDecimalOdds.every(o => o !== null) 
        ? sgpDecimalOdds.reduce((acc, o) => acc * o, 1) 
        : null;
    
    // Debug logging for SGP calculation
    if (sgpLegs.length >= 2) {
        console.log('[calculateSgpMath] SGP Calculation:', {
            numLegs: sgpLegs.length,
            prices: sgpLegs.map(l => l.price),
            decimalOdds: sgpDecimalOdds,
            combinedDecimal: sgpCombinedDecimal
        });
    }
    
    // Calculate straight leg odds
    const straightDecimalOdds = straightLegs.map(l => {
        if (l.price === null || isNaN(l.price)) return null;
        return l.price > 0 ? (1 + l.price / 100) : (1 + 100 / (-l.price));
    });
    const straightCombinedDecimal = straightDecimalOdds.every(o => o !== null) 
        ? straightDecimalOdds.reduce((acc, o) => acc * o, 1) 
        : null;
    
    // Total combined odds
    const combinedDecimal = sgpCombinedDecimal && straightCombinedDecimal
        ? sgpCombinedDecimal * straightCombinedDecimal
        : sgpCombinedDecimal || straightCombinedDecimal;
    
    const combinedAmerican = combinedDecimal 
        ? (combinedDecimal >= 2 ? Math.round((combinedDecimal - 1) * 100) : Math.round(-100 / (combinedDecimal - 1)))
        : null;
    
    // Payout
    const payout = combinedDecimal ? stake * combinedDecimal : null;
    const profit = payout ? payout - stake : null;
    
    // EV calculation
    const ev = (independentProb && combinedDecimal) 
        ? (independentProb * combinedDecimal * stake - stake) 
        : null;
    const evPercent = ev !== null ? ((ev / stake) * 100) : null;
    
    return {
        legs: legs.length,
        sgpLegs: sgpLegs.length,
        straightLegs: straightLegs.length,
        independentProb,
        combinedDecimal,
        combinedAmerican,
        stake,
        payout,
        profit,
        ev,
        evPercent,
        betType: betMode === 'sgpx' ? 'SGPx' : 'SGP'
    };
};

const SgpFlowScreen = () => {
    // Navigation context
    const { goBack, routePayload, navigateTo } = useNavigation();
    const { userProfile, updateUserProfile, user } = useAuth();
    const { isTickerEnabled, totalHeaderHeight } = useTicker();
    const { sendToBackground, canAddBackgroundQuery, IS_MOBILE, registerForegroundQuery, completeForegroundQuery } = useBackgroundQuery();
    
    // Navigation state
    const [currentLeg, setCurrentLeg] = useState(0); // 0-based index
    const [selectedOptions, setSelectedOptions] = useState({}); // { legId: optionId }
    
    // Selection state for current leg
    const [selectedCard, setSelectedCard] = useState(null); // 0 = first card selected by default
    const [pendingQuery, setPendingQuery] = useState('');
    const [fetchedOptions, setFetchedOptions] = useState([]); // normalized LegBetCard options from bOPT AI
    const [selectedLegs, setSelectedLegs] = useState([]); // selected legs
    const [loading, setLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true); // Prevent flash of content before query starts
    
    // Query limit tracking
    const [showQueryLimitModal, setShowQueryLimitModal] = useState(false);
    const [queryUsage, setQueryUsage] = useState({ queriesUsed: 0, queryLimit: 3, planType: 'FREE' });
    
    // Watched fixtures tracking
    const [watchedFixtures, setWatchedFixtures] = useState(new Set()); // Set of fixture IDs being watched
    
    // Sync sortMethod with user preference when it changes
    useEffect(() => {
        const userDefault = userProfile?.preferences?.defaultSortMethod;
        if (userDefault && (userDefault === 'ev' || userDefault === 'odds')) {
            setSortMethod(userDefault);
            setSortBy(userDefault === 'odds' ? 'price_desc' : 'ev');
        }
    }, [userProfile?.preferences?.defaultSortMethod]);
    
    const [progress, setProgress] = useState(0);
    const [currentPage, setCurrentPage] = useState(1); // Pagination: current page
    const itemsPerPage = userProfile?.preferences?.legsPerPage || 10;
    const [showAllMathLocal, setShowAllMathLocal] = useState(false); // Local toggle for Show All Math
    const [progressText, setProgressText] = useState('');
    const [avgMs, setAvgMs] = useState(8000); // Start with realistic default
    const [fetchingAiProbs, setFetchingAiProbs] = useState(false);
    const [hasStartedSearch, setHasStartedSearch] = useState(false); // Track if search has actually started
    const [fixturesFoundNoOdds, setFixturesFoundNoOdds] = useState(null); // Track when fixtures exist but have no odds
    const [searchStatus, setSearchStatus] = useState({ step: '', fixturesFound: 0, elapsedMs: 0 }); // Detailed search status
    const [sgpGenerationStats, setSgpGenerationStats] = useState(null); // Stats about SGP generation (combinations_tested, markets_used)
    const progressIntervalRef = useRef(null); // Track progress interval to prevent duplicates
    const [betMode, setBetMode] = useState(() => {
        if (Platform.OS === 'web') {
            try {
                const stored = window.sessionStorage.getItem('sgp:mode');
                if (stored) return stored;
            } catch {}
        }
        // Use user's default bet mode preference if available
        return userProfile?.preferences?.defaultBetMode || 'infer';
    }); // 'infer' | 'sgp' | 'straight' | 'sgpx'
    const [builderStake, setBuilderStake] = useState(10);
    const [activeFixtureId, setActiveFixtureId] = useState(null);
    const [lastNotice, setLastNotice] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [noResults, setNoResults] = useState(false);
    const [didBroaden, setDidBroaden] = useState(false);
    const [showRawLog, setShowRawLog] = useState(false);
    const lastRawRef = useRef('');
    const abortRef = useRef(null);
    
    // Book-specific override state
    const [bookOverride, setBookOverride] = useState(null); // null = use defaults, string = specific book
    const [showBookSelector, setShowBookSelector] = useState(false);
    
    // Saved requests state
    const [showSavedRequests, setShowSavedRequests] = useState(false);
    
    // Share betslip state
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const mountedRef = useRef(false);
    const [searchedSportsbooks, setSearchedSportsbooks] = useState([]);
    const [detectedPromotion, setDetectedPromotion] = useState(null);
    const [queryFeedback, setQueryFeedback] = useState(null); // 'up' | 'down' | null
    const [currentQueryMetricId, setCurrentQueryMetricId] = useState(null);
    
    // Filter states
    const [filterSportsbooks, setFilterSportsbooks] = useState([]);
    const [filterMarkets, setFilterMarkets] = useState([]);
    const [filterDeepLinksOnly, setFilterDeepLinksOnly] = useState(false); // Only show bets with deep links
    // Sort method: 'ev' for Best EV%, 'odds' for Best Odds (uses user preference as default)
    const defaultSort = userProfile?.preferences?.defaultSortMethod || 'ev';
    const [sortMethod, setSortMethod] = useState(defaultSort); // 'ev' | 'odds'
    const [sortBy, setSortBy] = useState(defaultSort === 'odds' ? 'price_desc' : 'ev'); // 'price_asc' | 'price_desc' | 'ev' | 'date' | 'market'
    const [searchText, setSearchText] = useState('');
    
    // Modal state
    const [showBetSlipModal, setShowBetSlipModal] = useState(false);
    const [showSubmissionScreen, setShowSubmissionScreen] = useState(false);
    
    // Animation values for leg number buttons (individual for each button)
    const legNumberGlowT = useRef(
        Array.from({ length: mockSgpData.totalLegs }, () => new Animated.Value(0))
    ).current;

    // Page animation values
    const pageOpacity = useRef(new Animated.Value(0)).current;
    const pageTranslateY = useRef(new Animated.Value(20)).current;

    // Get current leg data
    const currentLegData = mockSgpData.legs[currentLeg];
    const currentLegOptions = currentLegData?.options || [];

    // Navigation functions
    const goToNextLeg = () => {
        if (currentLeg < mockSgpData.totalLegs - 1) {
            // Save current selection
            if (currentLegOptions[selectedCard]) {
                setSelectedOptions(prev => ({
                    ...prev,
                    [currentLegData.id]: currentLegOptions[selectedCard].id
                }));
            }
            
            setCurrentLeg(prev => prev + 1);
            // selectedCard will be restored by useEffect
        }
    };

    const goToPreviousLeg = () => {
        if (currentLeg > 0) {
            setCurrentLeg(prev => prev - 1);
            // selectedCard will be restored by useEffect
        }
    };

    // Check if all legs have selected options
    const isAllLegsSelected = () => {
        // Check if all previous legs have selections
        const previousLegsSelected = mockSgpData.legs.slice(0, currentLeg).every(leg => selectedOptions[leg.id] !== undefined);
        
        // Check if current leg has a selection (either saved or currently selected)
        const currentLegSelected = selectedOptions[currentLegData?.id] !== undefined || selectedCard !== undefined;
        
        return previousLegsSelected && currentLegSelected;
    };

    // Handle finish action
    const handleFinish = () => {
        if (isAllLegsSelected()) {
            // Save current selection before finishing
            if (currentLegOptions[selectedCard]) {
                setSelectedOptions(prev => ({
                    ...prev,
                    [currentLegData.id]: currentLegOptions[selectedCard].id
                }));
            }
            
            // Force open the bet slip modal
            setShowBetSlipModal(true);
        }
    };

    // Update selected card when leg changes
    useEffect(() => {
        // Check if there's a previously selected option for this leg
        const currentLegId = currentLegData?.id;
        if (currentLegId && selectedOptions[currentLegId]) {
            // Find the index of the selected option in the current leg's options
            const selectedOptionId = selectedOptions[currentLegId];
            const optionIndex = currentLegOptions.findIndex(option => option.id === selectedOptionId);
            if (optionIndex !== -1) {
                setSelectedCard(optionIndex);
            } else {
                setSelectedCard(null);
            }
        } else {
            setSelectedCard(null);
        }
    }, [currentLeg, selectedOptions, currentLegData, currentLegOptions]);
    

    // Leg number button hover animations
    const onLegNumberIn = (index) => {
        Animated.timing(legNumberGlowT[index], { toValue: 1, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
    };

    const onLegNumberOut = (index) => {
        Animated.timing(legNumberGlowT[index], { toValue: 0, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
    };
    
    // Card selection handlers (legacy mock flow)
    const handleCardPress = (cardIndex) => {
        setSelectedCard(cardIndex);
        setSelectedOptions(prev => ({
            ...prev,
            [currentLegData.id]: currentLegOptions[cardIndex].id
        }));
    };

    // Normalize dates in query (expand relative dates to actual dates)
    const normalizeDates = (query) => {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        
        const dateReplacements = {
            'tonight': today.toISOString().split('T')[0],
            'today': today.toISOString().split('T')[0],
            'tomorrow': tomorrow.toISOString().split('T')[0],
        };
        
        let normalized = query;
        for (const [term, date] of Object.entries(dateReplacements)) {
            const regex = new RegExp(`\\b${term}\\b`, 'gi');
            if (query.toLowerCase().match(regex)) {
                // Keep the original term but add the date
                normalized = normalized.replace(regex, `${term} (${date})`);
            }
        }
        
        return normalized;
    };

    // Normalize team names in query (expand abbreviations to full names)
    const normalizeQuery = (query) => {
        // First normalize dates
        let normalized = normalizeDates(query);
        
        const teamMap = {
            // NBA Teams
            'warriors': 'Golden State Warriors',
            'gsw': 'Golden State Warriors',
            'lakers': 'Los Angeles Lakers',
            'lal': 'Los Angeles Lakers',
            'celtics': 'Boston Celtics',
            'bos': 'Boston Celtics',
            'heat': 'Miami Heat',
            'mia': 'Miami Heat',
            'bucks': 'Milwaukee Bucks',
            'mil': 'Milwaukee Bucks',
            'nuggets': 'Denver Nuggets',
            'den': 'Denver Nuggets',
            'suns': 'Phoenix Suns',
            'phx': 'Phoenix Suns',
            'mavs': 'Dallas Mavericks',
            'mavericks': 'Dallas Mavericks',
            'dal': 'Dallas Mavericks',
            'sixers': 'Philadelphia 76ers',
            '76ers': 'Philadelphia 76ers',
            'phi': 'Philadelphia 76ers',
            'clippers': 'Los Angeles Clippers',
            'lac': 'Los Angeles Clippers',
            'nets': 'Brooklyn Nets',
            'bkn': 'Brooklyn Nets',
            'hawks': 'Atlanta Hawks',
            'atl': 'Atlanta Hawks',
            'bulls': 'Chicago Bulls',
            'chi': 'Chicago Bulls',
            'cavs': 'Cleveland Cavaliers',
            'cavaliers': 'Cleveland Cavaliers',
            'cle': 'Cleveland Cavaliers',
            'grizzlies': 'Memphis Grizzlies',
            'mem': 'Memphis Grizzlies',
            'hornets': 'Charlotte Hornets',
            'cha': 'Charlotte Hornets',
            'jazz': 'Utah Jazz',
            'uta': 'Utah Jazz',
            'kings': 'Sacramento Kings',
            'sac': 'Sacramento Kings',
            'spurs': 'San Antonio Spurs',
            'sas': 'San Antonio Spurs',
            'thunder': 'Oklahoma City Thunder',
            'okc': 'Oklahoma City Thunder',
            'blazers': 'Portland Trail Blazers',
            'por': 'Portland Trail Blazers',
            'rockets': 'Houston Rockets',
            'hou': 'Houston Rockets',
            'pelicans': 'New Orleans Pelicans',
            'nop': 'New Orleans Pelicans',
            'knicks': 'New York Knicks',
            'nyk': 'New York Knicks',
            'raptors': 'Toronto Raptors',
            'tor': 'Toronto Raptors',
            'pacers': 'Indiana Pacers',
            'ind': 'Indiana Pacers',
            'pistons': 'Detroit Pistons',
            'det': 'Detroit Pistons',
            'magic': 'Orlando Magic',
            'orl': 'Orlando Magic',
            'wizards': 'Washington Wizards',
            'was': 'Washington Wizards',
            'timberwolves': 'Minnesota Timberwolves',
            'wolves': 'Minnesota Timberwolves',
            'min': 'Minnesota Timberwolves',
        };
        
        const queryLower = normalized.toLowerCase();
        
        // Replace team abbreviations with full names
        for (const [abbrev, fullName] of Object.entries(teamMap)) {
            // Match whole words only (with word boundaries)
            const regex = new RegExp(`\\b${abbrev}\\b`, 'gi');
            if (queryLower.match(regex)) {
                normalized = normalized.replace(regex, fullName);
                break; // Only replace first match to avoid conflicts
            }
        }
        
        return normalized;
    };

    // Check query limits and execute if allowed
    const handleExecute = async (query) => {
        if (!query || !query.trim()) return;
        
        // Get functions base URL
        const isLocalhost = Platform.OS === 'web' && 
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        
        const base = isLocalhost
            ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
            : (
                (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
                || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
                || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net'
            );
        
        try {
            // Check query limit before executing
            const checkResponse = await fetch(`${base}/checkAndIncrementQuery`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uid: user?.uid || null,
                    ipAddress: null, // Server will use req.ip
                    hasResults: false, // Don't increment yet, just check
                })
            });
            
            if (checkResponse.ok) {
                const checkData = await checkResponse.json();
                setQueryUsage(checkData);
                
                if (!checkData.allowed) {
                    // Show limit modal
                    setShowQueryLimitModal(true);
                    return;
                }
            }
            
            // Execute the query
            await executeQuery(query.trim());
            
        } catch (error) {
            console.error('[handleExecute] Error checking query limit:', error);
            // If limit check fails, still allow the query (fail open)
            await executeQuery(query.trim());
        }
    };
    
    // Track successful query after results are received
    const trackQuerySuccess = async (hasResults) => {
        const isLocalhost = Platform.OS === 'web' && 
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        
        const base = isLocalhost
            ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
            : (
                (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
                || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
                || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net'
            );
        
        try {
            // Only increment if query returned results
            const trackResponse = await fetch(`${base}/checkAndIncrementQuery`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uid: user?.uid || null,
                    ipAddress: null,
                    hasResults: hasResults,
                })
            });
            
            if (trackResponse.ok) {
                const trackData = await trackResponse.json();
                setQueryUsage(trackData);
                console.log('[trackQuerySuccess] Updated usage:', trackData);
            }
        } catch (error) {
            console.error('[trackQuerySuccess] Error tracking query:', error);
        }
    };

    // Core executor for nlOdds query
    const executeQuery = async (q, { broaden = false } = {}) => {
        if (!q) return;
        
        // Initialize search logs for debugging and metrics
        const searchLogs = [];
        const logEvent = (event, data = {}) => {
            const logEntry = { timestamp: Date.now(), event, ...data };
            searchLogs.push(logEntry);
            console.log('[SGP Log]', event, data);
        };
        
        // Normalize the query to expand team abbreviations
        const normalizedQuery = normalizeQuery(q);
        console.log('[SGP] Original query:', q);
        console.log('[SGP] Normalized query:', normalizedQuery);
        logEvent('search_started', { originalQuery: q, normalizedQuery, broaden });
        
        setErrorMsg('');
        setNoResults(false);
        setIsInitializing(false); // Done initializing, now showing loading state
        setLoading(true);
        setHasStartedSearch(true); // Mark that we've started a search
        
        // ⭐ Register this query for history tracking
        registerForegroundQuery(q, { betMode, broaden });
        setFixturesFoundNoOdds(null); // Reset marker for new search
        setSgpGenerationStats(null); // Clear previous stats
        setProgress(0); setProgressText('Understanding request…');
        if (!broaden) {
            setFetchedOptions([]);
            setSelectedLegs([]);
            setActiveFixtureId(null);
            setLastNotice('');
            setCurrentPage(1); // Reset pagination on new search
            // Reset filters on new search
            setFilterSportsbooks([]);
            setFilterMarkets([]);
            setFilterDeepLinksOnly(false);
            setSearchText('');
            // Reset to user's default sort method
            const userDefault = userProfile?.preferences?.defaultSortMethod || 'ev';
            setSortMethod(userDefault);
            setSortBy(userDefault === 'odds' ? 'price_desc' : 'ev');
        }
        lastRawRef.current = '';
        mountedRef.current = true;

        try {
            // Clear any existing progress interval to prevent jumping
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
            }
            
            // Progress animation with asymptotic curve (never stalls, slows down naturally)
            const startTs = Date.now();
            // Use actual average time for accurate progress (no cap)
            const target = Math.max(5000, avgMs); // Minimum 5 seconds, no maximum cap
            const interval = setInterval(() => {
                const elapsed = Date.now() - startTs;
                
                // Asymptotic progress: approaches 100% but never reaches it
                // Formula: 100 * (1 - e^(-3 * elapsed/target))
                // This creates smooth deceleration without hard stops
                const ratio = elapsed / target;
                const pct = Math.floor(100 * (1 - Math.exp(-3 * ratio)));
                
                setProgress(Math.min(99, pct));
                setSearchStatus(prev => ({ ...prev, elapsedMs: elapsed }));
            }, 100);
            progressIntervalRef.current = interval;
            // Determine functions URL: use emulator on localhost, production otherwise
            const isLocalhost = Platform.OS === 'web' && 
                (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
            
            const base = isLocalhost
                ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1' // Emulator
                : (
                    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
                    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
                    || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net' // Production
                );
            console.log('[SGP] Functions base URL:', base, '(localhost:', isLocalhost, ')');
            setProgressText('Understanding your query...');
            setSearchStatus({ step: 'Analyzing request', fixturesFound: 0, elapsedMs: 0 });
            const controller = new AbortController();
            abortRef.current = controller;
            
            // Smart sportsbook detection: check if query mentions a specific sportsbook
            const sportsbookKeywords = {
                'fanduel': 'FanDuel',
                'draftkings': 'DraftKings',
                'caesars': 'Caesars',
                'betmgm': 'BetMGM',
                'bet mgm': 'BetMGM',
                'pointsbet': 'PointsBet',
                'betrivers': 'BetRivers',
                'bet rivers': 'BetRivers',
                'wynnbet': 'WynnBet',
                'wynn': 'WynnBet',
                'barstool': 'Barstool',
                'foxbet': 'FOX Bet',
                'fox bet': 'FOX Bet',
                'unibet': 'Unibet',
                'betfred': 'Betfred',
                'superbook': 'Superbook',
                'bet365': 'bet365',
                'pinnacle': 'Pinnacle',
                'circa': 'Circa Sports',
                'circa sports': 'Circa Sports',
                'sisportsbook': 'SI Sportsbook',
                'si sportsbook': 'SI Sportsbook',
            };
            
            const queryLower = q.toLowerCase();
            let detectedSportsbook = null;
            for (const [keyword, bookName] of Object.entries(sportsbookKeywords)) {
                if (queryLower.includes(keyword)) {
                    detectedSportsbook = bookName;
                    break;
                }
            }
            
            let sportsbooksParam = null;
            let searchedBooks = [];
            
            // Check for book override first
            if (bookOverride) {
                // User has selected a specific book override
                sportsbooksParam = bookOverride;
                searchedBooks = [bookOverride];
            } else if (detectedSportsbook) {
                // User specified a sportsbook in query - use only that one
                sportsbooksParam = detectedSportsbook;
                searchedBooks = [detectedSportsbook];
            } else {
                // No sportsbook specified - use top 20 popular sportsbooks
                const top20Books = [
                    'DraftKings',
                    'FanDuel', 
                    'Caesars',
                    'BetMGM',
                    'PointsBet',
                    'BetRivers',
                    'WynnBet',
                    'Barstool',
                    'FOX Bet',
                    'Unibet',
                    'Bet365',
                    'William Hill',
                    'SugarHouse',
                    'Betfred',
                    'SuperBook',
                    'Circa Sports',
                    'Westgate',
                    'SI Sportsbook',
                    'BetUS',
                    'MyBookie'
                ];
                
                // If user has custom preferences, merge them with top 20
                const userSportsbooks = userProfile?.preferences?.selectedSportsbooks || [];
            if (userSportsbooks.length > 0) {
                    const combined = [...new Set([...userSportsbooks, ...top20Books])];
                sportsbooksParam = combined.join(',');
                    searchedBooks = combined;
                } else {
                    sportsbooksParam = top20Books.join(',');
                    searchedBooks = top20Books;
            }
            }
            setSearchedSportsbooks(searchedBooks);
            
            // When broadening, simplify the query to just get all games for the sport/league
            let finalPrompt = normalizedQuery;
            if (broaden) {
                // Generic broaden strategy: extract sport/league/date, remove specific restrictions
                // This works for ANY sport, not just hardcoded ones
                
                // List of common sport/league keywords to preserve
                const sportKeywords = ['nba', 'nfl', 'nhl', 'mlb', 'mls', 'ufc', 'pga', 
                                      'basketball', 'football', 'hockey', 'baseball', 'soccer', 'tennis',
                                      'ncaa', 'ncaaf', 'ncaab', 'college', 'wnba', 'cfl', 'premier league',
                                      'champions league', 'europa league', 'la liga', 'serie a', 'bundesliga',
                                      'ligue 1', 'championship'];
                
                // List of common date keywords to preserve
                const dateKeywords = ['today', 'tonight', 'tomorrow', 'this weekend', 'weekend',
                                     'next week', 'this week', 'monday', 'tuesday', 'wednesday',
                                     'thursday', 'friday', 'saturday', 'sunday'];
                
                // Extract sport/league terms from query
                const words = queryLower.split(/\s+/);
                const foundSports = [];
                const foundDates = [];
                
                for (let i = 0; i < words.length; i++) {
                    const word = words[i];
                    const twoWords = words.slice(i, i + 2).join(' ');
                    const threeWords = words.slice(i, i + 3).join(' ');
                    
                    // Check multi-word league names first
                    if (sportKeywords.includes(threeWords)) {
                        foundSports.push(threeWords);
                        i += 2;
                    } else if (sportKeywords.includes(twoWords)) {
                        foundSports.push(twoWords);
                        i += 1;
                    } else if (sportKeywords.includes(word)) {
                        foundSports.push(word);
                    }
                    
                    if (dateKeywords.includes(twoWords)) {
                        foundDates.push(twoWords);
                        i += 1;
                    } else if (dateKeywords.includes(word)) {
                        foundDates.push(word);
                    }
                }
                
                // Build temporally broadened query - KEEP original specificity
                // Only expand time range, don't add "all markets"
                const datePart = foundDates.length > 0 ? foundDates.join(' ') + ' and tomorrow and this week' : 'today, tomorrow, and this week';
                
                // Replace or append temporal expansion while keeping original query specificity
                let expandedQuery = normalizedQuery;
                if (foundDates.length > 0) {
                    // Replace existing date with expanded date range
                    for (const dateWord of foundDates) {
                        expandedQuery = expandedQuery.replace(dateWord, datePart);
                        break; // Only replace first occurrence
                    }
                } else {
                    // Append temporal range if no date specified
                    expandedQuery = normalizedQuery + ' ' + datePart;
                }
                
                finalPrompt = expandedQuery;
                
                console.log('[SGP] Temporally broadened query from:', normalizedQuery);
                console.log('[SGP] Broadened to:', finalPrompt);
                console.log('[SGP] Strategy: Keep original markets/teams, expand time only');
            }
            
            const t0 = Date.now();
            
            // 🔍 DETECT SGP INTENT: Check if user wants SGPs
            // Include explicit keywords, typos, AND pattern detection
            const sgpKeywords = [
                'sgp', 'sgps', 
                'same game parlay', 'same game parlays',
                'same-game', 'samegame', 
                'parlay', 'parlays',
                // Common typos
                'sgos', 'spg', 'spgs', 'sgo', 'sgp\'s'
            ];
            const hasExplicitSGPKeyword = sgpKeywords.some(keyword => 
                queryLower.includes(keyword)
            );
            
            // ⭐ SMART SGP DETECTION: Also detect queries that LOOK like SGPs
            // Pattern 1: "X and Y" combo queries (e.g., "Lebron points and Chiefs touchdowns")
            const hasComboPattern = /\band\b/.test(queryLower) && queryLower.split(/\band\b/).length >= 2;
            
            // Pattern 2: Player prop queries (player name + stat)
            const playerStatKeywords = ['points', 'rebounds', 'assists', 'steals', 'blocks', 'threes', '3pm', 
                                       'goals', 'saves', 'shots', 'hits', 'strikeouts', 'ks',
                                       'touchdowns', 'tds', 'yards', 'receptions', 'catches',
                                       'aces', 'double faults'];
            const hasPlayerStatPattern = playerStatKeywords.some(stat => queryLower.includes(stat));
            
            // Pattern 3: Team + market queries (team name + ML/spread/total)
            const marketKeywords = ['ml', 'moneyline', 'money line', 'spread', 'ats', 
                                   'over', 'under', 'total', 'totals', '1st quarter', 'first quarter',
                                   '1st half', 'first half', '2nd half', 'second half'];
            const hasMarketPattern = marketKeywords.some(market => queryLower.includes(market));
            
            // Pattern 4: "vs" matchup queries (e.g., "Navy vs Connecticut")
            const hasMatchupPattern = /\bvs\.?\b|\bversus\b|\b@\b/.test(queryLower);
            
            // Pattern 5: League-wide queries (e.g., "nba today", "nfl tonight", "atp today")
            const leagueBroadKeywords = ['nba', 'nfl', 'nhl', 'mlb', 'ncaab', 'ncaaf', 'atp', 'wta', 'mls', 'wnba'];
            const temporalWords = ['today', 'tonight', 'tomorrow', 'now', 'live', 'upcoming'];
            const hasLeagueBroadPattern = leagueBroadKeywords.some(league => queryLower.includes(league)) &&
                                          temporalWords.some(time => queryLower.includes(time));
            
            // Combine all detection patterns
            // ⭐ FIX: Player stat patterns and market patterns alone should NOT trigger SGP mode
            // Only these should trigger SGP:
            // 1. Explicit SGP keywords (sgp, parlay, same game)
            // 2. Combo patterns (using "and" or "+")
            // 3. League broad patterns with SGP keywords
            // 4. Matchup patterns are ambiguous - keep them for now
            // Player queries like "jokic points" should go to Straight bets, NOT SGPs!
            const isSGPQuery = hasExplicitSGPKeyword || hasComboPattern;
            
            console.log('[SGP] Query intent detection:', { 
                hasExplicitSGPKeyword,
                hasComboPattern,
                hasPlayerStatPattern,
                hasMarketPattern,
                hasMatchupPattern,
                hasLeagueBroadPattern,
                isSGPQuery
            });
            
            console.log('[SGP] Query analysis:', { 
                isSGPQuery, 
                betMode, 
                queryLower,
                detectionReason: hasExplicitSGPKeyword ? 'explicit_keyword' : 
                                hasComboPattern ? 'combo_and_pattern' :
                                hasPlayerStatPattern ? 'player_stat' :
                                hasMarketPattern ? 'market_keyword' :
                                hasMatchupPattern ? 'matchup_vs' :
                                hasLeagueBroadPattern ? 'league_broad' : 'none'
            });
            
            // 🎯 AUTO-SET BET MODE: If SGP intent is detected, switch to SGP mode
            if (isSGPQuery && betMode !== 'sgp' && betMode !== 'sgpx') {
                console.log('[SGP] 🔄 Auto-switching to SGP mode based on query intent');
                setBetMode('sgp');
            }
            
            // ⚡ TRY SGP CACHE FIRST if SGP intent detected
            let sgpCacheResult = null;
            
            // 🆕 Smart league/sport detection - declare outside if block so fallback can use them
            // Uses team name mapping + keyword detection
            let detectedLeague = null;
            let detectedSport = null;
            
            if (isSGPQuery || betMode === 'sgp') {
                console.log('[SGP] 🎯 SGP intent detected - checking SGP cache first...');
                setProgressText('⚡ Checking pre-generated SGP cache...');
                setSearchStatus(prev => ({ ...prev, step: 'Checking SGP cache' }));
                
                try {
                    // Use the outer-scope variables
                    let league = detectedLeague;
                    let sport = detectedSport;
                    
                    // Team-to-league quick lookup for common teams that might be confused
                    const TEAM_LEAGUE_QUICK_MAP = {
                        // NHL teams
                        'avalanche': { league: 'nhl', sport: 'hockey' },
                        'avs': { league: 'nhl', sport: 'hockey' },
                        'bruins': { league: 'nhl', sport: 'hockey' },
                        'penguins': { league: 'nhl', sport: 'hockey' },
                        'pens': { league: 'nhl', sport: 'hockey' },
                        'blackhawks': { league: 'nhl', sport: 'hockey' },
                        'hawks': { league: 'nhl', sport: 'hockey' },
                        'maple leafs': { league: 'nhl', sport: 'hockey' },
                        'leafs': { league: 'nhl', sport: 'hockey' },
                        'canadiens': { league: 'nhl', sport: 'hockey' },
                        'habs': { league: 'nhl', sport: 'hockey' },
                        'oilers': { league: 'nhl', sport: 'hockey' },
                        'flames': { league: 'nhl', sport: 'hockey' },
                        'canucks': { league: 'nhl', sport: 'hockey' },
                        'kraken': { league: 'nhl', sport: 'hockey' },
                        'sharks': { league: 'nhl', sport: 'hockey' },
                        'ducks': { league: 'nhl', sport: 'hockey' },
                        'golden knights': { league: 'nhl', sport: 'hockey' },
                        'knights': { league: 'nhl', sport: 'hockey' },
                        'predators': { league: 'nhl', sport: 'hockey' },
                        'preds': { league: 'nhl', sport: 'hockey' },
                        'wild': { league: 'nhl', sport: 'hockey' },
                        'blues': { league: 'nhl', sport: 'hockey' },
                        'stars': { league: 'nhl', sport: 'hockey' },
                        'lightning': { league: 'nhl', sport: 'hockey' },
                        'hurricanes': { league: 'nhl', sport: 'hockey' },
                        'canes': { league: 'nhl', sport: 'hockey' },
                        'blue jackets': { league: 'nhl', sport: 'hockey' },
                        'devils': { league: 'nhl', sport: 'hockey' },
                        'islanders': { league: 'nhl', sport: 'hockey' },
                        'isles': { league: 'nhl', sport: 'hockey' },
                        'rangers': { league: 'nhl', sport: 'hockey' },
                        'flyers': { league: 'nhl', sport: 'hockey' },
                        'capitals': { league: 'nhl', sport: 'hockey' },
                        'caps': { league: 'nhl', sport: 'hockey' },
                        'red wings': { league: 'nhl', sport: 'hockey' },
                        'sabres': { league: 'nhl', sport: 'hockey' },
                        'senators': { league: 'nhl', sport: 'hockey' },
                        'sens': { league: 'nhl', sport: 'hockey' },
                        // MLB teams
                        'yankees': { league: 'mlb', sport: 'baseball' },
                        'red sox': { league: 'mlb', sport: 'baseball' },
                        'dodgers': { league: 'mlb', sport: 'baseball' },
                        'mets': { league: 'mlb', sport: 'baseball' },
                        'cubs': { league: 'mlb', sport: 'baseball' },
                        'white sox': { league: 'mlb', sport: 'baseball' },
                        'cardinals': { league: 'mlb', sport: 'baseball' },
                        'braves': { league: 'mlb', sport: 'baseball' },
                        'phillies': { league: 'mlb', sport: 'baseball' },
                        'astros': { league: 'mlb', sport: 'baseball' },
                        'mariners': { league: 'mlb', sport: 'baseball' },
                        'padres': { league: 'mlb', sport: 'baseball' },
                        'guardians': { league: 'mlb', sport: 'baseball' },
                        'twins': { league: 'mlb', sport: 'baseball' },
                        'orioles': { league: 'mlb', sport: 'baseball' },
                        'blue jays': { league: 'mlb', sport: 'baseball' },
                        'jays': { league: 'mlb', sport: 'baseball' },
                        'rays': { league: 'mlb', sport: 'baseball' },
                        'royals': { league: 'mlb', sport: 'baseball' },
                        'brewers': { league: 'mlb', sport: 'baseball' },
                        'reds': { league: 'mlb', sport: 'baseball' },
                        'pirates': { league: 'mlb', sport: 'baseball' },
                        'marlins': { league: 'mlb', sport: 'baseball' },
                        'nationals': { league: 'mlb', sport: 'baseball' },
                        'nats': { league: 'mlb', sport: 'baseball' },
                        'diamondbacks': { league: 'mlb', sport: 'baseball' },
                        'd-backs': { league: 'mlb', sport: 'baseball' },
                        'rockies': { league: 'mlb', sport: 'baseball' },
                        'athletics': { league: 'mlb', sport: 'baseball' },
                        "a's": { league: 'mlb', sport: 'baseball' },
                        'angels': { league: 'mlb', sport: 'baseball' },
                        // MLS teams
                        'galaxy': { league: 'mls', sport: 'soccer' },
                        'lafc': { league: 'mls', sport: 'soccer' },
                        'inter miami': { league: 'mls', sport: 'soccer' },
                        'sounders': { league: 'mls', sport: 'soccer' },
                        'timbers': { league: 'mls', sport: 'soccer' },
                        'union': { league: 'mls', sport: 'soccer' },
                        'atlanta united': { league: 'mls', sport: 'soccer' },
                        'crew': { league: 'mls', sport: 'soccer' },
                        'nycfc': { league: 'mls', sport: 'soccer' },
                        'red bulls': { league: 'mls', sport: 'soccer' },
                        // NBA teams - needed for SGP queries
                        'thunder': { league: 'nba', sport: 'basketball' },
                        'okc': { league: 'nba', sport: 'basketball' },
                        'lakers': { league: 'nba', sport: 'basketball' },
                        'celtics': { league: 'nba', sport: 'basketball' },
                        'warriors': { league: 'nba', sport: 'basketball' },
                        'dubs': { league: 'nba', sport: 'basketball' },
                        'nuggets': { league: 'nba', sport: 'basketball' },
                        'heat': { league: 'nba', sport: 'basketball' },
                        'bulls': { league: 'nba', sport: 'basketball' },
                        'knicks': { league: 'nba', sport: 'basketball' },
                        'nets': { league: 'nba', sport: 'basketball' },
                        'sixers': { league: 'nba', sport: 'basketball' },
                        '76ers': { league: 'nba', sport: 'basketball' },
                        'bucks': { league: 'nba', sport: 'basketball' },
                        'suns': { league: 'nba', sport: 'basketball' },
                        'mavericks': { league: 'nba', sport: 'basketball' },
                        'mavs': { league: 'nba', sport: 'basketball' },
                        'clippers': { league: 'nba', sport: 'basketball' },
                        'spurs': { league: 'nba', sport: 'basketball' },
                        'rockets': { league: 'nba', sport: 'basketball' },
                        'grizzlies': { league: 'nba', sport: 'basketball' },
                        'pelicans': { league: 'nba', sport: 'basketball' },
                        'timberwolves': { league: 'nba', sport: 'basketball' },
                        'wolves': { league: 'nba', sport: 'basketball' },
                        'pacers': { league: 'nba', sport: 'basketball' },
                        'cavaliers': { league: 'nba', sport: 'basketball' },
                        'cavs': { league: 'nba', sport: 'basketball' },
                        'pistons': { league: 'nba', sport: 'basketball' },
                        'hornets': { league: 'nba', sport: 'basketball' },
                        'magic': { league: 'nba', sport: 'basketball' },
                        'wizards': { league: 'nba', sport: 'basketball' },
                        'raptors': { league: 'nba', sport: 'basketball' },
                        'trail blazers': { league: 'nba', sport: 'basketball' },
                        'jazz': { league: 'nba', sport: 'basketball' },
                        'hawks': { league: 'nba', sport: 'basketball' },
                        // NFL teams
                        'chiefs': { league: 'nfl', sport: 'football' },
                        'eagles': { league: 'nfl', sport: 'football' },
                        'bills': { league: 'nfl', sport: 'football' },
                        'cowboys': { league: 'nfl', sport: 'football' },
                        'niners': { league: 'nfl', sport: 'football' },
                        '49ers': { league: 'nfl', sport: 'football' },
                        'ravens': { league: 'nfl', sport: 'football' },
                        'dolphins': { league: 'nfl', sport: 'football' },
                        'lions': { league: 'nfl', sport: 'football' },
                        'bengals': { league: 'nfl', sport: 'football' },
                        'chargers': { league: 'nfl', sport: 'football' },
                        'broncos': { league: 'nfl', sport: 'football' },
                        'seahawks': { league: 'nfl', sport: 'football' },
                        'packers': { league: 'nfl', sport: 'football' },
                        'steelers': { league: 'nfl', sport: 'football' },
                        'vikings': { league: 'nfl', sport: 'football' },
                        'falcons': { league: 'nfl', sport: 'football' },
                        'saints': { league: 'nfl', sport: 'football' },
                        'jaguars': { league: 'nfl', sport: 'football' },
                        'jags': { league: 'nfl', sport: 'football' },
                        'colts': { league: 'nfl', sport: 'football' },
                        'texans': { league: 'nfl', sport: 'football' },
                        'titans': { league: 'nfl', sport: 'football' },
                        'carolina panthers': { league: 'nfl', sport: 'football' },
                        'bears': { league: 'nfl', sport: 'football' },
                        'browns': { league: 'nfl', sport: 'football' },
                        'commanders': { league: 'nfl', sport: 'football' },
                        'giants': { league: 'nfl', sport: 'football' },
                        'jets': { league: 'nfl', sport: 'football' },
                        'patriots': { league: 'nfl', sport: 'football' },
                        'pats': { league: 'nfl', sport: 'football' },
                        'raiders': { league: 'nfl', sport: 'football' },
                        'az cardinals': { league: 'nfl', sport: 'football' },
                        'arizona cardinals': { league: 'nfl', sport: 'football' },
                        'rams': { league: 'nfl', sport: 'football' },
                        'buccaneers': { league: 'nfl', sport: 'football' },
                        'bucs': { league: 'nfl', sport: 'football' },
                    };
                    
                    // ⭐ Popular player-to-league mapping for player prop queries
                    const PLAYER_LEAGUE_MAP = {
                        // NBA stars
                        'lebron': { league: 'nba', sport: 'basketball' },
                        'curry': { league: 'nba', sport: 'basketball' },
                        'durant': { league: 'nba', sport: 'basketball' },
                        'giannis': { league: 'nba', sport: 'basketball' },
                        'jokic': { league: 'nba', sport: 'basketball' },
                        'embiid': { league: 'nba', sport: 'basketball' },
                        'tatum': { league: 'nba', sport: 'basketball' },
                        'brunson': { league: 'nba', sport: 'basketball' },
                        'luka': { league: 'nba', sport: 'basketball' },
                        'doncic': { league: 'nba', sport: 'basketball' },
                        'anthony edwards': { league: 'nba', sport: 'basketball' },
                        'ant': { league: 'nba', sport: 'basketball' },
                        'shai': { league: 'nba', sport: 'basketball' },
                        'gilgeous': { league: 'nba', sport: 'basketball' },
                        'booker': { league: 'nba', sport: 'basketball' },
                        'harden': { league: 'nba', sport: 'basketball' },
                        // NHL stars
                        'ovechkin': { league: 'nhl', sport: 'hockey' },
                        'ovi': { league: 'nhl', sport: 'hockey' },
                        'mcdavid': { league: 'nhl', sport: 'hockey' },
                        'crosby': { league: 'nhl', sport: 'hockey' },
                        'matthews': { league: 'nhl', sport: 'hockey' },
                        'draisaitl': { league: 'nhl', sport: 'hockey' },
                        'mackinnon': { league: 'nhl', sport: 'hockey' },
                        'makar': { league: 'nhl', sport: 'hockey' },
                        // NFL stars
                        'mahomes': { league: 'nfl', sport: 'football' },
                        'kelce': { league: 'nfl', sport: 'football' },
                        'hurts': { league: 'nfl', sport: 'football' },
                        'allen': { league: 'nfl', sport: 'football' },
                        'lamar': { league: 'nfl', sport: 'football' },
                        'burrow': { league: 'nfl', sport: 'football' },
                        'henry': { league: 'nfl', sport: 'football' },
                        'jefferson': { league: 'nfl', sport: 'football' },
                        'chase': { league: 'nfl', sport: 'football' },
                        'hill': { league: 'nfl', sport: 'football' },
                        // MLB stars
                        'ohtani': { league: 'mlb', sport: 'baseball' },
                        'judge': { league: 'mlb', sport: 'baseball' },
                        'trout': { league: 'mlb', sport: 'baseball' },
                        'acuna': { league: 'mlb', sport: 'baseball' },
                    };
                    
                    // Check player mapping first (for player prop queries)
                    for (const [playerKey, info] of Object.entries(PLAYER_LEAGUE_MAP)) {
                        if (queryLower.includes(playerKey)) {
                            league = info.league;
                            sport = info.sport;
                            console.log(`[SGP] 🏀 Player "${playerKey}" detected → ${league.toUpperCase()}`);
                            break;
                        }
                    }
                    
                    // Check team mapping (if no player match)
                    if (!league) {
                        for (const [teamKey, info] of Object.entries(TEAM_LEAGUE_QUICK_MAP)) {
                            if (queryLower.includes(teamKey)) {
                                league = info.league;
                                sport = info.sport;
                                console.log(`[SGP] 🏟️ Team "${teamKey}" detected → ${league.toUpperCase()}`);
                                break;
                            }
                        }
                    }
                    
                    // If no team match, check explicit league keywords
                    if (!league) {
                        if (queryLower.includes('nfl') || (queryLower.includes('football') && !queryLower.includes('college'))) {
                        league = 'nfl';
                        sport = 'football';
                    } else if (queryLower.includes('nhl') || queryLower.includes('hockey')) {
                        league = 'nhl';
                        sport = 'hockey';
                    } else if (queryLower.includes('mlb') || queryLower.includes('baseball')) {
                        league = 'mlb';
                        sport = 'baseball';
                        } else if (queryLower.includes('mls') || queryLower.includes('soccer')) {
                            league = 'mls';
                            sport = 'soccer';
                        } else if (queryLower.includes('nba') || queryLower.includes('basketball')) {
                            league = 'nba';
                            sport = 'basketball';
                        } else if (queryLower.includes('wnba')) {
                            league = 'wnba';
                            sport = 'basketball';
                        } else if (queryLower.includes('ncaaf') || queryLower.includes('college football')) {
                            league = 'ncaaf';
                            sport = 'football';
                        } else if (queryLower.includes('ncaab') || queryLower.includes('college basketball')) {
                            league = 'ncaab';
                            sport = 'basketball';
                        } else if (queryLower.includes('atp') || queryLower.includes('wta') || queryLower.includes('tennis')) {
                            // Tennis: ATP for men's, WTA for women's
                            league = queryLower.includes('wta') ? 'wta' : 'atp';
                            sport = 'tennis';
                        }
                    }
                    
                    // ⭐ College team detection - works with or without matchup pattern
                    // Common college team indicators (expanded list)
                    if (!league) {
                        const collegeTeamHints = [
                            // Service academies
                            'navy', 'army', 'air force',
                            // Power 5 schools
                            'notre dame', 'alabama', 'ohio state', 'georgia', 'michigan', 'texas', 'oklahoma',
                            'usc', 'clemson', 'florida', 'lsu', 'tennessee', 'penn state', 'oregon', 'auburn',
                            'kentucky', 'duke', 'north carolina', 'unc', 'kansas', 'gonzaga', 'villanova',
                            'uconn', 'connecticut', 'purdue', 'houston', 'baylor', 'iowa', 'wisconsin', 'indiana',
                            'ucla', 'arizona', 'virginia', 'syracuse', 'louisville', 'utah', 'colorado', 'stanford',
                            'memphis', 'tulane', 'smu', 'tcu', 'ole miss', 'arkansas', 'vanderbilt', 'south carolina',
                            'nc state', 'wake forest', 'boston college', 'pitt', 'pittsburgh', 'west virginia',
                            'cincinnati', 'ucf', 'byu', 'iowa state', 'oklahoma state', 'texas tech', 'kansas state',
                            'minnesota', 'nebraska', 'illinois', 'northwestern', 'michigan state', 'rutgers', 'maryland',
                            // Group of 5 / Mid-majors
                            'marshall', 'app state', 'appalachian', 'coastal carolina', 'liberty', 'james madison',
                            'old dominion', 'odu', 'south florida', 'usf', 'central florida', 'east carolina', 'ecu',
                            'temple', 'tulsa', 'navy', 'rice', 'utsa', 'north texas', 'unt', 'fau', 'florida atlantic',
                            'fiu', 'charlotte', 'uab', 'middle tennessee', 'mtsu', 'western kentucky', 'wku',
                            'louisiana tech', 'utep', 'new mexico state', 'nmsu', 'sam houston', 'jacksonville state',
                            'kennesaw state', 'bowling green', 'toledo', 'akron', 'kent state', 'ohio', 'ball state',
                            'buffalo', 'miami ohio', 'northern illinois', 'niu', 'central michigan', 'cmu',
                            'eastern michigan', 'emu', 'western michigan', 'wmu', 'boise state', 'fresno state',
                            'san diego state', 'sdsu', 'unlv', 'nevada', 'hawaii', 'san jose state', 'sjsu',
                            'air force', 'wyoming', 'colorado state', 'csu', 'utah state', 'new mexico',
                            // Basketball schools
                            'creighton', 'xavier', 'butler', 'marquette', 'providence', 'seton hall', 'depaul',
                            'st johns', 'georgetown', 'dayton', 'vcu', 'davidson', 'saint marys', 'gonzaga',
                            // More HBCU and smaller schools
                            'morgan state', 'howard', 'hampton', 'norfolk state', 'delaware state', 'florida a&m',
                            'famu', 'bethune cookman', 'coppin state', 'grambling', 'southern', 'jackson state',
                            'alcorn state', 'prairie view', 'texas southern', 'arkansas pine bluff'
                        ];
                        
                        const hasCollegeTeam = collegeTeamHints.some(team => queryLower.includes(team));
                        if (hasCollegeTeam) {
                            const matchedTeam = collegeTeamHints.find(t => queryLower.includes(t));
                            
                            // Check for explicit sport indicators
                            const isExplicitBasketball = queryLower.includes('basketball') || queryLower.includes('ncaab') ||
                                                        queryLower.includes('march') || queryLower.includes('tournament') ||
                                                        queryLower.includes('hoops');
                            const isExplicitFootball = queryLower.includes('football') || queryLower.includes('ncaaf') ||
                                                      queryLower.includes('bowl') || queryLower.includes('cfb');
                            
                            if (isExplicitBasketball) {
                                league = 'ncaab';
                                sport = 'basketball';
                                console.log(`[SGP] 🎓 College basketball team "${matchedTeam}" detected → NCAAB`);
                            } else if (isExplicitFootball) {
                                league = 'ncaaf';
                                sport = 'football';
                                console.log(`[SGP] 🎓 College football team "${matchedTeam}" detected → NCAAF`);
                            } else {
                                // ⭐ No explicit sport - DON'T set league, let backend search by team name
                                // This allows finding the team in either NCAAB or NCAAF
                                console.log(`[SGP] 🎓 College team "${matchedTeam}" detected - no explicit sport, will search all college leagues`);
                                // Don't set league - backend will search by team name across all leagues
                            }
                        }
                    }
                    
                    // Log if we couldn't detect - AI will handle it
                    if (!league) {
                        console.log('[SGP] ⚠️ No league detected - AI will determine from query');
                    }
                    
                    // ⭐ UPDATE OUTER SCOPE VARIABLES for fallback to use
                    detectedLeague = league;
                    detectedSport = sport;
                    
                    // 🔍 Extract team name from query
                    // Remove SGP keywords and league/sport keywords to get potential team name
                    // The backend will do partial matching on team names
                    const sgpKeywordsForExtraction = [
                        'sgp', 'sgps', 'same game parlay', 'same game parlays', 'samegame', 'same-game',
                        'parlay', 'parlays',
                        // Common typos
                        'sgos', 'spg', 'spgs', 'sgo', 'sgp\'s'
                    ];
                    const leagueKeywords = [
                        // Major leagues
                        'nba', 'nfl', 'nhl', 'mlb', 'ncaaf', 'ncaab', 'wnba', 'mls',
                        // Tennis
                        'atp', 'wta', 'tennis',
                        // Sports
                        'college football', 'college basketball', 'basketball', 'football', 'hockey', 'baseball', 'soccer'
                    ];
                    // ⭐ Keywords that should NOT be treated as team names
                    const temporalKeywords = [
                        // Time-based
                        'today', 'tonight', 'tomorrow', 'yesterday',
                        'week', 'weekend', 'this week', 'next week',
                        'now', 'live', 'upcoming', 'later',
                        // Generic betting terms
                        'best', 'top', 'good', 'great', 'all', 'any',
                        'bets', 'bet', 'picks', 'pick', 'odds', 'lines',
                        'games', 'game', 'match', 'matches',
                        // Market type keywords (should filter results, not team search)
                        'ml', 'moneyline', 'money line', 'spread', 'ats',
                        'over', 'under', 'total', 'totals', 'ou', 'o/u',
                        'points', 'rebounds', 'assists', 'steals', 'blocks', 'threes', '3pm',
                        'goals', 'saves', 'shots', 'hits', 'strikeouts', 'ks', 'aces',
                        'touchdowns', 'tds', 'yards', 'receptions', 'catches', 'passing', 'rushing', 'receiving',
                        'props', 'prop', 'player', 'team',
                        'first', 'half', 'quarter', '1h', '2h', '1q', '2q', '3q', '4q', '1st', '2nd', '3rd', '4th',
                        'win', 'cover', 'hit', 'vs', 'versus', 'and'
                    ];
                    
                    let extractedTeam = queryLower;
                    
                    // Remove SGP keywords
                    sgpKeywordsForExtraction.forEach(kw => {
                        extractedTeam = extractedTeam.replace(new RegExp(`\\b${kw}\\b`, 'gi'), '');
                    });
                    
                    // Remove league keywords
                    leagueKeywords.forEach(kw => {
                        extractedTeam = extractedTeam.replace(new RegExp(`\\b${kw}\\b`, 'gi'), '');
                    });
                    
                    // ⭐ Remove temporal keywords (today, tonight, tomorrow, etc.)
                    temporalKeywords.forEach(kw => {
                        extractedTeam = extractedTeam.replace(new RegExp(`\\b${kw}\\b`, 'gi'), '');
                    });
                    
                    // ⭐ Remove numeric patterns (like "2.5", "10+", "over/under lines")
                    extractedTeam = extractedTeam.replace(/\d+\.?\d*\+?/g, '');
                    
                    // ⭐ Remove common connectors and punctuation
                    extractedTeam = extractedTeam.replace(/[@\-+]/g, ' ');
                    
                    // Clean up extra spaces and trim
                    extractedTeam = extractedTeam.replace(/\s+/g, ' ').trim();
                    
                    // If nothing left, set to null (will return all SGPs for the league)
                    if (!extractedTeam || extractedTeam.length < 2) {
                        extractedTeam = null;
                        console.log('[SGP] 🌐 No specific team detected - will return all SGPs for league:', league);
                    } else {
                        console.log('[SGP] 🏀 Extracted team search term:', extractedTeam);
                    }
                    
                    sgpCacheResult = await queryCachedSGPs({
                        league,
                        sport,
                        team: extractedTeam,
                        limit: 50,
                        max_age_hours: 6,
                        sort_by: 'ev'
                    });
                    
                    console.log('[SGP] SGP cache result:', {
                        success: sgpCacheResult?.success,
                        sgpsCount: sgpCacheResult?.sgps?.length || 0,
                        fixturesCount: sgpCacheResult?.fixtures?.length || 0
                    });
                    
                    // If we got SGPs from cache, use them!
                    if (sgpCacheResult?.success && sgpCacheResult?.sgps?.length > 0) {
                        console.log('[SGP] ✅ Using SGPs from cache!');
                        setProgressText('⚡ Loaded pre-generated SGPs from cache');
                        setSearchStatus(prev => ({ 
                            ...prev, 
                            step: 'SGP cache hit',
                            cached: true
                        }));
                        logEvent('sgp_cache_hit', { 
                            sgpsCount: sgpCacheResult.sgps.length,
                            fixturesCount: sgpCacheResult.fixtures?.length || 0
                        });
                    } else {
                        console.log('[SGP] ℹ️ No SGPs in cache, falling back to live generation');
                        setProgressText('No cached SGPs found - generating live...');
                        logEvent('sgp_cache_miss');
                    }
                } catch (sgpCacheError) {
                    console.warn('[SGP] Error checking SGP cache:', sgpCacheError);
                    logEvent('sgp_cache_error', { error: sgpCacheError.message });
                    // Continue to live generation
                }
            }
            
            // ⚡ Use cache-first approach with status tracking
            console.log('[SGP] Starting cache-first query...');
            
            let bodyJson;
            
            // If we got SGPs from cache, convert them to the expected format
            if (sgpCacheResult?.success && sgpCacheResult?.sgps?.length > 0) {
                console.log('[SGP] 🎉 Converting cached SGPs to result format');
                
                // Group SGPs by fixture
                const fixtureMap = {};
                for (const sgp of sgpCacheResult.sgps) {
                    const fixtureId = sgp.fixture_id;
                    if (!fixtureMap[fixtureId]) {
                        fixtureMap[fixtureId] = {
                            fixture: sgp.fixture || {
                                id: fixtureId,
                                home_team: sgp.home_team,
                                away_team: sgp.away_team,
                                start_date: sgp.start_date,
                                league: sgp.league,
                                sport: sgp.sport
                            },
                            sgps: []
                        };
                    }
                    fixtureMap[fixtureId].sgps.push(sgp);
                }
                
                // Convert to results format
                bodyJson = {
                    success: true,
                    cached: true,
                    source: 'sgp_cache',
                    results: Object.values(fixtureMap).map(f => ({
                        fixture: f.fixture,
                        sgps: f.sgps,
                        _fromSGPCache: true,
                        items: []
                    }))
                };
                
                console.log('[SGP] Converted SGP cache to', bodyJson.results.length, 'fixtures');
                setProgressText(`✅ Loaded ${sgpCacheResult.sgps.length} pre-generated SGPs`);
                setSearchStatus(prev => ({ 
                    ...prev, 
                    step: 'SGP cache loaded',
                    cached: true,
                    fixturesFound: bodyJson.results.length
                }));
                
            } else {
                // No SGP cache hit - proceed with regular query
            setProgressText('Checking cached data...');
            setSearchStatus(prev => ({ ...prev, step: 'Checking cache' }));
            
            try {
                // ⭐ Use detected league/sport from SGP detection, or null to let AI detect
                const fallbackLeague = detectedLeague || null;
                const fallbackSport = detectedSport || null;
                console.log('[SGP] Fallback using detected league/sport:', { fallbackLeague, fallbackSport });
                
                const queryResult = await queryWithCacheFallback(
                    finalPrompt,
                    {
                        league: fallbackLeague,
                        sport: fallbackSport,
                        prefer: betMode,
                defaults: { 
                    sport: fallbackSport || undefined, 
                    league: fallbackLeague || undefined, 
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, 
                    now: new Date().toISOString(),
                            sportsbooks: sportsbooksParam,
                            broaden
                        }
                    },
                    (status) => {
                        // Update progress based on cache status
                        console.log('[SGP] Cache status:', status);
                        
                        if (status.stage === 'checking_cache') {
                            setProgressText('🔍 Checking cached data...');
                            setSearchStatus(prev => ({ ...prev, step: 'Checking cache' }));
                        } else if (status.stage === 'cache_hit') {
                            setProgressText('⚡ Found cached data');
                            setSearchStatus(prev => ({ ...prev, step: 'Loading cached data' }));
                        } else if (status.stage === 'cache_miss') {
                            // Frontend cache miss - backend will search
                            setProgressText('🔍 Analyzing your request...');
                            setSearchStatus(prev => ({ ...prev, step: 'Processing query' }));
                        } else if (status.stage === 'backend_cache_hit') {
                            // Backend found cached data
                            setProgressText('⚡ Found data');
                            setSearchStatus(prev => ({ ...prev, step: 'Loading results' }));
                        } else if (status.stage === 'live_api') {
                            // Actually hitting the live OpticOdds API
                            setProgressText('🌐 Checking multiple sources - this may take a moment');
                            setSearchStatus(prev => ({ ...prev, step: 'Live search' }));
                        } else if (status.stage === 'live_api_complete') {
                            setProgressText('✓ Search complete');
                            setSearchStatus(prev => ({ ...prev, step: 'Processing results' }));
                        }
                    }
                );
                
                logEvent('query_complete', { cached: queryResult.cached });
                
                if (!queryResult.success) {
                    throw new Error('Query failed');
                }
                
                // Handle cached vs live data
                bodyJson = queryResult;
                
                // If we got fixtures from cache (for SGP), convert to expected format
                if (queryResult.fixtures && Array.isArray(queryResult.fixtures)) {
                    console.log('[SGP] Got cached fixtures, converting to results format');
                    bodyJson = {
                        results: queryResult.fixtures.map(fixture => ({
                            fixture: {
                                id: fixture.fixture_id,
                                home_team: fixture.home_team,
                                away_team: fixture.away_team,
                                start_date: fixture.start_date
                            },
                            items: []  // Will be populated from fixture.markets if needed
                        }))
                    };
                }
                
                console.log('[SGP] Query result:', { 
                    cached: queryResult.cached, 
                    hasResults: !!bodyJson?.results 
                });
                
            } catch (error) {
                console.log('[SGP] Error during query:', error);
                logEvent('api_error', { error: error.message });
                setErrorMsg(error.message || 'Query failed');
                setProgress(100);
                setProgressText('Failed');
                setLoading(false);
                clearInterval(interval);
                
                // Save failed search to metrics
                try {
                    const durationMs = Date.now() - t0;
                    const u = auth?.currentUser;
                    const uid = u?.uid || 'anon';
                    const ref = collection(db, 'metrics_queries');
                    await addDoc(ref, {
                        uid,
                        prompt: q,
                        broaden,
                        durationMs,
                        resultsCount: 0,
                        feedback: null,
                        logs: searchLogs,
                        createdAt: serverTimestamp()
                    });
                } catch {}
                return;
            }
            } // Close the else block from SGP cache check
            console.log('[SGP] Response body:', bodyJson);
            const results = Array.isArray(bodyJson?.results) ? bodyJson.results : [];
            console.log('[SGP] Parsed results count:', results.length);
            logEvent('results_parsed', { resultsCount: results.length });
            
            // 🎯 SPECIAL HANDLING FOR SGP CACHE RESULTS
            // When we got results from the SGP cache, they're pre-built parlay combinations
            // not individual odds - so we need to display them differently
            if (bodyJson?.source === 'sgp_cache' && bodyJson?.cached) {
                console.log('[SGP] 🎉 Processing cached SGP results directly...');
                
                // Capture generation stats from fixtures
                let totalCombinationsTested = 0;
                let totalMarketsUsed = 0;
                
                // Convert cached SGPs to display format
                const cachedSGPCards = [];
            
            for (const result of results) {
                    const fixture = result.fixture;
                    const sgps = result.sgps || [];
                    
                    // Accumulate stats from all fixtures
                    if (fixture?.stats) {
                        totalCombinationsTested += fixture.stats.combinations_tested || 0;
                        totalMarketsUsed += fixture.stats.markets_used || 0;
                    }
                    
                    console.log(`[SGP] Processing ${sgps.length} cached SGPs for ${fixture?.away_team} @ ${fixture?.home_team}`);
                    
                    for (let sgpIndex = 0; sgpIndex < sgps.length; sgpIndex++) {
                        const sgp = sgps[sgpIndex];
                        
                        // Build bookOdds object from all_books array (format LegBetCard expects)
                        const bookOdds = {};
                        const availableBooks = [];
                        
                        // Get the combined parlay price (this is what we want to display)
                        const parlayPrice = sgp.combined_price || sgp.fair_price || 0;
                        
                        if (sgp.all_books && Array.isArray(sgp.all_books)) {
                            for (const book of sgp.all_books) {
                                const bookName = book.sportsbook || book.book;
                                if (bookName) {
                                    // Use the book's price, or fallback to combined_price
                                    const bookPrice = book.price || parlayPrice;
                                    bookOdds[bookName] = {
                                        price: bookPrice,
                                        ev_percent: book.ev_percent || 0,
                                        ev_method1_additive: book.ev_method1_additive,
                                        ev_method2_multiplicative: book.ev_method2_multiplicative,
                                        ev_method_used: book.ev_method_used,
                                        deep_link: book.deep_link
                                    };
                                    availableBooks.push(bookName);
                                }
                            }
                        }
                        
                        // If no all_books, use best_book with combined_price
                        if (availableBooks.length === 0 && sgp.best_book) {
                            bookOdds[sgp.best_book] = {
                                price: parlayPrice,
                                ev_percent: sgp.best_ev || sgp.ev_percent || 0,
                                deep_link: sgp.deep_link
                            };
                            availableBooks.push(sgp.best_book);
                        }
                        
                        console.log(`[SGP] Card ${sgpIndex}: price=${parlayPrice}, books=${availableBooks.join(',')}`);
                        
                        // Create leg descriptions for display
                        const legDescriptions = (sgp.legs || []).map(l => {
                            const name = l.name || l.selection || '';
                            const market = l.market_name || l.market || '';
                            return `${name}${market ? ` (${market})` : ''}`;
                        });
                        
                        // Each SGP is a complete parlay combination
                        const card = {
                            id: `sgp_${fixture?.id || sgp.fixture_id}_${sgpIndex}`, // Unique ID for React key
                            _isCachedSGP: true,
                            _sgpData: sgp,
                            _fixtureId: sgp.fixture_id || fixture?.id,
                            fixture_id: sgp.fixture_id || fixture?.id,
                            home_team: sgp.home_team || fixture?.home_team,
                            away_team: sgp.away_team || fixture?.away_team,
                            league: sgp.league || fixture?.league,
                            sport: sgp.sport || fixture?.sport,
                            start_date: sgp.start_date || fixture?.start_date,
                            // SGP-specific fields
                            legs: sgp.legs || [],
                            combined_price: sgp.combined_price,
                            fair_price: sgp.fair_price,
                            best_book: sgp.best_book,
                            best_ev: sgp.best_ev || sgp.ev_percent || 0,
                            deep_link: sgp.deep_link,
                            leg_count: sgp.leg_count || sgp.legs?.length || 2,
                            
                            // ⭐ LegBetCard compatibility fields
                            bookOdds: bookOdds,
                            availableBooks: availableBooks,
                            market: `${sgp.leg_count || 2}-Leg SGP`,
                            selection: legDescriptions.join(' + ') || 'Same Game Parlay',
                            name: legDescriptions.join(' + ') || 'Same Game Parlay',
                            
                            // EV and pricing data
                            ev_percent: sgp.best_ev || sgp.ev_percent || 0,
                            ev_method1_additive: sgp.all_books?.[0]?.ev_method1_additive,
                            ev_method2_multiplicative: sgp.all_books?.[0]?.ev_method2_multiplicative,
                            
                            // Raw data for reference
                            raw: {
                                ...sgp,
                                bookOdds: bookOdds,
                                market: `${sgp.leg_count || 2}-Leg SGP`,
                                selection: legDescriptions.join(' + '),
                                fixture_id: sgp.fixture_id || fixture?.id,
                                home_team: sgp.home_team || fixture?.home_team,
                                away_team: sgp.away_team || fixture?.away_team,
                            }
                        };
                        
                        cachedSGPCards.push(card);
                    }
                }
                
                console.log(`[SGP] ✅ Created ${cachedSGPCards.length} SGP cards from cache`);
                console.log(`[SGP] 📊 Stats: ${totalCombinationsTested} combinations tested, ${totalMarketsUsed} markets used`);
                
                // Save generation stats
                setSgpGenerationStats({
                    combinationsTested: totalCombinationsTested,
                    marketsUsed: totalMarketsUsed,
                    totalSgps: cachedSGPCards.length
                });
                
                // Set the results and finish
                setFetchedOptions(cachedSGPCards);
                setLoading(false);
                setProgress(100);
                setProgressText(`✅ Found ${cachedSGPCards.length} pre-generated SGPs`);
                setSearchStatus(prev => ({ 
                    ...prev, 
                    step: 'Complete',
                    cached: true,
                    fixturesFound: cachedSGPCards.length
                }));
                logEvent('search_completed', { 
                    finalResultsCount: cachedSGPCards.length, 
                    source: 'sgp_cache',
                    cached: true
                });
                
                // ⭐ Save to query history
                try {
                    await completeForegroundQuery(cachedSGPCards, { cached: true, sgpCount: cachedSGPCards.length });
                } catch (historyErr) {
                    console.warn('[SGP] Error saving to query history:', historyErr);
                }
                
                // Track query success
                try {
                    await trackQuerySuccess(uid, true);
                } catch {}
                
                return; // Exit early - we're done!
            }
            
            // ⚡ NOTE: We skip direct Firestore SGP cache queries here because:
            // 1. Frontend doesn't have permission to read sgp_cache collection directly
            // 2. The HTTP function queryCachedSGPs was already called at the start of this function
            // 3. If we got here, it means we're processing live API results, not cached SGPs
            console.log('[SGP] ⚡ Skipping direct SGP cache check (already checked via HTTP function earlier)');
            
            // Immediately update fixture count when we get the response
            const totalItems = results.reduce((sum, r) => sum + (Array.isArray(r?.items) ? r.items.length : 0), 0);
            setProgressText(`Found ${totalItems} betting options...`);
            setSearchStatus(prev => ({ ...prev, step: `Found ${totalItems} options`, fixturesFound: totalItems }));
            
            if (results.length === 0) {
                console.log('[SGP] ⚠️ No results in API response');
                console.log('[SGP] Query was:', finalPrompt);
                console.log('[SGP] Broaden mode:', broaden);
                console.log('[SGP] Full response:', txt.substring(0, 500));
            }
            // Track "no odds yet" fixtures for display
            const noOddsYetFixtures = [];
            
            const legsTemp = [];
            let totalItemsReceived = 0;
            let totalValidOdds = 0;
            let totalSkippedOdds = 0;
            
            let processedCount = 0;
            for (const r of results) {
                const qDate = (r?.query?.date ? String(r.query.date) : '') || '';
                const list = Array.isArray(r?.items) ? r.items : [];
                totalItemsReceived += list.length;
                processedCount += list.length;
                console.log('[SGP] Processing result with', list.length, 'items, endpoint:', r?.endpoint);
                
                // Update fixture count in real-time as we process
                setSearchStatus(prev => ({ ...prev, fixturesFound: processedCount }));
                
                // Extract fixture-level team information (if available)
                const fixtureHomeTeam = r?.fixture?.home_team || r?.home_team || '';
                const fixtureAwayTeam = r?.fixture?.away_team || r?.away_team || '';
                console.log('[SGP] Fixture teams:', { home: fixtureHomeTeam, away: fixtureAwayTeam });
                
                // Debug: Log first few items to see structure
                if (list.length > 0) {
                    console.log('[SGP] First item in list:', JSON.stringify(list[0]).substring(0, 300));
                    console.log('[SGP] Item has _noOddsYet?', list[0]?._noOddsYet);
                    console.log('[SGP] Item has _marker?', list[0]?._marker);
                }
                
                const flat = list.flatMap(it => {
                    // Skip old-style markers (backward compatibility)
                    if (it?._marker) {
                        console.log('[SGP] Skipping old marker item');
                        return [];
                    }
                    
                    // If this is a "no odds yet" fixture, add to special array
                    if (it?._noOddsYet) {
                        console.log('[SGP] Found _noOddsYet fixture:', it.title);
                        noOddsYetFixtures.push(it);
                        return []; // Don't include in regular processing
                    }
                    
                    return Array.isArray(it?.odds) ? it.odds : [it];
                });
                console.log('[SGP] Flattened to', flat.length, 'odds, found', noOddsYetFixtures.length, 'fixtures without odds');
                for (const o of flat) {
                    if (!(o && (o.market || o.market_id) && (o.selection || o.name))) {
                        console.log('[SGP] Skipping invalid odd - missing required fields:', {
                            has_market: !!(o?.market || o?.market_id),
                            has_selection: !!(o?.selection || o?.name),
                            keys: o ? Object.keys(o).slice(0, 10) : []
                        });
                        totalSkippedOdds++;
                        continue;
                    }
                    totalValidOdds++;
                    const key = `${o.market || o.market_id}|${o.selection || o.name}|${o.selection_line ?? o.points ?? ''}`;
                    
                    // Get logos from our database if API doesn't provide them
                    // Try individual odds first, then fall back to fixture-level teams
                    const homeTeam = o.home_team || o.home || fixtureHomeTeam || '';
                    const awayTeam = o.away_team || o.away || fixtureAwayTeam || '';
                    
                    // ⭐ Better sport detection - check multiple sources
                    const detectedSport = (() => {
                        // Try query sport first
                        if (r?.query?.sport) return r.query.sport;
                        // Try odds-level sport
                        if (o.sport) return o.sport;
                        // Try fixture-level sport
                        if (r?.fixtures?.[0]?.sport) return r.fixtures[0].sport;
                        // Try to infer from league
                        const league = (o.league || r?.query?.league || r?.fixtures?.[0]?.league || '').toLowerCase();
                        if (['nba', 'wnba', 'ncaab', 'basketball'].some(l => league.includes(l))) return 'basketball';
                        if (['nhl', 'hockey', 'ahl'].some(l => league.includes(l))) return 'hockey';
                        if (['nfl', 'ncaaf', 'football'].some(l => league.includes(l))) return 'football';
                        if (['mlb', 'baseball'].some(l => league.includes(l))) return 'baseball';
                        if (['soccer', 'epl', 'mls', 'premier'].some(l => league.includes(l))) return 'soccer';
                        if (['tennis', 'atp', 'wta'].some(l => league.includes(l))) return 'tennis';
                        if (['ufc', 'mma', 'fighting', 'boxing'].some(l => league.includes(l))) return 'fighting';
                        return 'basketball'; // Default fallback
                    })();
                    
                    const homeLogo = o.home_logo || o.home_team_logo || getTeamLogo(homeTeam, detectedSport);
                    const awayLogo = o.away_logo || o.away_team_logo || getTeamLogo(awayTeam, detectedSport);
                    const teamLogo = o.team_logo || getTeamLogo(o.team || homeTeam || awayTeam, detectedSport);
                    const sportsbookLogo = o.sportsbook_logo || o.book_logo || getSportsbookLogo(o.sportsbook || o.book);
                    
                    // If we have a player prop, try to extract team from player name or context
                    let playerTeamLogo = null;
                    if (o.player || o.player_name) {
                        // Try to match player to home or away team based on context
                        const playerTeam = extractTeamFromSelection(o.selection || o.name, homeTeam, awayTeam);
                        if (playerTeam) {
                            playerTeamLogo = getTeamLogo(playerTeam, detectedSport);
                        }
                    }
                    
                    // Debug logo lookups for first 3 items
                    if (legsTemp.length < 3) {
                        console.log('[SGP] Logo Debug #' + legsTemp.length + ':', {
                            market: o.market,
                            selection: o.selection,
                            sportsbook: o.sportsbook,
                            sportsbookLogo: sportsbookLogo ? '✅' : '❌',
                            homeTeam: homeTeam || 'none',
                            awayTeam: awayTeam || 'none',
                            team: o.team || 'none',
                            player: o.player || o.player_name || 'none',
                            homeLogo: homeLogo ? '✅' : '❌',
                            awayLogo: awayLogo ? '✅' : '❌',
                            teamLogo: teamLogo ? '✅' : '❌',
                            playerTeamLogo: playerTeamLogo ? '✅' : '❌',
                            apiHadLogos: {
                                home: o.home_logo ? '✅' : '❌',
                                away: o.away_logo ? '✅' : '❌',
                                sportsbook: o.sportsbook_logo ? '✅' : '❌'
                            }
                        });
                    }
                    
                    // ⭐ Include line value in title for player props (e.g., "Player Points + Rebounds + Assists • 109")
                    const lineValue = o.points ?? o.selection_line ?? o.line ?? o.handicap ?? null;
                    const marketName = o.market || o.market_id || '';
                    const titleWithLine = lineValue !== null && lineValue !== undefined 
                        ? `${marketName} • ${lineValue}`
                        : marketName;
                    
                    legsTemp.push({
                        id: o.id || `${o.market}:${o.selection}:${o.selection_line ?? ''}:${o.sportsbook || 'unknown'}:${Date.now()}:${Math.random()}`,
                        title: titleWithLine,
                        // ⭐ Don't include sportsbook in description - shown in badge and right column instead
                        description: `${o.selection || o.name}`,
                        lineValue: lineValue, // Store for display
                        date: qDate || (o.event_date || o.commence_time || o.game_time || ''),
                        raw: o,
                        ai_prob: o.ai_prob || o.math_prob || 0,
                        _groupKey: key,
                        _book: o.sportsbook || o.book || '',
                        _homeLogo: homeLogo,
                        _awayLogo: awayLogo,
                        _teamLogo: teamLogo || playerTeamLogo, // Use player team logo if no direct team logo
                        _sportsbookLogo: sportsbookLogo,
                    });
                }
            }
            console.log('[SGP] Total legs parsed:', legsTemp.length);
            console.log('[SGP] Total "no odds yet" fixtures:', noOddsYetFixtures.length);
            logEvent('legs_processed', { totalLegs: legsTemp.length, noOddsYetFixtures: noOddsYetFixtures.length });
            
            // 🔍 Enrich bOPT AI data with cached odds (includes devigged EV% calculations)
            console.log('[SGP] 📊 Enriching legs with cached devigged EV% data...');
            try {
                const enrichedCount = await Promise.all(legsTemp.map(async (leg) => {
                    const fixtureId = leg.raw?.fixture_id || leg.raw?.game_id;
                    const market = leg.raw?.market;
                    const selection = leg.raw?.selection || leg.raw?.name;
                    
                    if (!fixtureId || !market || !selection) return 0;
                    
                    try {
                        // Query the odds cache for this specific market/selection
                        const oddsQuery = query(
                            collection(db, 'odds_cache'),
                            where('fixture_id', '==', fixtureId),
                            where('market', '==', market),
                            where('selection_normalized', '==', selection),
                            limit(1)
                        );
                        
                        const snapshot = await getDocs(oddsQuery);
                        if (!snapshot.empty) {
                            const cachedOdd = snapshot.docs[0].data();
                            
                            // Merge devigged EV% data into raw object
                            if (cachedOdd.ev_percent !== undefined) {
                                leg.raw.ev_percent = cachedOdd.ev_percent;
                                leg.raw.ev_method1_additive = cachedOdd.ev_method1_additive;
                                leg.raw.ev_method2_multiplicative = cachedOdd.ev_method2_multiplicative;
                                leg.raw.ev_method3_power = cachedOdd.ev_method3_power;
                                leg.raw.ev_method_used = cachedOdd.ev_method_used;
                                leg.raw.devig_methods = cachedOdd.devig_methods;
                                
                                console.log(`[SGP] ✅ Enriched ${market} ${selection} with devigged EV%:`, cachedOdd.ev_percent);
                                return 1;
                            }
                        }
                    } catch (err) {
                        console.error(`[SGP] Error enriching leg with cached data:`, err);
                    }
                    return 0;
                }));
                
                const totalEnriched = enrichedCount.reduce((sum, count) => sum + count, 0);
                console.log(`[SGP] 📊 Enriched ${totalEnriched}/${legsTemp.length} legs with cached devigged EV% data`);
            } catch (err) {
                console.error('[SGP] Error during legs enrichment:', err);
            }
            
            // ⚠️ REMOVED AGGRESSIVE FILTERING - Trust the AI/Cache to return relevant results
            // The previous filtering logic was too restrictive and filtered out valid results
            // like "3rd quarter team total" because "total" and "quarter" were in the ignore list
            let filteredLegs = legsTemp;
            
            console.log('[SGP] Total results (no frontend filtering):', legsTemp.length);
            console.log('[SGP] noOddsYetFixtures count:', noOddsYetFixtures.length);
            
            console.log('[SGP] ═══════════════════════════════════════════════════════');
            console.log('[SGP] SUMMARY - Items Received:', totalItemsReceived);
            console.log('[SGP] SUMMARY - Valid Odds (passed validation):', totalValidOdds);
            console.log('[SGP] SUMMARY - Skipped Odds (missing fields):', totalSkippedOdds);
            console.log('[SGP] SUMMARY - Legs Created (before filtering):', legsTemp.length);
            console.log('[SGP] SUMMARY - Legs After Filtering:', filteredLegs.length);
            console.log('[SGP] ═══════════════════════════════════════════════════════');
            
            // ⭐ FIX: Build available books per group and deduplicate BEFORE slicing
            // This ensures we capture all sportsbooks for each bet
            const keyToBooks = {};
            const keyToBookOdds = {}; // Store odds per book per groupKey
            const keyToFirstLeg = {}; // Track first leg for each unique key
            
            for (const it of filteredLegs) {
                const k = it._groupKey;
                if (!keyToBooks[k]) {
                    keyToBooks[k] = new Set();
                    keyToBookOdds[k] = {};
                    keyToFirstLeg[k] = it; // Store first leg for this key
                }
                
                // ⚡ NEW: Check if backend already grouped odds with bookOdds object
                if (it.raw?.bookOdds && typeof it.raw.bookOdds === 'object') {
                    // Backend returned grouped odds - extract all books from bookOdds
                    const entries = Object.entries(it.raw.bookOdds);
                    // 🔍 DEBUG: Log first entry to verify deep_link is present from cache
                    if (entries.length > 0 && !window._deepLinkDebugLogged) {
                        const [firstBook, firstData] = entries[0];
                        console.log('[SGP] 🔗 RAW bookOdds from cache - book:', firstBook,
                            '| deep_link:', firstData?.deep_link || 'UNDEFINED',
                            '| data keys:', Object.keys(firstData || {}).join(','));
                        window._deepLinkDebugLogged = true;
                    }
                    entries.forEach(([book, oddsData]) => {
                        keyToBooks[k].add(book);
                        // ⭐ Preserve full bookOdds object (includes price, ev_percent, deep_link, etc.)
                        keyToBookOdds[k][book] = typeof oddsData === 'object' ? oddsData : { price: oddsData };
                    });
                } else if (it._book) {
                    // Legacy: Individual sportsbook odd - use old logic
                    keyToBooks[k].add(it._book);
                    
                    // 🔍 DEBUG: Log deep_link status for first few items
                    if (!window._deepLinkLiveDebugLogged && legsTemp.indexOf(it) < 3) {
                        console.log('[SGP] 🔗 Live API deep_link check:', it._book, 
                            '| has deep_link:', !!it.raw?.deep_link,
                            '| deep_link type:', typeof it.raw?.deep_link,
                            '| raw keys:', Object.keys(it.raw || {}).filter(k => k.includes('link') || k.includes('url')).join(',') || 'none'
                        );
                        if (legsTemp.indexOf(it) === 2) window._deepLinkLiveDebugLogged = true;
                    }
                    
                    // Store the price AND deep_link for this book
                    keyToBookOdds[k][it._book] = { 
                        price: it.raw?.price || it.raw?.american_price || null,
                        deep_link: it.raw?.deep_link || null, // ⭐ CRITICAL: Preserve deep_link for bet placement
                        ev_percent: it.raw?.ev_percent || null,
                        decimal_odds: it.raw?.decimal_odds || null,
                        implied_prob: it.raw?.implied_prob || null
                    };
                }
            }
            
            // Create deduplicated legs array (one per unique key)
            const uniqueKeys = Object.keys(keyToFirstLeg);
            console.log('[SGP] Deduplicated to', uniqueKeys.length, 'unique bets from', filteredLegs.length, 'individual odds');
            
            // Debug: Log a sample bet to verify all books are captured
            if (uniqueKeys.length > 0) {
                const sampleKey = uniqueKeys[0];
                const sampleBooks = Array.from(keyToBooks[sampleKey] || []);
                console.log('[SGP] Sample bet:', sampleKey, '→', sampleBooks.length, 'books:', sampleBooks.join(', '));
                
                // 🔍 DEBUG: Check deep_link data
                const sampleBookOdds = keyToBookOdds[sampleKey] || {};
                const firstBook = Object.keys(sampleBookOdds)[0];
                if (firstBook) {
                    const bookData = sampleBookOdds[firstBook];
                    console.log('[SGP] 🔗 DeepLink DEBUG - First book:', firstBook, 
                        '| deep_link:', bookData?.deep_link || 'UNDEFINED',
                        '| bookData keys:', Object.keys(bookData || {}).join(','));
                }
            }
            
            // Now slice for performance (AFTER deduplication)
            const slicedKeys = uniqueKeys.slice(0, 100);
            
            const legs = slicedKeys.map((k) => {
                const it = keyToFirstLeg[k];
                const books = Array.from(keyToBooks[k] || []);
                const bookLogos = books.map(book => getSportsbookLogo(book)).filter(Boolean);
                const bookOdds = keyToBookOdds[k] || {}; // Odds per book
                
                return {
                    id: it.id,
                    title: it.title,
                    description: it.description,
                    date: it.date,
                    raw: it.raw,
                    ai_prob: it.ai_prob,
                    availableBooks: books,
                    sportsbookLogos: bookLogos, // Array of all sportsbook logos
                    bookOdds: bookOdds, // Object mapping book name to odds
                    homeLogoUrl: it._homeLogo || null,
                    awayLogoUrl: it._awayLogo || null,
                    teamLogoUrl: it._teamLogo || null,
                    sportsbookLogoUrl: it._sportsbookLogo || null,
                };
            });
            
            console.log('[SGP] About to create noOddsYetCards from', noOddsYetFixtures.length, 'fixtures (before dedup)');
            
            // Deduplicate fixtures by fixture_id (same fixture may appear in multiple result objects)
            const uniqueFixtures = Array.from(
                new Map(noOddsYetFixtures.map(f => [f._fixtureId || f.fixture_id, f])).values()
            );
            
            console.log('[SGP] After deduplication:', uniqueFixtures.length, 'unique fixtures');
            
            // Add "no odds yet" fixture cards to the legs array
            const noOddsYetCards = uniqueFixtures.map(fixture => ({
                id: `no-odds-${fixture._fixtureId || Math.random()}`,
                title: fixture.title || `${fixture.away_team} vs ${fixture.home_team}`,
                description: fixture.description || 'Odds not posted yet',
                date: fixture.start_date || '',
                _noOddsYet: true, // Flag to style differently
                _isLive: fixture._isLive || false, // Pass through live status
                _isEnded: fixture._isEnded || false, // Pass through ended status
                _fixtureId: fixture._fixtureId || fixture.fixture_id,
                _score: fixture._score || null, // Pass through score data
                homeLogoUrl: fixture.home_logo || null,
                awayLogoUrl: fixture.away_logo || null,
                raw: fixture,
                availableBooks: [],
                sportsbookLogos: [],
                bookOdds: {},
            }));
            
            // Combine regular legs with "no odds yet" cards
            const allLegs = [...legs, ...noOddsYetCards];
            
            console.log('[SGP] Setting fetched options, count:', allLegs.length, '(', legs.length, 'with odds,', noOddsYetCards.length, 'without odds)');
            logEvent('search_completed', { finalResultsCount: allLegs.length, withOdds: legs.length, withoutOdds: noOddsYetCards.length, broaden });
            setFetchedOptions(allLegs);
            setDidBroaden(broaden);
            setFixturesFoundNoOdds(noOddsYetCards.length > 0 ? { teamName: '', fixtureCount: noOddsYetCards.length } : null);
            
            // ⭐ Save to query history
            try {
                await completeForegroundQuery(allLegs, { cached: false, sgpCount: legs.length });
            } catch (historyErr) {
                console.warn('[SGP] Error saving to query history:', historyErr);
            }
            
            // Show "no results" only if we have NO legs (including "no odds yet" cards)
            if (allLegs.length === 0) {
                console.log('[SGP] No legs found at all, setting noResults=true');
                setNoResults(true);
            }
            
            // Track query success (only count queries with results)
            if (!broaden) { // Only track the initial query, not broadening
                await trackQuerySuccess(legs.length > 0);
            }
            
            // REMOVED AUTO-BROADENING: Show only what user searched for
            // Users can manually broaden if they want more results
            setProgressText(`Formatting ${legs.length} betting option${legs.length !== 1 ? 's' : ''}...`);
            setSearchStatus(prev => ({ ...prev, step: 'Formatting results' }));

            // Metrics: write query record with duration and logs
            try {
                const durationMs = Date.now() - t0;
                const u = auth?.currentUser;
                const uid = u?.uid || 'anon';
                const ref = collection(db, 'metrics_queries');
                const docRef = await addDoc(ref, {
                    uid,
                    prompt: q,
                    broaden,
                    durationMs,
                    resultsCount: legs.length,
                    feedback: null, // Will be updated if user provides feedback
                    logs: searchLogs, // Add detailed logs for admin debugging
                    createdAt: serverTimestamp()
                });
                setCurrentQueryMetricId(docRef.id);
                setQueryFeedback(null); // Reset feedback for new query
                // Update avg from last 20 queries (simple in-memory smoothing)
                setAvgMs(prev => Math.round((prev * 0.7) + (durationMs * 0.3)));
            } catch (err) {
                console.error('[SGP] Error saving metrics:', err);
            }
        } catch (e) {
            if (e?.name === 'AbortError') return;
            setErrorMsg(e?.message || String(e));
        } finally {
            // Clear progress interval
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
            }
            setProgress(100); setProgressText('Done');
            setTimeout(() => { if (mountedRef.current) setLoading(false); }, 150);
        }
    };

    // Cancel loading
    const cancelLoading = () => {
        try { abortRef.current?.abort(); } catch {}
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
        setLoading(false);
        setFetchedOptions([]);
        setSelectedLegs([]);
        setActiveFixtureId(null);
        goBack();
    };

    // Handle user feedback on query results
    const handleQueryFeedback = async (feedbackType) => {
        if (!currentQueryMetricId) return;
        
        try {
            const { doc, updateDoc } = await import('firebase/firestore');
            const metricRef = doc(db, 'metrics_queries', currentQueryMetricId);
            await updateDoc(metricRef, {
                feedback: feedbackType,
                feedbackAt: serverTimestamp()
            });
            setQueryFeedback(feedbackType);
        } catch (e) {
            console.error('[handleQueryFeedback] Error:', e);
        }
    };

    // Save query for later
    const handleSaveQuery = async (query) => {
        if (!query) return;
        
        try {
            const u = auth?.currentUser;
            if (!u) {
                alert('Please sign in to save queries');
                return;
            }
            
            const savedQuery = {
                uid: u.uid,
                query: query,
                teamName: fixturesFoundNoOdds?.teamName || null,
                fixtureCount: fixturesFoundNoOdds?.fixtureCount || 0,
                createdAt: serverTimestamp(),
                lastChecked: serverTimestamp(),
                notifyWhenAvailable: true
            };
            
            await addDoc(collection(db, 'saved_queries'), savedQuery);
            alert('Query saved! We\'ll notify you when odds are available.');
        } catch (e) {
            console.error('[handleSaveQuery] Error:', e);
            alert('Failed to save query. Please try again.');
        }
    };

    // Fetch watched fixtures for the current user
    const fetchWatchedFixtures = async () => {
        try {
            const u = auth?.currentUser;
            if (!u) return;

            const watchersQuery = query(
                collection(db, 'query_watchers'),
                where('uid', '==', u.uid),
                where('status', '==', 'active'),
                orderBy('createdAt', 'desc')
            );

            const snapshot = await getDocs(watchersQuery);
            const fixtureIds = new Set(snapshot.docs.map(doc => doc.data().fixtureId));
            setWatchedFixtures(fixtureIds);
            console.log('[fetchWatchedFixtures] Loaded', fixtureIds.size, 'watched fixtures');
        } catch (e) {
            console.error('[fetchWatchedFixtures] Error:', e);
        }
    };

    // Handle watching a "no odds yet" fixture
    const handleWatchFixture = async (fixtureOption) => {
        try {
            const u = auth?.currentUser;
            if (!u) {
                alert('Please sign in to watch fixtures');
                return;
            }

            const fixtureId = fixtureOption?._fixtureId || fixtureOption?.raw?.fixture_id || null;
            if (!fixtureId) {
                console.error('[handleWatchFixture] No fixture ID found');
                alert('Unable to watch this fixture. Please try again.');
                return;
            }

            // Check if already watching
            if (watchedFixtures.has(fixtureId)) {
                alert('You\'re already watching this fixture!');
                return;
            }

            const watcher = {
                uid: u.uid,
                fixtureId: fixtureId,
                fixtureTitle: fixtureOption.title || `${fixtureOption.away_team} vs ${fixtureOption.home_team}`,
                homeTeam: fixtureOption.home_team || null,
                awayTeam: fixtureOption.away_team || null,
                startDate: fixtureOption.start_date || fixtureOption.date || null,
                sport: fixtureOption.sport || 'basketball',
                league: fixtureOption.league || 'nba',
                status: 'active', // active | completed | expired
                createdAt: serverTimestamp(),
                lastChecked: serverTimestamp(),
                notificationSent: false
            };

            await addDoc(collection(db, 'query_watchers'), watcher);
            
            // Update local state
            setWatchedFixtures(prev => new Set([...prev, fixtureId]));
            
            alert('🔔 Watching! We\'ll notify you when odds are available.');
            console.log('[handleWatchFixture] Watcher created:', watcher);
        } catch (e) {
            console.error('[handleWatchFixture] Error:', e);
            alert('Failed to watch fixture. Please try again.');
        }
    };

    // Handle unwatching a fixture
    const handleUnwatchFixture = async (fixtureOption) => {
        try {
            const u = auth?.currentUser;
            if (!u) {
                alert('Please sign in');
                return;
            }

            const fixtureId = fixtureOption?._fixtureId || fixtureOption?.raw?.fixture_id || null;
            if (!fixtureId) {
                console.error('[handleUnwatchFixture] No fixture ID found');
                return;
            }

            // Find the watcher document
            const watchersQuery = query(
                collection(db, 'query_watchers'),
                where('uid', '==', u.uid),
                where('fixtureId', '==', fixtureId),
                where('status', '==', 'active')
            );

            const snapshot = await getDocs(watchersQuery);
            
            // Delete all matching watchers
            const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);

            // Update local state
            setWatchedFixtures(prev => {
                const newSet = new Set(prev);
                newSet.delete(fixtureId);
                return newSet;
            });

            alert('✅ Stopped watching this fixture.');
            console.log('[handleUnwatchFixture] Stopped watching:', fixtureId);
        } catch (e) {
            console.error('[handleUnwatchFixture] Error:', e);
            alert('Failed to stop watching. Please try again.');
        }
    };

    // ⭐ Market relevance scoring function - used for filter ordering AND result sorting
    const getMarketRelevanceToQuery = (market, query) => {
        if (!market || !query) return 0;
        
        const marketLower = market.toLowerCase();
        const queryLower = query.toLowerCase();
        
        // Market keyword aliases for matching
        const marketKeywordsWithAliases = {
            'moneyline': ['moneyline', 'money line', 'ml', 'winner', 'to win'],
            'spread': ['spread', 'point spread', 'handicap', 'ats', 'against the spread'],
            'total': ['total', 'totals', 'over', 'under', 'o/u', 'over/under'],
            'points': ['points', 'pts', 'point'],
            'assists': ['assists', 'ast', 'assist'],
            'rebounds': ['rebounds', 'reb', 'rebs', 'rebound'],
            'threes': ['threes', '3-pointers', '3pt', '3s', 'made threes', 'three pointers'],
            'steals': ['steals', 'stl', 'steal'],
            'blocks': ['blocks', 'blk', 'block'],
            'yards': ['yards', 'passing yards', 'rushing yards', 'receiving yards', 'yds'],
            'touchdowns': ['touchdowns', 'tds', 'td', 'touchdown'],
            'goals': ['goals', 'goal', 'to score'],
            'saves': ['saves', 'save'],
            'hits': ['hits', 'hit'],
            'home runs': ['home runs', 'hr', 'homers'],
            'strikeouts': ['strikeouts', 'ks', 'so', 'strikeout'],
            'puck line': ['puck line', 'puck'],
            'run line': ['run line'],
            'team total': ['team total', 'team totals'],
            'player': ['player', 'prop', 'props']
        };
        
        // Find which market types the user is asking for
        let requestedMarkets = [];
        for (const [canonical, aliases] of Object.entries(marketKeywordsWithAliases)) {
            if (aliases.some(alias => queryLower.includes(alias))) {
                requestedMarkets.push(canonical);
            }
        }
        
        // Score this market against requested markets
        let score = 0;
        
        for (const requested of requestedMarkets) {
            const aliases = marketKeywordsWithAliases[requested] || [requested];
            
            // Score 100: Exact match or very close
            if (marketLower === requested || aliases.some(a => marketLower === a)) {
                score = Math.max(score, 100);
            }
            // Score 90: Market starts with requested term
            else if (marketLower.startsWith(requested) || aliases.some(a => marketLower.startsWith(a))) {
                score = Math.max(score, 90);
            }
            // Score 80: Plain market (no time qualifiers like "1st half")
            else if ((marketLower.includes(requested) || aliases.some(a => marketLower.includes(a))) && 
                     !/\d|half|quarter|qt|qtr|period|inning/i.test(marketLower)) {
                score = Math.max(score, 80);
            }
            // Score 50: Contains but with qualifiers
            else if (marketLower.includes(requested) || aliases.some(a => marketLower.includes(a))) {
                score = Math.max(score, 50);
            }
        }
        
        return score;
    };

    // Get available filter options from results
    const availableSportsbooks = useMemo(() => {
        const books = new Set();
        fetchedOptions.forEach(option => {
            const book = option?.raw?.sportsbook || option?.raw?.book;
            if (book) books.add(book);
        });
        return Array.from(books).sort();
    }, [fetchedOptions]);

    // ⭐ Available markets sorted by relevance to query
    const availableMarkets = useMemo(() => {
        const markets = new Set();
        fetchedOptions.forEach(option => {
            const market = option?.raw?.market || option?.raw?.market_id || option?.title;
            if (market) markets.add(market);
        });
        
        const marketArray = Array.from(markets);
        
        // Sort by relevance to query first, then alphabetically
        return marketArray.sort((a, b) => {
            const scoreA = getMarketRelevanceToQuery(a, pendingQuery);
            const scoreB = getMarketRelevanceToQuery(b, pendingQuery);
            
            // Higher score first
            if (scoreA !== scoreB) return scoreB - scoreA;
            
            // If same score, alphabetical
            return a.localeCompare(b);
        });
    }, [fetchedOptions, pendingQuery]);

    // Filter and sort options in real-time
    const filteredAndSortedOptions = useMemo(() => {
        let filtered = [...fetchedOptions];
        
        // STRICT DATE FILTERING: Extract date from query and filter by it
        if (pendingQuery && pendingQuery.trim()) {
            const queryLower = pendingQuery.toLowerCase();
            let targetDate = null;
            
            // Parse temporal keywords from query
            const now = new Date();
            if (/\btonight\b|\btoday\b/.test(queryLower)) {
                targetDate = now.toISOString().split('T')[0]; // YYYY-MM-DD
            } else if (/\btomorrow\b/.test(queryLower)) {
                const tomorrow = new Date(now);
                tomorrow.setDate(tomorrow.getDate() + 1);
                targetDate = tomorrow.toISOString().split('T')[0];
            }
            
            // Apply date filter strictly if we detected a temporal keyword
            if (targetDate) {
                filtered = filtered.filter(option => {
                    // PRIORITY 1: Extract date from the item ID (format: "33152-35142-2025-10-22:draftkings:...")
                    let gameDateStr = '';
                    const itemId = option?.raw?.id || '';
                    const idDateMatch = itemId.match(/(\d{4})-(\d{2})-(\d{2})/);
                    
                    if (idDateMatch) {
                        gameDateStr = `${idDateMatch[1]}-${idDateMatch[2]}-${idDateMatch[3]}`;
                    } else {
                        // PRIORITY 2: Check other date fields
                        const gameDate = option?.date || 
                                       option?.raw?.commence_time || 
                                       option?.raw?.event_date ||
                                       option?.raw?.game_time ||
                                       option?.raw?.start_date || 
                                       option?.raw?.start_time || 
                                       option?.raw?.date || 
                                       option?.raw?.timestamp || '';
                        
                        if (!gameDate) {
                            console.warn('[SGP] No date found for option:', option?.title, 'ID:', itemId);
                            return false; // Filter out if no date (strict filtering for "tomorrow")
                        }
                        
                        // Convert to YYYY-MM-DD format for comparison
                        try {
                            const d = new Date(gameDate);
                            if (isNaN(d.getTime())) {
                                // If date is invalid, try to extract YYYY-MM-DD from string
                                const match = String(gameDate).match(/(\d{4})-?(\d{2})-?(\d{2})/);
                                if (match) {
                                    gameDateStr = `${match[1]}-${match[2]}-${match[3]}`;
                                }
                            } else {
                                gameDateStr = d.toISOString().split('T')[0];
                            }
                        } catch {
                            // If date parsing fails, try to extract YYYY-MM-DD from string
                            const match = String(gameDate).match(/(\d{4})-?(\d{2})-?(\d{2})/);
                            if (match) {
                                gameDateStr = `${match[1]}-${match[2]}-${match[3]}`;
                            }
                        }
                    }
                    
                    const matches = gameDateStr === targetDate;
                    if (!matches) {
                        console.log('[SGP] Date Filter EXCLUDED:', { 
                            targetDate, 
                            gameDateStr, 
                            itemId: itemId.substring(0, 60), 
                            title: option?.title?.substring(0, 40) 
                        });
                    }
                    return matches;
                });
                
                console.log(`[SGP] Date filtered for ${targetDate}: ${filtered.length} of ${fetchedOptions.length} bets matched`);
            }
        }
        
        // Apply query-based relevance filter (remove completely irrelevant results)
        // BUT skip this filtering if the results were broadened (user explicitly asked for broader results)
        if (pendingQuery && pendingQuery.trim() && !didBroaden) {
            const queryLower = pendingQuery.toLowerCase();
            const normalizedQueryLower = normalizeQuery(pendingQuery).toLowerCase();
            const queryTerms = [...new Set([
                ...queryLower.split(/\s+/),
                ...normalizedQueryLower.split(/\s+/)
            ])].filter(t => t.length >= 3 && !['the', 'and', 'bet', 'for', 'best', 'game', 'state', 'golden', 'moneyline', 'spread', 'total', 'over', 'under', 'parlays', 'parlay'].includes(t));
            
            // Only filter if we have meaningful terms (like team names, player names, or league abbreviations)
            // Don't filter if we only have generic terms
            // ⭐ Expanded list includes leagues (nba, nfl, etc.) since those already filter at cache level
            const genericTerms = [
                'today', 'tonight', 'tomorrow', 'week', 'weekend', 
                'games', 'bets', 'props', 'picks', 'best', 'top', 'good',
                // League names (already filtered at cache level, shouldn't re-filter at display level)
                'nba', 'nfl', 'nhl', 'mlb', 'ncaaf', 'ncaab', 'wnba', 'mls',
                'atp', 'wta', 'tennis',
                'basketball', 'football', 'hockey', 'baseball', 'soccer'
            ];
            const hasSpecificTerms = queryTerms.some(term => !genericTerms.includes(term));
            
            if (queryTerms.length > 0 && hasSpecificTerms) {
                filtered = filtered.filter(option => {
                    const fields = [
                        (option?.raw?.home_team || '').toLowerCase(),
                        (option?.raw?.away_team || '').toLowerCase(),
                        (option?.raw?.team || '').toLowerCase(),
                        (option?.raw?.player || '').toLowerCase(),
                        (option?.raw?.selection || '').toLowerCase(),
                        (option?.raw?.market || '').toLowerCase(),
                        (option?.raw?.league || '').toLowerCase(),
                        (option?.raw?.sport || '').toLowerCase(),
                        (option?.description || '').toLowerCase(),
                    ].join(' ');
                    
                    // Check if ANY query term appears in the fields
                    return queryTerms.some(term => fields.includes(term));
                });
            }
            // If only generic terms (like "best parlays tomorrow"), don't filter - show all API results
        }
        
        // Apply search text filter
        if (searchText.trim()) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(option => {
                const title = (option?.title || '').toLowerCase();
                const desc = (option?.description || '').toLowerCase();
                const selection = (option?.raw?.selection || '').toLowerCase();
                const market = (option?.raw?.market || '').toLowerCase();
                return title.includes(search) || desc.includes(search) || 
                       selection.includes(search) || market.includes(search);
            });
        }
        
        // Apply sportsbook filter
        if (filterSportsbooks.length > 0) {
            filtered = filtered.filter(option => {
                const book = option?.raw?.sportsbook || option?.raw?.book || '';
                return filterSportsbooks.includes(book);
            });
        }
        
        // Apply market filter
        if (filterMarkets.length > 0) {
            filtered = filtered.filter(option => {
                const market = option?.raw?.market || option?.raw?.market_id || option?.title || '';
                return filterMarkets.includes(market);
            });
        }
        
        // Apply deep links filter - only show bets that have at least one deep link
        if (filterDeepLinksOnly) {
            const beforeCount = filtered.length;
            filtered = filtered.filter(option => {
                const bookOdds = option?.bookOdds || option?.raw?.bookOdds || {};
                // Check if any book has a valid deep_link
                const hasDeepLink = Object.entries(bookOdds).some(([bookName, bookData]) => {
                    if (!bookData || typeof bookData !== 'object') return false;
                    const dl = bookData.deep_link || bookData.deeplink;
                    if (!dl) return false;
                    // Handle both string and object formats
                    if (typeof dl === 'string') return dl.length > 0;
                    if (typeof dl === 'object') return !!(dl.desktop || dl.ios || dl.android);
                    return false;
                });
                return hasDeepLink;
            });
            console.log(`[DeepLink Filter] Filtered ${beforeCount} → ${filtered.length} results with deep links`);
            
            // Debug: Show sample of what bookOdds looks like
            if (beforeCount > 0 && filtered.length === 0) {
                const sample = fetchedOptions[0];
                const sampleBookOdds = sample?.bookOdds || sample?.raw?.bookOdds || {};
                console.log('[DeepLink Filter] ⚠️ No results have deep links! Sample bookOdds:', 
                    Object.entries(sampleBookOdds).slice(0, 2).map(([book, data]) => ({
                        book,
                        hasDeepLink: !!(data?.deep_link || data?.deeplink),
                        dataKeys: Object.keys(data || {})
                    }))
                );
            }
        }
        
        // Apply sorting
        const sorted = [...filtered];
        
        // ⭐ Helper: Get filter relevance score for sorting
        // When multiple market filters are selected, sort by their selection order (which is relevance-based)
        const getFilterRelevanceScore = (option) => {
            if (filterMarkets.length <= 1) return 0; // No need if only 0-1 filters
            
            const optionMarket = option?.raw?.market || option?.raw?.market_id || option?.title || '';
            
            // Find the index in filterMarkets - earlier = more relevant
            const filterIndex = filterMarkets.findIndex(fm => 
                fm.toLowerCase() === optionMarket.toLowerCase()
            );
            
            // Not in filters = lowest priority
            if (filterIndex === -1) return 999;
            
            return filterIndex;
        };
        
        // ⭐ Helper: Get query relevance score (for both filters and general sorting)
        // STRICT matching: For multi-part queries, ALL parts must match for high score
        // ⭐ NEW: Prioritize single-player markets over combo markets
        const getQueryRelevanceScore = (option) => {
            const market = option?.raw?.market || option?.raw?.market_id || option?.title || '';
            let score = getMarketRelevanceToQuery(market, pendingQuery);
            
            if (!pendingQuery) return score;
            
            const queryLower = pendingQuery.toLowerCase();
            
            // ⭐ Detect multi-part queries (using + or "and")
            const queryParts = queryLower.split(/\s*[\+&]\s*|\s+and\s+/).map(p => p.trim()).filter(p => p);
            
            // Extract meaningful search terms from EACH part separately
            const extractTermsFromPart = (part) => {
                return part
                    .replace(/\b(sgp|sgps|parlay|parlays|same game|today|tonight|tomorrow|best|top|ml|moneyline|spread|over|under|1st|2nd|3rd|4th|quarter|half|vs|versus)\b/gi, '')
                    .split(/\s+/)
                    .filter(t => t.length >= 3);
            };
            
            // For SGPs, get all leg text
            const legs = option?.raw?.legs || [];
            const legTexts = legs.map(leg => 
                `${leg.name || ''} ${leg.selection || ''} ${leg.player || ''} ${leg.team || ''}`.toLowerCase()
            );
            
            // Also check main option fields
            const mainText = [
                option?.raw?.home_team || '',
                option?.raw?.away_team || '',
                option?.raw?.team || '',
                option?.raw?.player || '',
                option?.raw?.selection || '',
                option?.title || '',
                option?.description || ''
            ].join(' ').toLowerCase();
            
            const allText = mainText + ' ' + legTexts.join(' ');
            
            // ⭐ TEAM-SPECIFIC LEG MATCHING
            // When searching "suns sgps", we want SGPs with "Suns ML" or Suns player props FIRST
            // Not just any SGP from a Suns vs Warriors game
            const homeTeam = (option?.raw?.home_team || '').toLowerCase();
            const awayTeam = (option?.raw?.away_team || '').toLowerCase();
            
            // Extract team search terms (excluding common words)
            const teamSearchTerms = extractTermsFromPart(queryLower).filter(term => 
                // Keep terms that might be team names (not player stat keywords)
                !['points', 'assists', 'rebounds', 'blocks', 'steals', 'threes', 'goals', 'saves', 'rushing', 'passing', 'receiving', 'touchdowns', 'shots'].includes(term)
            );
            
            // Check if any search term is likely a team name (appears in home or away team)
            const searchedTeamTerms = teamSearchTerms.filter(term => {
                const termRegex = new RegExp(`\\b${term}\\b`, 'i');
                return termRegex.test(homeTeam) || termRegex.test(awayTeam);
            });
            
            // ⭐ COUNT HOW MANY LEGS DIRECTLY REFERENCE THE SEARCHED TEAM
            let legsWithSearchedTeam = 0;
            let legsWithOpponentOnly = 0;
            
            if (searchedTeamTerms.length > 0 && legs.length > 0) {
                // Determine which team the user is searching for
                const isSearchingHome = searchedTeamTerms.some(term => {
                    const termRegex = new RegExp(`\\b${term}\\b`, 'i');
                    return termRegex.test(homeTeam);
                });
                const isSearchingAway = searchedTeamTerms.some(term => {
                    const termRegex = new RegExp(`\\b${term}\\b`, 'i');
                    return termRegex.test(awayTeam);
                });
                
                const searchedTeamName = isSearchingHome ? homeTeam : (isSearchingAway ? awayTeam : '');
                const opponentTeamName = isSearchingHome ? awayTeam : (isSearchingAway ? homeTeam : '');
                
                legs.forEach(leg => {
                    const legText = `${leg.name || ''} ${leg.selection || ''} ${leg.player || ''} ${leg.team || ''}`.toLowerCase();
                    
                    // Check if this leg is about the searched team
                    const legMentionsSearchedTeam = searchedTeamTerms.some(term => {
                        const termRegex = new RegExp(`\\b${term}\\b`, 'i');
                        return termRegex.test(legText);
                    });
                    
                    // Check if leg mentions opponent but not searched team
                    const legMentionsOpponent = opponentTeamName && new RegExp(`\\b${opponentTeamName.split(' ').pop()}\\b`, 'i').test(legText);
                    
                    if (legMentionsSearchedTeam) {
                        legsWithSearchedTeam++;
                    } else if (legMentionsOpponent) {
                        legsWithOpponentOnly++;
                    }
                });
                
                // ⭐ SCORING BONUS/PENALTY based on leg team specificity
                if (legsWithSearchedTeam > 0) {
                    // SGP has at least one leg about the searched team - BONUS!
                    score += 200 * legsWithSearchedTeam;
                    
                    // Extra bonus if ALL legs are about the searched team
                    if (legsWithSearchedTeam === legs.length) {
                        score += 300;
                    }
                } else if (legsWithOpponentOnly > 0) {
                    // SGP has NO legs about searched team, only opponent - PENALTY
                    score -= 150 * legsWithOpponentOnly;
                }
            }
            
            // ⭐ DETECT COMBO/MULTI-PLAYER MARKETS - penalize these for single-player queries
            const marketLower = market.toLowerCase();
            const titleOriginal = option?.title || '';
            const titleLower = titleOriginal.toLowerCase();
            const descOriginal = option?.description || '';
            const descLower = descOriginal.toLowerCase();
            const combinedMarketText = marketLower + ' ' + titleLower + ' ' + descLower;
            const combinedOriginal = market + ' ' + titleOriginal + ' ' + descOriginal;
            
            // Check if this is a combo market (Either, Each, Combined, +, etc.)
            // Also check for specific patterns like "(Either)" or "(Each)"
            const isComboMarket = /\b(combo|either|each|combined|alternate|both)\b|\+/.test(combinedMarketText)
                || /\((either|each|combo)\)/i.test(combinedOriginal);
            
            // Check if multiple player names appear in the title/description
            // Use original case to detect "Nikola Jokic + Paolo Banchero" patterns
            // Match "Firstname Lastname" patterns (capital letter followed by lowercase)
            const playerNamePattern = /[A-Z][a-z]+\.?\s+[A-Z][a-z]+/g;
            const playerMatches = combinedOriginal.match(playerNamePattern) || [];
            // Also check for " + " which indicates combining players
            const hasPlayerSeparator = combinedOriginal.includes(' + ') || combinedOriginal.includes(' & ');
            let hasMultiplePlayers = playerMatches.length >= 2 || hasPlayerSeparator;
            
            // ⭐ STRICT MATCHING: For each query part, check if terms match
            let matchedParts = 0;
            let totalTermsMatched = 0;
            let playerNameMatches = 0;
            let exactPlayerMatch = false;
            
            queryParts.forEach(part => {
                const terms = extractTermsFromPart(part);
                const partMatches = terms.filter(term => allText.includes(term));
                
                if (partMatches.length > 0) {
                    matchedParts++;
                    totalTermsMatched += partMatches.length;
                    
                    // ⭐ Check if this match is in a leg (not just home/away team)
                    terms.forEach(term => {
                        if (legTexts.some(legText => legText.includes(term))) {
                            playerNameMatches++;
                            // Check for exact player name match (not part of combo)
                            if (!isComboMarket && !hasMultiplePlayers) {
                                exactPlayerMatch = true;
                            }
                        }
                    });
                }
            });
            
            // ⭐ SCORING LOGIC:
            // - If ALL query parts match: high score
            // - If only some parts match: medium score  
            // - If no parts match: penalty
            // - ⭐ NEW: Single player markets get bonus, combo markets get penalty
            
            if (queryParts.length > 0) {
                const matchRatio = matchedParts / queryParts.length;
                
                if (matchRatio === 1) {
                    // ALL parts matched - high relevance
                    score += 200 + (playerNameMatches * 50);
                } else if (matchRatio >= 0.5) {
                    // At least half matched
                    score += 50 + (playerNameMatches * 25);
                } else if (matchRatio > 0) {
                    // Some matched
                    score += 10;
                } else {
                    // NOTHING matched - penalty (push to bottom)
                    score -= 100;
                }
            }
            
            // ⭐ SINGLE vs COMBO MARKET ADJUSTMENT
            // For single-player queries like "jokic points", prioritize non-combo markets
            const queryTermsFlat = queryParts.flatMap(part => extractTermsFromPart(part));
            const isSinglePlayerQuery = queryParts.length === 1 && queryTermsFlat.length <= 2;
            
            // ⭐ ENHANCED: For SGPs, check if ALL legs are for the same player (single-player SGP)
            // Note: `legs` is already declared above, reuse it
            let isAllSamePlayer = false;
            let hasSinglePlayerLeg = false;
            
            if (legs.length > 0 && queryTermsFlat.length > 0) {
                const playerTerm = queryTermsFlat.find(t => 
                    !['points', 'assists', 'rebounds', 'blocks', 'steals', 'goals', 'saves', 'threes'].includes(t)
                );
                
                if (playerTerm) {
                    // Check if all legs mention the same player
                    const legsWithPlayer = legs.filter(leg => {
                        const legText = ((leg.name || '') + ' ' + (leg.player || '')).toLowerCase();
                        return legText.includes(playerTerm);
                    });
                    
                    isAllSamePlayer = legsWithPlayer.length === legs.length;
                    hasSinglePlayerLeg = legsWithPlayer.length >= 1;
                    
                    // If looking at legs with the player, check if there are OTHER players too
                    if (hasSinglePlayerLeg && !isAllSamePlayer && legs.length > 1) {
                        // This is a combo SGP with multiple different players
                        hasMultiplePlayers = true;
                    }
                }
            }
            
            if (isSinglePlayerQuery) {
                if (isAllSamePlayer && !isComboMarket) {
                    // SGP with ALL legs for the same player - highest priority!
                    score += 200;
                } else if (exactPlayerMatch && !isComboMarket && !hasMultiplePlayers) {
                    // Pure single-player market - big bonus
                    score += 150;
                } else if (hasSinglePlayerLeg && !isAllSamePlayer) {
                    // Has the player but also has other players - medium penalty
                    score -= 50;
                } else if (isComboMarket || hasMultiplePlayers) {
                    // Combo market - significant penalty for single-player queries
                    score -= 100;
                }
            }
            
            // ⭐ MARKET SIMPLICITY BONUS
            // For player prop queries, prioritize simple market names over complex ones
            // "Player Points" should appear before "Player Points H2H Spread" or "Player Points (Each)"
            const statKeywords = ['points', 'assists', 'rebounds', 'blocks', 'steals', 'threes', 'goals', 'saves'];
            const queryHasStatKeyword = statKeywords.some(stat => queryLower.includes(stat));
            
            if (queryHasStatKeyword) {
                // Check what kind of market this is
                const isSimpleMarket = statKeywords.some(stat => {
                    const simplePattern = new RegExp(`^player\\s+${stat}$`, 'i');
                    return simplePattern.test(marketLower.trim());
                });
                
                // Check for complex market patterns that should be deprioritized
                const hasComplexSuffix = /\b(h2h|spread|each|either|combo|alternate|both|\+)\b/i.test(marketLower);
                const hasParentheses = /\([^)]+\)/.test(market);
                
                if (isSimpleMarket && !hasComplexSuffix && !hasParentheses) {
                    // Simple "Player Points", "Player Assists", etc. - big bonus
                    score += 300;
                } else if (hasComplexSuffix || hasParentheses) {
                    // Complex market like "Player Points (Each)" or "Player Points H2H Spread" - penalty
                    score -= 150;
                }
            }
            
            return score;
        };
        
        switch (sortBy) {
            case 'price_asc':
                sorted.sort((a, b) => {
                    // First: Filter relevance (if multiple filters selected)
                    const filterA = getFilterRelevanceScore(a);
                    const filterB = getFilterRelevanceScore(b);
                    if (filterA !== filterB) return filterA - filterB;
                    
                    // Second: Query relevance
                    const relevanceA = getQueryRelevanceScore(a);
                    const relevanceB = getQueryRelevanceScore(b);
                    if (relevanceA !== relevanceB) return relevanceB - relevanceA;
                    
                    // Third: Price
                    const priceA = a?.raw?.price ?? a?.raw?.american_price ?? 0;
                    const priceB = b?.raw?.price ?? b?.raw?.american_price ?? 0;
                    return priceA - priceB;
                });
                break;
            case 'price_desc':
                sorted.sort((a, b) => {
                    // First: Filter relevance (if multiple filters selected)
                    const filterA = getFilterRelevanceScore(a);
                    const filterB = getFilterRelevanceScore(b);
                    if (filterA !== filterB) return filterA - filterB;
                    
                    // Second: Query relevance
                    const relevanceA = getQueryRelevanceScore(a);
                    const relevanceB = getQueryRelevanceScore(b);
                    if (relevanceA !== relevanceB) return relevanceB - relevanceA;
                    
                    // Third: Price (descending)
                    const priceA = a?.raw?.price ?? a?.raw?.american_price ?? 0;
                    const priceB = b?.raw?.price ?? b?.raw?.american_price ?? 0;
                    return priceB - priceA;
                });
                break;
            case 'ev':
                sorted.sort((a, b) => {
                    // ⭐ PRIORITY 0: Filter relevance (when multiple market filters selected)
                    const filterA = getFilterRelevanceScore(a);
                    const filterB = getFilterRelevanceScore(b);
                    if (filterA !== filterB) return filterA - filterB;
                    
                    // Helper: Determine if a bet is team-level or player-level
                    const isTeamBet = (option) => {
                        const market = (option?.raw?.market || option?.raw?.market_id || option?.title || '').toLowerCase();
                        const teamMarkets = ['moneyline', 'money line', 'spread', 'point spread', 'total', 'totals', 'over/under', 'team total', 'team totals'];
                        return teamMarkets.some(tm => market.includes(tm));
                    };
                    
                    // Calculate actual EV% for each option
                    const calculateEV = (option) => {
                        const aiProb = option?.ai_prob ?? option?.raw?.ai_prob ?? null;
                        const price = option?.raw?.price ?? option?.raw?.american_price ?? null;
                        
                        if (aiProb === null || price === null) return -999; // Push to bottom if missing data
                        
                        // Calculate implied probability from American odds
                        let impliedProb;
                        if (price > 0) {
                            impliedProb = 100 / (price + 100);
                        } else {
                            impliedProb = (-price) / ((-price) + 100);
                        }
                        
                        // EV% = (ai_prob - implied_prob) * 100
                        return (aiProb - impliedProb) * 100;
                    };
                    
                    // PRIORITY 1: Query relevance (use the shared helper)
                    const aRelevance = getQueryRelevanceScore(a);
                    const bRelevance = getQueryRelevanceScore(b);
                    
                    // If relevance differs, sort by relevance (higher first)
                    if (aRelevance !== bRelevance) {
                        return bRelevance - aRelevance;
                    }
                    
                    const isTeamA = isTeamBet(a);
                    const isTeamB = isTeamBet(b);
                    
                    // PRIORITY 2: Team bets before player props
                    if (isTeamA && !isTeamB) return -1;
                    if (!isTeamA && isTeamB) return 1;
                    
                    // PRIORITY 3: Within same category (team or player), sort by EV (highest first)
                    const evA = calculateEV(a);
                    const evB = calculateEV(b);
                    
                    if (evB !== evA) {
                        return evB - evA;
                    }
                    
                    // PRIORITY 4: Sort by date (sooner games first)
                    try {
                        const dateA = new Date(a?.raw?.start_date || a?.raw?.start_time || a?.raw?.commence_time || 0);
                        const dateB = new Date(b?.raw?.start_date || b?.raw?.start_time || b?.raw?.commence_time || 0);
                        return dateA - dateB; // Earlier dates first
                    } catch (e) {
                        return 0;
                    }
                });
                break;
            case 'date':
                sorted.sort((a, b) => {
                    // First: Filter relevance
                    const filterA = getFilterRelevanceScore(a);
                    const filterB = getFilterRelevanceScore(b);
                    if (filterA !== filterB) return filterA - filterB;
                    
                    // Second: Query relevance
                    const relevanceA = getQueryRelevanceScore(a);
                    const relevanceB = getQueryRelevanceScore(b);
                    if (relevanceA !== relevanceB) return relevanceB - relevanceA;
                    
                    // Third: Date
                    try {
                        const dateA = new Date(a?.raw?.start_date || a?.raw?.start_time || a?.raw?.commence_time || 0);
                        const dateB = new Date(b?.raw?.start_date || b?.raw?.start_time || b?.raw?.commence_time || 0);
                        return dateA - dateB; // Earlier dates first
                    } catch (e) {
                        return 0;
                    }
                });
                break;
            case 'sportsbooks':
                sorted.sort((a, b) => {
                    // First: Filter relevance
                    const filterA = getFilterRelevanceScore(a);
                    const filterB = getFilterRelevanceScore(b);
                    if (filterA !== filterB) return filterA - filterB;
                    
                    // Second: Query relevance
                    const relevanceA = getQueryRelevanceScore(a);
                    const relevanceB = getQueryRelevanceScore(b);
                    if (relevanceA !== relevanceB) return relevanceB - relevanceA;
                    
                    // Third: Count number of sportsbooks offering odds for each leg
                    const countA = a?.bookOdds ? Object.keys(a.bookOdds).length : 0;
                    const countB = b?.bookOdds ? Object.keys(b.bookOdds).length : 0;
                    
                    // Most sportsbooks first (descending)
                    if (countB !== countA) {
                        return countB - countA;
                    }
                    
                    // Fourth: By EV if sportsbook count is the same
                    const calculateEV = (option) => {
                        const aiProb = option?.ai_prob ?? option?.raw?.ai_prob ?? null;
                        const price = option?.raw?.price ?? option?.raw?.american_price ?? null;
                        if (aiProb === null || price === null) return -999;
                        const impliedProb = price > 0 ? (100 / (price + 100)) : ((-price) / ((-price) + 100));
                        return (aiProb - impliedProb) * 100;
                    };
                    const evA = calculateEV(a);
                    const evB = calculateEV(b);
                    return evB - evA;
                });
                break;
            case 'market':
                sorted.sort((a, b) => {
                    // First: Filter relevance
                    const filterA = getFilterRelevanceScore(a);
                    const filterB = getFilterRelevanceScore(b);
                    if (filterA !== filterB) return filterA - filterB;
                    
                    // Second: Query relevance
                    const relevanceA = getQueryRelevanceScore(a);
                    const relevanceB = getQueryRelevanceScore(b);
                    if (relevanceA !== relevanceB) return relevanceB - relevanceA;
                    
                    // Third: Market name alphabetically
                    const marketA = (a?.raw?.market || a?.title || '').toLowerCase();
                    const marketB = (b?.raw?.market || b?.title || '').toLowerCase();
                    return marketA.localeCompare(marketB);
                });
                break;
        }
        
        return sorted;
    }, [fetchedOptions, searchText, filterSportsbooks, filterMarkets, filterDeepLinksOnly, sortBy, pendingQuery, didBroaden]);

    // ⭐ Create market peers map for true devigged market average calculation
    // Groups all selections (both sides) for each market so we can calculate no-vig odds
    // MUST be at top-level to follow React Rules of Hooks
    const marketPeersMap = useMemo(() => {
        const map = {};
        filteredAndSortedOptions.forEach(opt => {
            // Create market key: fixture + market + line (for over/under)
            const fixtureId = opt?.fixture_id || opt?.raw?.fixture_id || opt?._fixtureId || '';
            const market = opt?.market || opt?.raw?.market || '';
            const points = opt?.points || opt?.raw?.points || '';
            const marketKey = `${fixtureId}_${market}_${points}`.toLowerCase();
            
            if (!map[marketKey]) {
                map[marketKey] = [];
            }
            map[marketKey].push(opt);
        });
        return map;
    }, [filteredAndSortedOptions]);

    // Helper to get market peers for an option
    const getMarketPeers = useCallback((opt) => {
        const fixtureId = opt?.fixture_id || opt?.raw?.fixture_id || opt?._fixtureId || '';
        const market = opt?.market || opt?.raw?.market || '';
        const points = opt?.points || opt?.raw?.points || '';
        const marketKey = `${fixtureId}_${market}_${points}`.toLowerCase();
        return marketPeersMap[marketKey] || [];
    }, [marketPeersMap]);

    // Toggle filters
    const toggleSportsbookFilter = (book) => {
        setFilterSportsbooks(prev => 
            prev.includes(book) ? prev.filter(b => b !== book) : [...prev, book]
        );
        setCurrentPage(1); // Reset to first page when filtering
    };

    const toggleMarketFilter = (market) => {
        setFilterMarkets(prev => 
            prev.includes(market) ? prev.filter(m => m !== market) : [...prev, market]
        );
        setCurrentPage(1); // Reset to first page when filtering
    };

    // Clear all filters
    const clearAllFilters = () => {
        setFilterSportsbooks([]);
        setFilterMarkets([]);
        setFilterDeepLinksOnly(false);
        setSearchText('');
        // Reset to user's default sort method
        const userDefault = userProfile?.preferences?.defaultSortMethod || 'ev';
        setSortMethod(userDefault);
        setSortBy(userDefault === 'odds' ? 'price_desc' : 'ev');
        setCurrentPage(1);
    };

    // Retry without broadening
    const retryFetch = () => {
        executeQuery(pendingQuery, { broaden: false });
    };

    // Ask to broaden (explicit user opt-in)
    const requestBroaden = () => {
        executeQuery(pendingQuery, { broaden: true });
    };

    // Add a fetched leg (from AI results) to the local bet slip
    const computeCap = () => {
        return 5; // All users can select up to 5 legs
    };

    const handleAddFetchedLeg = (legOption) => {
        if (!legOption) return;
        const autoSwitch = userProfile?.preferences?.autoSwitchMode !== false; // Default to true
        
        setSelectedLegs((prev) => {
            const exists = prev.find((l) => l.id === legOption.id);
            if (exists) {
                // deselect
                const next = prev.filter((l) => l.id !== legOption.id);
                setLastNotice(''); // Clear notice when deselecting
                return next;
            }
            // Role-based cap
            const cap = computeCap();
            if (prev.length >= cap) { 
                setLastNotice(`Selection limit reached (${cap}).`); 
                return prev; 
            }
            
            const fixId = legOption?.raw?.fixture_id || legOption?.raw?.game_id || null;
            const newLegs = [...prev, legOption];
            
            // Auto-switching logic
            if (betMode === 'infer') {
                // Infer mode: automatically determine bet type based on selections
                const fixtures = newLegs.map(l => l?.raw?.fixture_id || l?.raw?.game_id).filter(Boolean);
                const uniqueFixtures = [...new Set(fixtures)];
                
                if (uniqueFixtures.length === 1 && newLegs.length >= 2) {
                    // All legs from same game -> SGP
                    setLastNotice('ℹ️ Multiple legs from same game - displaying as SGP');
                    if (!activeFixtureId) setActiveFixtureId(uniqueFixtures[0]);
                } else if (uniqueFixtures.length > 1) {
                    // Legs from different games -> could be Straight or SGPx
                    setLastNotice('ℹ️ Legs from different games - displaying as separate bets');
                } else {
                    setLastNotice('');
                }
            } else if (betMode === 'sgp') {
                // SGP mode: enforce same-game restriction
                if (!activeFixtureId && fixId) {
                    setActiveFixtureId(fixId);
                } else if (activeFixtureId && fixId && activeFixtureId !== fixId) {
                    // Incompatible: either switch modes or block selection
                    if (autoSwitch) {
                        setBetMode('sgpx');
                        if (Platform.OS === 'web') { try { window.sessionStorage.setItem('sgp:mode', 'sgpx'); } catch {} }
                        setLastNotice('⚠️ Switched to SGPx mode: legs are from different games. You can switch to "Straight" if you prefer separate bets.');
                    } else {
                        setLastNotice('❌ Cannot add leg from different game in SGP mode. Switch to SGPx or Straight mode, or enable Auto-Switch in settings.');
                        return prev; // Block the selection
                    }
                }
            } else if (betMode === 'straight') {
                // Straight mode: each leg is independent
                if (!activeFixtureId && fixId) setActiveFixtureId(fixId);
                
                // Check if all legs are now from same game
                const fixtures = newLegs.map(l => l?.raw?.fixture_id || l?.raw?.game_id).filter(Boolean);
                const uniqueFixtures = [...new Set(fixtures)];
                if (uniqueFixtures.length === 1 && newLegs.length >= 2) {
                    setLastNotice('💡 Tip: All your legs are from the same game. Consider switching to "SGP" mode for parlay odds.');
                }
            } else if (betMode === 'sgpx') {
                // SGPx mode: allow mixed bets with SGP + straight legs
                // No restrictions, just track fixtures
                if (!activeFixtureId && fixId) setActiveFixtureId(fixId);
                setLastNotice('');
            }
            
            return newLegs;
        });
    };

    // Fetch real market average probabilities from OpticOdds parlay endpoint
    const fetchAiProbabilities = async (legs) => {
        if (!legs || legs.length < 2) return; // Need at least 2 legs for parlay
        
        setFetchingAiProbs(true);
        try {
            const apiUrl = Constants.expoConfig?.extra?.apiUrl || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
            
            // Build parlay entries from selected legs
            const entries = legs.map(leg => {
                const raw = leg?.raw || {};
                const price = raw.price ?? raw.american_price ?? raw.odds ?? -110;
                return {
                    market: raw.market || 'Unknown',
                    name: raw.name || raw.selection || leg.title || 'Unknown',
                    fixture_id: raw.fixture_id || null,
                    price_american: price
                };
            }).filter(e => e.fixture_id); // Only include legs with fixture_id
            
            if (entries.length < 2) {
                console.log('Not enough legs with fixture_id for AI parlay pricing');
                return;
            }
            
            // Call OpticOdds parlay endpoint via our proxy
            const response = await fetch(`${apiUrl}/opticOddsProxy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    path: '/parlay/odds',
                    method: 'POST',
                    body: {
                        sportsbooks: ['OpticOdds AI'],
                        entries
                    }
                })
            });
            
            if (!response.ok) {
                console.warn('AI prob fetch failed:', response.status);
                return;
            }
            
            const data = await response.json();
            console.log('AI parlay response:', data);
            
            // Update selected legs with market average probabilities if available
            // OpticOdds returns fair odds and probabilities for the entire parlay
            // For individual legs, we can derive from the response or keep our estimates
            if (data?.data?.legs) {
                setSelectedLegs(prev => prev.map((leg, idx) => {
                    const aiLeg = data.data.legs[idx];
                    if (aiLeg?.probability) {
                        return {
                            ...leg,
                            ai_prob: aiLeg.probability,
                            ai_prob_source: 'OpticOdds AI'
                        };
                    }
                    return leg;
                }));
            }
        } catch (err) {
            console.error('Error fetching market average probabilities:', err);
        } finally {
            setFetchingAiProbs(false);
        }
    };

    // Fetch market average probabilities when legs are selected (debounced)
    useEffect(() => {
        if (selectedLegs.length < 2) return;
        
        const timer = setTimeout(() => {
            fetchAiProbabilities(selectedLegs);
        }, 1000); // Wait 1s after selection changes
        
        return () => clearTimeout(timer);
    }, [selectedLegs.length]); // Only trigger on count change to avoid loops

    // Save current selected legs to Firestore under users/{uid}/betslips
    const handleSaveBetSlip = async () => {
        try {
            const user = auth?.currentUser;
            if (!user || selectedLegs.length === 0) return;
            
            const legs = selectedLegs.map(l => ({
                id: l.id,
                market: l.raw?.market || l.raw?.market_id || l.title || '',
                selection: l.raw?.selection || l.raw?.name || l.description || '',
                price: l.raw?.price ?? l.raw?.american_price ?? l.raw?.odds ?? null,
                sportsbook: l.raw?.sportsbook || l.raw?.book || '',
                deep_link: l.raw?.urls?.offer || l.raw?.urls?.deeplink || l.raw?.urls?.event || l.raw?.urls?.market || l.raw?.deep_link?.desktop || null,
                // Game information
                home_team: l.raw?.home_team_display || l.raw?.home_team || '',
                away_team: l.raw?.away_team_display || l.raw?.away_team || '',
                is_live: l.raw?.is_live || false,
                status: l.raw?.status || 'unplayed',
                // Logos
                sportsbook_logo: l.raw?.sportsbook_logo || l.raw?.book_logo || null,
                home_logo: l.raw?.home_logo || l.raw?.home_team_logo || null,
                away_logo: l.raw?.away_logo || l.raw?.away_team_logo || null,
                team_logo: l.raw?.team_logo || null,
            }));
            
            await addDoc(collection(db, 'users', user.uid, 'betslips'), {
                createdAt: serverTimestamp(),
                legs,
                query: pendingQuery || ''
            });
            
            setSelectedLegs([]);
        } catch (e) {
            console.error('[handleSaveBetSlip] Error:', e);
        }
    };

    // Handle bet slip submission
    const handleBetSlipSubmission = () => {
        setShowBetSlipModal(false);
        setShowSubmissionScreen(true);
    };

    // Persist and restore draft to local storage
    useEffect(() => {
        if (Platform.OS === 'web') {
            try {
                const raw = window.localStorage.getItem('sgp:draft');
                if (raw) {
                    const arr = JSON.parse(raw);
                    if (Array.isArray(arr)) setSelectedLegs(arr);
                }
            } catch {}
        }
    }, []);
    useEffect(() => {
        if (Platform.OS === 'web') {
            try { window.localStorage.setItem('sgp:draft', JSON.stringify(selectedLegs)); } catch {}
        }
    }, [selectedLegs]);

    // Handle submission completion
    const handleSubmissionComplete = () => {
        setShowSubmissionScreen(false);
        try { if (Platform.OS === 'web') window.localStorage.removeItem('sgp:draft'); } catch {}
        navigateTo('Home');
    };

    // Handle saved request application
    const handleApplySavedRequest = (request) => {
        setPendingQuery(request.query);
        if (request.books) {
            setBookOverride(request.books);
        }
        executeQuery(request.query);
    };

    // Handle saving a new request
    const handleSaveRequest = async (savedRequests) => {
        try {
            // Update user profile with saved requests
            const userRef = doc(db, 'users', userProfile.uid);
            await setDoc(userRef, {
                preferences: {
                    ...userProfile.preferences,
                    savedRequests: savedRequests
                }
            }, { merge: true });
        } catch (error) {
            console.error('Error saving request:', error);
        }
    };

    // Handle sharing betslip
    const handleShareBetSlip = () => {
        if (selectedLegs.length === 0) return;
        
        // Create a shareable betslip object
        const betslipData = {
            legs: selectedLegs.map(leg => ({
                id: leg.id,
                title: leg.title,
                description: leg.description,
                raw: {
                    price: leg.raw?.price,
                    american_price: leg.raw?.american_price,
                    market: leg.raw?.market,
                    selection: leg.raw?.selection,
                    sportsbook: leg.raw?.sportsbook,
                    fixture_id: leg.raw?.fixture_id,
                    ai_prob: leg.raw?.ai_prob
                }
            })),
            betMode,
            stake: builderStake,
            timestamp: new Date().toISOString()
        };
        
        // Encode the betslip data as base64
        const encodedData = btoa(JSON.stringify(betslipData));
        const shareUrl = `${window.location.origin}/#/sgp?betslip=${encodedData}`;
        
        setShareUrl(shareUrl);
        setShowShareModal(true);
    };

    // Handle copying share URL
    const handleCopyShareUrl = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            // You could show a toast notification here
            console.log('Share URL copied to clipboard');
        } catch (error) {
            console.error('Failed to copy URL:', error);
        }
    };

    // Exit animation function
    const exitAnimation = (callback) => {
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 0,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: 20,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            }),
        ]).start(() => {
            if (callback) callback();
        });
    };

    // Page load animation
    useEffect(() => {
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 1,
                duration: 1000,
                easing: Easing.inOut(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: 0,
                duration: 1000,
                easing: Easing.inOut(Easing.cubic),
                useNativeDriver: true,
            }),
        ]).start();
    }, []);

    // Fetch average query time from recent searches on mount
    useEffect(() => {
        const fetchAvgQueryTime = async () => {
            try {
                const metricsRef = collection(db, 'metrics_queries');
                const q = query(metricsRef, orderBy('createdAt', 'desc'), limit(20));
                const snapshot = await getDocs(q);
                
                if (!snapshot.empty) {
                    const durations = snapshot.docs
                        .map(doc => doc.data().durationMs)
                        .filter(d => d && d > 0);
                    
                    if (durations.length > 0) {
                        const avg = Math.round(durations.reduce((sum, d) => sum + d, 0) / durations.length);
                        console.log(`[SGP] Fetched average query time: ${avg}ms from ${durations.length} recent queries`);
                        setAvgMs(avg);
                    }
                }
            } catch (err) {
                console.error('[SGP] Error fetching average query time:', err);
                // Keep default avgMs if fetch fails
            }
        };
        
        fetchAvgQueryTime();
    }, []);

    // Handle photo upload progress
    useEffect(() => {
        const isPhotoUpload = routePayload?.photoUploadInProgress;
        if (!isPhotoUpload || Platform.OS !== 'web') return;
        
        setIsInitializing(false); // Done initializing, now showing loading state
        setLoading(true);
        setProgress(0);
        setProgressText('Preparing...');
        
        // Poll for progress updates from window.photoUploadProgress
        const pollInterval = setInterval(() => {
            try {
                const photoProgress = window.photoUploadProgress;
                if (photoProgress) {
                    setProgress(photoProgress.progress || 0);
                    setProgressText(photoProgress.message || 'Processing...');
                    
                    if (photoProgress.step === 'error') {
                        setErrorMsg(photoProgress.message);
                        setLoading(false);
                        clearInterval(pollInterval);
                        delete window.photoUploadProgress;
                        setTimeout(() => goBack(), 3000);
                    }
                }
                
                // Check if query is ready to execute
                const triggerData = window.triggerPhotoQuery;
                if (triggerData && triggerData.query) {
                    const { query, promotion } = triggerData;
                    setPendingQuery(query);
                    setDetectedPromotion(promotion);
                    delete window.triggerPhotoQuery;
                    delete window.photoUploadProgress;
                    clearInterval(pollInterval);
                    // Small delay to show "Found: ..." message
                    setTimeout(() => executeQuery(query), 800);
                }
            } catch (e) {
                console.error('[photoUpload] poll error:', e);
            }
        }, 200);
        
        // Cleanup on unmount
        return () => {
            clearInterval(pollInterval);
            try {
                delete window.photoUploadProgress;
                delete window.triggerPhotoQuery;
            } catch {}
        };
    }, [routePayload?.photoUploadInProgress]);

    // Fetch legs for incoming query payload
    useEffect(() => {
        const payloadQuery = routePayload?.query || '';
        const payloadPromotion = routePayload?.promotion || null;
        const isPhotoUpload = routePayload?.photoUploadInProgress;
        const isFromBackground = routePayload?.fromBackground === true;
        
        // Skip if this is a photo upload (handled by separate effect above)
        if (isPhotoUpload) return;
        
        let q = payloadQuery;
        if (!q && Platform.OS === 'web') {
            try { q = window.sessionStorage.getItem('sgp:lastQuery') || ''; } catch {}
        }
        
        // No query to run - stop initializing and show empty state
        if (!q) {
            setIsInitializing(false);
            return;
        }
        
        setPendingQuery(q);
        setDetectedPromotion(payloadPromotion);
        
        // Check if this is from a background query with pre-fetched results
        if (isFromBackground && Platform.OS === 'web') {
            try {
                const storedResults = window.sessionStorage.getItem('sgp:backgroundResults');
                const wasFromBackground = window.sessionStorage.getItem('sgp:fromBackground') === 'true';
                
                if (storedResults && wasFromBackground) {
                    const results = JSON.parse(storedResults);
                    // Clear the session storage flags
                    window.sessionStorage.removeItem('sgp:backgroundResults');
                    window.sessionStorage.removeItem('sgp:fromBackground');
                    
                    if (results && results.length > 0) {
                        // Transform background results to ensure they have title/description
                        const transformedResults = results.map((result, idx) => {
                            // If already has title, use it
                            if (result.title) return result;
                            
                            const raw = result.raw || result;
                            
                            // ⭐ Check if this is an SGP result (has legs array)
                            const isSGP = result.type === 'sgp' || result._isCachedSGP || Array.isArray(result.legs);
                            
                            if (isSGP) {
                                // Handle SGP results
                                const legs = result.legs || raw?.legs || [];
                                const legCount = result.legCount || legs.length;
                                const combinedPrice = result.combined_price || raw?.combined_price || result.price || raw?.price;
                                
                                // Build description from legs
                                const legDescriptions = legs.slice(0, 3).map(leg => 
                                    `${leg.selection || leg.name}${leg.points ? ` ${leg.points}` : ''}`
                                ).join(' + ');
                                
                                return {
                                    ...result,
                                    id: result.id || `sgp-bg-${idx}`,
                                    title: `${legCount}-Leg Same Game Parlay`,
                                    description: legDescriptions || result.game || 'SGP',
                                    _isCachedSGP: true,
                                    _source: result._source || 'sgp_cache',
                                    // ⭐ Ensure SGPCard-compatible fields are preserved
                                    combined_price: combinedPrice,
                                    price: combinedPrice,
                                    best_ev: result.best_ev || raw?.best_ev || result.ev_percent || 0,
                                    best_book: result.best_book || raw?.best_book || result.sportsbook || 'Theoretical',
                                    legs: legs,
                                    leg_count: legCount,
                                    home_team: result.home_team || raw?.home_team || '',
                                    away_team: result.away_team || raw?.away_team || '',
                                    all_books: result.all_books || raw?.all_books || [],
                                    raw: raw
                                };
                            }
                            
                            // Handle straight bet results (original logic)
                            const market = result.market || raw?.market || raw?.market_id || 'N/A';
                            const selection = result.bet || raw?.selection || raw?.name || '';
                            const points = raw?.points || raw?.selection_line || '';
                            
                            // Build title like: "Moneyline" or "Player Points" or market name
                            let title = market;
                            if (points) {
                                title = `${market} ${points}`;
                            }
                            
                            // Description is the selection name (player/team)
                            const description = selection;
                            
                            return {
                                ...result,
                                title: result.title || title,
                                description: result.description || description,
                                // Ensure raw is preserved
                                raw: raw
                            };
                        });
                        
                        // Use the transformed results
                        console.log('[SgpFlow] Using background results:', transformedResults.length, 'items, SGPs:', transformedResults.filter(r => r._isCachedSGP).length);
                        setFetchedOptions(transformedResults);
                        setHasStartedSearch(true);
                        setIsInitializing(false);
                        setLoading(false);
                        return;
                    }
                }
            } catch (e) {
                console.error('[SgpFlow] Failed to load background results:', e);
            }
        }
        
        // No cached results, run the query normally
        handleExecute(q);
    }, [routePayload]);
    
    // Listen for background query navigation events (when clicking View on another query while on results page)
    useEffect(() => {
        if (Platform.OS !== 'web') return;
        
        const handleBackgroundNav = (event) => {
            const { queryId, prompt } = event.detail || {};
            console.log('[SgpFlow] Received backgroundQueryNavigation event:', { queryId, prompt });
            
            if (prompt) {
                // Clear current state
                setFetchedOptions([]);
                setSelectedLegs([]);
                setLoading(true);
                setProgress(0);
                setHasStartedSearch(true);
                setIsInitializing(false);
                setPendingQuery(prompt);
                
                // Try to load pre-fetched results
                try {
                    const storedResults = window.sessionStorage.getItem('sgp:backgroundResults');
                    if (storedResults) {
                        const results = JSON.parse(storedResults);
                        window.sessionStorage.removeItem('sgp:backgroundResults');
                        window.sessionStorage.removeItem('sgp:fromBackground');
                        window.sessionStorage.removeItem('sgp:queryId');
                        
                        if (results && results.length > 0) {
                            console.log('[SgpFlow] Using pre-fetched results from background query:', results.length);
                            
                            // Transform background results to ensure they have title/description
                            const transformedResults = results.map((result, idx) => {
                                if (result.title) return result;
                                const raw = result.raw || result;
                                
                                // ⭐ Check if this is an SGP result (has legs array)
                                const isSGP = result.type === 'sgp' || result._isCachedSGP || Array.isArray(result.legs);
                                
                                if (isSGP) {
                                    const legs = result.legs || raw?.legs || [];
                                    const legCount = result.legCount || legs.length;
                                    const combinedPrice = result.combined_price || raw?.combined_price || result.price || raw?.price;
                                    const legDescriptions = legs.slice(0, 3).map(leg => 
                                        `${leg.selection || leg.name}${leg.points ? ` ${leg.points}` : ''}`
                                    ).join(' + ');
                                    return {
                                        ...result,
                                        id: result.id || `sgp-bg-${idx}`,
                                        title: `${legCount}-Leg Same Game Parlay`,
                                        description: legDescriptions || result.game || 'SGP',
                                        _isCachedSGP: true,
                                        _source: result._source || 'sgp_cache',
                                        // ⭐ Ensure SGPCard-compatible fields
                                        combined_price: combinedPrice,
                                        price: combinedPrice,
                                        best_ev: result.best_ev || raw?.best_ev || result.ev_percent || 0,
                                        best_book: result.best_book || raw?.best_book || result.sportsbook || 'Theoretical',
                                        legs: legs,
                                        leg_count: legCount,
                                        home_team: result.home_team || raw?.home_team || '',
                                        away_team: result.away_team || raw?.away_team || '',
                                        all_books: result.all_books || raw?.all_books || [],
                                        raw: raw
                                    };
                                }
                                
                                const market = result.market || raw?.market || raw?.market_id || 'N/A';
                                const selection = result.bet || raw?.selection || raw?.name || '';
                                const points = raw?.points || raw?.selection_line || '';
                                let title = market;
                                if (points) title = `${market} ${points}`;
                                return {
                                    ...result,
                                    title: result.title || title,
                                    description: result.description || selection,
                                    raw: raw
                                };
                            });
                            
                            console.log('[SgpFlow] Transformed background results:', transformedResults.length, 'SGPs:', transformedResults.filter(r => r._isCachedSGP).length);
                            setFetchedOptions(transformedResults);
                            setLoading(false);
                            return;
                        }
                    }
                } catch (e) {
                    console.error('[SgpFlow] Failed to load background results:', e);
                }
                
                // Fall back to executing the query
                handleExecute(prompt);
            }
        };
        
        window.addEventListener('backgroundQueryNavigation', handleBackgroundNav);
        return () => {
            window.removeEventListener('backgroundQueryNavigation', handleBackgroundNav);
        };
    }, [handleExecute]);
    
    // Load initial query usage on mount
    useEffect(() => {
        const loadQueryUsage = async () => {
            const isLocalhost = Platform.OS === 'web' && 
                (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
            
            const base = isLocalhost
                ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
                : (
                    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
                    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
                    || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net'
                );
            
            try {
                const response = await fetch(`${base}/getQueryUsage`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        uid: user?.uid || null,
                        ipAddress: null,
                    })
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setQueryUsage(data);
                    console.log('[SGP] Loaded query usage:', data);
                }
            } catch (error) {
                console.error('[SGP] Error loading query usage:', error);
            }
        };
        
        loadQueryUsage();
    }, [user?.uid]);

    // Fetch watched fixtures on mount and when user changes
    useEffect(() => {
        if (user?.uid) {
            fetchWatchedFixtures();
        }
    }, [user?.uid]);

    // Register exit animation with navigation context
    useEffect(() => {
        if (Platform.OS === 'web') {
            window.sgpFlowExitAnimation = exitAnimation;
        }

        return () => {
            if (Platform.OS === 'web') {
                delete window.sgpFlowExitAnimation;
            }
        };
    }, []);

    // Auto-convert to background when navigating away while loading (desktop only)
    // This stores the current loading state to be checked on unmount
    const loadingRef = useRef(loading);
    const pendingQueryRef = useRef(pendingQuery);
    const alreadySentToBackgroundRef = useRef(false); // Prevent duplicate sends
    
    useEffect(() => {
        loadingRef.current = loading;
        pendingQueryRef.current = pendingQuery;
    }, [loading, pendingQuery]);
    
    useEffect(() => {
        // Only on desktop, auto-convert to background when navigating away while loading
        if (IS_MOBILE) return;
        
        return () => {
            // Check if we're still loading when unmounting AND haven't already sent to background
            if (loadingRef.current && pendingQueryRef.current && canAddBackgroundQuery && !alreadySentToBackgroundRef.current) {
                console.log('[SgpFlow] Auto-converting to background on navigation away');
                sendToBackground(pendingQueryRef.current, {
                    league: 'nba',
                    sport: 'basketball',
                    prefer: betMode
                });
            }
        };
    }, []); // Empty deps - only run cleanup on unmount

    // Initializing state - show minimal loading to prevent flash of results
    if (isInitializing) {
        return (
            <View style={styles.container}>
                <View style={styles.background} />
                <View style={[styles.splashContainer, { 
                    paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + responsive(12, 20, 32)) : responsive(12, 20, 16) 
                }]}>
                    <NavigationHeader />
                    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <Image source={require('../assets/icon.png')} style={{ width: 56, height: 56, borderRadius: 12, opacity: 0.7 }} resizeMode="contain" />
                    </View>
                </View>
            </View>
        );
    }

    // Loading screen with progress bar
    if (loading) {
        const isPhotoUpload = routePayload?.photoUploadInProgress;
        const progressIcon = isPhotoUpload ? '📸' : '🔍';
        const mainTitle = isPhotoUpload ? 'Analyzing Photo…' : 'Searching for bets…';
        
        // Format elapsed time with exact average (1 decimal place)
        const elapsedSec = (searchStatus.elapsedMs / 1000).toFixed(1);
        const expectedSec = (avgMs / 1000).toFixed(1);
        const timeDisplay = `${elapsedSec}s / ${expectedSec}s avg`;
        
        return (
            <View style={styles.container}>
                <View style={styles.background} />
                <View style={[styles.splashContainer, { 
                    paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + responsive(12, 20, 32)) : responsive(12, 20, 16) 
                }]}>
                    <NavigationHeader />
                    <Animated.View style={[styles.mainContent, { alignItems: 'center', justifyContent: 'center' }] }>
                        <BlurView tint="dark" style={{ width: '100%', maxWidth: 720, padding: 28, borderRadius: 16 }}>
                            <View style={{ alignItems: 'center', marginBottom: 20 }}>
                                <View style={{ position: 'relative' }}>
                                    <Image source={require('../assets/icon.png')} style={{ width: 72, height: 72, borderRadius: 14 }} resizeMode="contain" />
                                    {isPhotoUpload && (
                                        <View style={{ position: 'absolute', bottom: -4, right: -4, backgroundColor: '#8b5cf6', width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: '#000' }}>
                                            <Text style={{ fontSize: 16 }}>{progressIcon}</Text>
                            </View>
                                    )}
                                </View>
                            </View>
                            
                            {/* Main title */}
                            <Text style={{ color: 'rgba(255,255,255,0.95)', fontSize: 20, marginBottom: 8, textAlign: 'center', fontWeight: '600' }}>
                                {mainTitle}
                            </Text>
                            
                            {/* Display user's query */}
                            {pendingQuery && (
                                <View style={{ 
                                    marginBottom: 16, 
                                    paddingVertical: 10, 
                                    paddingHorizontal: 16, 
                                    backgroundColor: 'rgba(139, 92, 246, 0.1)', 
                                    borderRadius: 10,
                                    borderWidth: 1,
                                    borderColor: 'rgba(139, 92, 246, 0.25)'
                                }}>
                                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 4, textTransform: 'uppercase', fontWeight: '600', letterSpacing: 0.5 }}>
                                        Your Query
                                    </Text>
                                    <Text style={{ color: '#c4b5fd', fontSize: 15, fontWeight: '500', fontStyle: 'italic' }}>
                                        "{pendingQuery}"
                                    </Text>
                                </View>
                            )}
                            
                            {/* Status and stats row */}
                            <View style={{ marginBottom: 16, gap: 12 }}>
                                {/* Current step */}
                                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 12, paddingVertical: 8, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 8 }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                        <Text style={{ fontSize: 16 }}>⚡</Text>
                                        <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, fontWeight: '500' }}>
                                            {searchStatus.step || progressText}
                                        </Text>
                            </View>
                                    <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                                        {progress}%
                                    </Text>
                                </View>
                                
                                {/* Stats grid */}
                                {!isPhotoUpload && (
                                    <View style={{ flexDirection: 'row', gap: 12 }}>
                                        {/* Fixtures found */}
                                        <View style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 10 }}>
                                            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 4, textTransform: 'uppercase', fontWeight: '600' }}>
                                                Fixtures
                                            </Text>
                                            <Text style={{ color: 'rgba(255,255,255,0.95)', fontSize: 20, fontWeight: '700' }}>
                                                {searchStatus.fixturesFound}
                                            </Text>
                                        </View>
                                        
                                        {/* Time elapsed */}
                                        <View style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 10 }}>
                                            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 4, textTransform: 'uppercase', fontWeight: '600' }}>
                                                Time
                                            </Text>
                                            <Text style={{ color: 'rgba(255,255,255,0.95)', fontSize: 16, fontWeight: '600' }}>
                                                {timeDisplay}
                                            </Text>
                                        </View>
                                    </View>
                                )}
                            </View>
                            
                            {/* Progress bar with gradient */}
                            <View style={{ height: 12, width: '100%', borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.08)', overflow: 'hidden', marginBottom: 16 }}>
                                <View 
                                    style={{ 
                                        height: '100%', 
                                        width: `${Math.max(0, Math.min(100, progress))}%`, 
                                        background: isPhotoUpload 
                                            ? 'linear-gradient(90deg, #8b5cf6 0%, #a78bfa 100%)' 
                                            : 'linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                                        transition: 'width 0.2s ease-out',
                                        borderRadius: 8
                                    }} 
                                />
                            </View>
                            
                            {/* Detailed progress text */}
                            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, textAlign: 'center', minHeight: 18, marginBottom: 12 }}>
                                {progressText}
                            </Text>
                            
                            {/* Show detected content if available */}
                            {isPhotoUpload && progressText.startsWith('Found:') && (
                                <View style={{ marginBottom: 12, padding: 12, backgroundColor: 'rgba(139, 92, 246, 0.15)', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#8b5cf6' }}>
                                    <Text style={{ color: '#c4b5fd', fontSize: 13, fontWeight: '600' }}>✨ Detected from your photo:</Text>
                                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, marginTop: 4 }}>{progressText.replace('Found: ', '')}</Text>
                                </View>
                            )}
                            
                            {errorMsg ? (
                                <Text style={{ color: '#ff9f9f', marginTop: 12, textAlign: 'center' }}>{String(errorMsg).slice(0, 300)}</Text>
                            ) : null}
                            
                            <View style={{ marginTop: 4, flexDirection: 'row', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                <GradientButton text="Cancel" onPress={cancelLoading} style={styles.footerButton} />
                                
                                {/* Send to Background button - only show on desktop when not errored */}
                                {!errorMsg && !IS_MOBILE && canAddBackgroundQuery && (
                                    <TouchableOpacity 
                                        onPress={async () => {
                                            // Mark as already sent to prevent duplicate on unmount
                                            alreadySentToBackgroundRef.current = true;
                                            
                                            const result = await sendToBackground(pendingQuery, {
                                                league: 'nba',
                                                sport: 'basketball',
                                                prefer: betMode
                                            });
                                            if (result.success) {
                                                // Navigate back to previous page
                                                goBack();
                                            } else {
                                                // Reset flag if send failed
                                                alreadySentToBackgroundRef.current = false;
                                            }
                                        }}
                                        style={{
                                            paddingVertical: 10,
                                            paddingHorizontal: 16,
                                            borderRadius: 8,
                                            backgroundColor: 'rgba(139, 92, 246, 0.15)',
                                            borderWidth: 1,
                                            borderColor: 'rgba(139, 92, 246, 0.4)',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 6
                                        }}
                                    >
                                        <Text style={{ color: '#c4b5fd', fontSize: 14, fontWeight: '600' }}>
                                            Send to Background
                                        </Text>
                                    </TouchableOpacity>
                                )}
                                
                                {errorMsg ? (
                                    <GradientButton text="Retry" onPress={retryFetch} style={styles.footerButton} />
                                ) : null}
                            </View>
                        </BlurView>
                    </Animated.View>
                </View>
      {selectedLegs.length > 0 ? (
        <View style={{ position: 'fixed', bottom: 0, left: 0, right: 0, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.12)', paddingTop: 8, paddingBottom: 10, paddingHorizontal: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#000' }}>
          <Text style={{ color: 'rgba(255,255,255,0.9)' }}>Selected: {selectedLegs.length} / {computeCap()}</Text>
          <Text style={{ color: 'rgba(255,255,255,0.8)' }}>Est. payout shown per leg; combined payout coming in next pass.</Text>
          <GradientButton text="Review BetSlip" onPress={() => setShowBetSlipModal(true)} style={styles.footerButton} />
        </View>
      ) : null}
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <View style={styles.background} />
            <View style={[styles.splashContainer, { 
                paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + responsive(12, 20, 32)) : responsive(12, 20, 16) 
            }]}>
                <NavigationHeader />

                {/* Search Bar for New Queries */}
                <View style={styles.searchBarContainer}>
                    <View style={styles.searchInputWrapper}>
                        <TextInput
                            placeholder="Search for another bet..."
                            placeholderTextColor="rgba(255, 255, 255, 0.5)"
                            style={styles.searchInput}
                            value={pendingQuery}
                            onChangeText={setPendingQuery}
                            onSubmitEditing={() => {
                                if (pendingQuery.trim()) {
                                    handleExecute(pendingQuery.trim());
                                }
                            }}
                            returnKeyType="search"
                            selectionColor={Platform.OS === 'web' ? undefined : 'rgba(255,255,255,0.9)'}
                        />
                        <TouchableOpacity
                            onPress={() => {
                                if (pendingQuery.trim()) {
                                    handleExecute(pendingQuery.trim());
                                }
                            }}
                            style={styles.searchButton}
                            disabled={!pendingQuery.trim()}
                        >
                            <Text style={[styles.searchButtonText, !pendingQuery.trim() && styles.searchButtonTextDisabled]}>
                                Search
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>

                <Animated.View 
                    style={[
                        styles.mainContent,
                        {
                            opacity: pageOpacity,
                            transform: [
                                { translateY: pageTranslateY }
                            ]
                        }
                    ]}
                >
                    <BlurView tint="dark" style={styles.blurContent}>

                        {/* Bet Mode Selector */}
                        <View style={{ alignSelf: 'stretch', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <View style={{ flexDirection: 'row', gap: 8 }}>
                            {['infer','sgp','straight','sgpx'].map((m) => (
                              <TouchableOpacity key={m} onPress={() => { setBetMode(m); if (Platform.OS==='web') { try { window.sessionStorage.setItem('sgp:mode', m); } catch {} } }} style={[styles.modeChip, betMode===m ? styles.modeChipActive : null]}>
                                <Text style={[styles.modeChipText, betMode===m ? styles.modeChipTextActive : null]}>{m==='infer'?'Infer':(m==='sgp'?'SGP':(m==='straight'?'Straight':'SGPx'))}</Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                          
                          {!!lastNotice && (
                            <Text style={{ color: 'rgba(255,255,255,0.7)', marginTop: 8 }}>{lastNotice}</Text>
                          )}
                        </View>

                        {/* Book Override Selector */}
                        <View style={{ alignSelf: 'stretch', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>Books:</Text>
                            <TouchableOpacity 
                              onPress={() => setShowBookSelector(!showBookSelector)}
                              style={[styles.bookOverrideBtn, bookOverride ? styles.bookOverrideBtnActive : null]}
                            >
                              <Text style={[styles.bookOverrideBtnText, bookOverride ? styles.bookOverrideBtnTextActive : null]}>
                                {bookOverride || 'All Books'}
                              </Text>
                            </TouchableOpacity>
                            {bookOverride && (
                              <TouchableOpacity 
                                onPress={() => setBookOverride(null)}
                                style={styles.clearOverrideBtn}
                              >
                                <Text style={styles.clearOverrideBtnText}>×</Text>
                              </TouchableOpacity>
                            )}
                          </View>
                          <TouchableOpacity 
                            onPress={() => handleSaveQuery(pendingQuery)}
                            style={styles.savedRequestsBtn}
                          >
                            <Text style={styles.savedRequestsBtnText}>💾 Save Query</Text>
                          </TouchableOpacity>
                        </View>

                        {/* Book Selector Dropdown */}
                        {showBookSelector && (
                          <View style={styles.bookSelectorDropdown}>
                            <Text style={styles.bookSelectorTitle}>Select Books for Calculation:</Text>
                            <View style={styles.bookList}>
                              {[
                                { key: null, name: 'All Books (Default)' },
                                { key: 'FanDuel', name: 'FanDuel Only' },
                                { key: 'DraftKings', name: 'DraftKings Only' },
                                { key: 'Caesars', name: 'Caesars Only' },
                                { key: 'BetMGM', name: 'BetMGM Only' },
                                { key: 'PointsBet', name: 'PointsBet Only' },
                                { key: 'BetRivers', name: 'BetRivers Only' },
                                { key: 'WynnBet', name: 'WynnBet Only' },
                                { key: 'Barstool', name: 'Barstool Only' },
                                { key: 'FOX Bet', name: 'FOX Bet Only' },
                                { key: 'Unibet', name: 'Unibet Only' },
                                { key: 'bet365', name: 'bet365 Only' },
                                { key: 'Pinnacle', name: 'Pinnacle Only' },
                                { key: 'Circa Sports', name: 'Circa Sports Only' },
                                { key: 'SI Sportsbook', name: 'SI Sportsbook Only' }
                              ].map((book) => (
                                <TouchableOpacity
                                  key={book.key || 'all'}
                                  onPress={() => {
                                    setBookOverride(book.key);
                                    setShowBookSelector(false);
                                  }}
                                  style={[styles.bookOption, bookOverride === book.key ? styles.bookOptionActive : null]}
                                >
                                  <Text style={[styles.bookOptionText, bookOverride === book.key ? styles.bookOptionTextActive : null]}>
                                    {book.name}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            </View>
                          </View>
                        )}
                        
                        {/* Promotion Detected Banner */}
                        {detectedPromotion && detectedPromotion.type && (
                          <View style={{ alignSelf: 'stretch', marginBottom: 12, padding: 12, backgroundColor: 'rgba(139, 92, 246, 0.15)', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#8b5cf6', flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
                            <View style={{ flex: 1 }}>
                              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <Text style={{ fontSize: 20 }}>🎁</Text>
                                <Text style={{ color: '#c4b5fd', fontSize: 14, fontWeight: '700', textTransform: 'uppercase' }}>Promotion Detected</Text>
                                  </View>
                              <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 15, fontWeight: '600', marginBottom: 2 }}>
                                {detectedPromotion.value} {detectedPromotion.type.replace(/_/g, ' ').toUpperCase()}
                              </Text>
                              {detectedPromotion.sportsbook && (
                                <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                                  📱 {detectedPromotion.sportsbook}
                                </Text>
                              )}
                              {detectedPromotion.conditions && (
                                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>
                                  {detectedPromotion.conditions}
                                </Text>
                              )}
                              {detectedPromotion.expiry && (
                                <Text style={{ color: '#fbbf24', fontSize: 12, marginTop: 4 }}>
                                  ⏰ Expires: {detectedPromotion.expiry}
                                </Text>
                              )}
                            </View>
                            <TouchableOpacity onPress={() => setDetectedPromotion(null)} style={{ padding: 4 }}>
                              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }}>✕</Text>
                            </TouchableOpacity>
                          </View>
                        )}
                        
                        {/* Search & Filter Panel */}
                        {fetchedOptions.length > 0 && (
                          <View style={styles.filterPanel}>
                            {/* Header with Stats and Feedback */}
                            <View style={styles.filterHeader}>
                              <View style={{ flex: 1 }}>
                                <Text style={styles.filterTitle}>
                                  {filteredAndSortedOptions.length} of {fetchedOptions.length} Bets
                                </Text>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                                  <Text style={styles.filterSubtitle}>Searched:</Text>
                                  {searchedSportsbooks.slice(0, 3).map((book, idx) => (
                                    <Text key={idx} style={styles.searchedBookTag}>{book}</Text>
                                  ))}
                                  {searchedSportsbooks.length > 3 && (
                                    <Text style={styles.filterSubtitle}>+{searchedSportsbooks.length - 3} more</Text>
                                  )}
                                </View>
                              </View>
                              
                              {/* AI Feedback */}
                              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>How did AI do?</Text>
                                <TouchableOpacity 
                                  onPress={() => handleQueryFeedback('up')}
                                  style={[styles.feedbackButton, queryFeedback === 'up' && styles.feedbackButtonActive]}
                                >
                                  <Text style={{ fontSize: 16 }}>{queryFeedback === 'up' ? '👍' : '👍🏻'}</Text>
                                </TouchableOpacity>
                                <TouchableOpacity 
                                  onPress={() => handleQueryFeedback('down')}
                                  style={[styles.feedbackButton, queryFeedback === 'down' && styles.feedbackButtonActive]}
                                >
                                  <Text style={{ fontSize: 16 }}>{queryFeedback === 'down' ? '👎' : '👎🏻'}</Text>
                                </TouchableOpacity>
                              </View>
                            </View>

                            {/* Search Bar */}
                            <View style={styles.searchContainer}>
                              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 20, marginRight: 8 }}>🔍</Text>
                              <TextInput
                                style={styles.searchInput}
                                value={searchText}
                                onChangeText={setSearchText}
                                placeholder="Search bets by team, player, market..."
                                placeholderTextColor="rgba(255,255,255,0.4)"
                              />
                              {searchText ? (
                                <TouchableOpacity onPress={() => setSearchText('')} style={{ padding: 4 }}>
                                  <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16 }}>✕</Text>
                                </TouchableOpacity>
                              ) : null}
                            </View>

                            {/* Filters Row */}
                            <View style={styles.filtersRow}>
                              {/* Sportsbook Filter */}
                              <View style={styles.filterSection}>
                                <View style={styles.filterSectionHeader}>
                                  <Text style={styles.filterLabel}>Sportsbook</Text>
                                  {filterSportsbooks.length > 0 && (
                                    <TouchableOpacity onPress={() => setFilterSportsbooks([])}>
                                      <Text style={styles.clearButton}>Clear ({filterSportsbooks.length})</Text>
                                    </TouchableOpacity>
                                  )}
                                </View>
                                <View style={styles.filterTagsContainer}>
                                  {availableSportsbooks.length > 0 ? availableSportsbooks.map(book => (
                                    <TouchableOpacity
                                      key={book}
                                      onPress={() => toggleSportsbookFilter(book)}
                                      style={[
                                        styles.filterTag,
                                        filterSportsbooks.includes(book) && styles.filterTagActive
                                      ]}
                                    >
                                      <Text style={[
                                        styles.filterTagText,
                                        filterSportsbooks.includes(book) && styles.filterTagTextActive
                                      ]}>
                                        {book}
                                  </Text>
                                </TouchableOpacity>
                                  )) : <Text style={styles.noFiltersText}>No sportsbooks</Text>}
                              </View>
                        </View>

                              {/* Market Filter */}
                              <View style={styles.filterSection}>
                                <View style={styles.filterSectionHeader}>
                                  <Text style={styles.filterLabel}>Market Type</Text>
                                  {filterMarkets.length > 0 && (
                                    <TouchableOpacity onPress={() => setFilterMarkets([])}>
                                      <Text style={styles.clearButton}>Clear ({filterMarkets.length})</Text>
                                    </TouchableOpacity>
                                  )}
                                </View>
                                <View style={styles.filterTagsContainer}>
                                  {availableMarkets.length > 0 ? availableMarkets.map(market => (
                                    <TouchableOpacity
                                      key={market}
                                      onPress={() => toggleMarketFilter(market)}
                                      style={[
                                        styles.filterTag,
                                        filterMarkets.includes(market) && styles.filterTagActive
                                      ]}
                                    >
                                      <Text style={[
                                        styles.filterTagText,
                                        filterMarkets.includes(market) && styles.filterTagTextActive
                                      ]}>
                                        {market}
                                      </Text>
                                    </TouchableOpacity>
                                  )) : <Text style={styles.noFiltersText}>No markets</Text>}
                                </View>
                              </View>

                              {/* Sort Options */}
                              <View style={styles.filterSection}>
                                <Text style={styles.filterLabel}>Additional Sorting</Text>
                                <View style={styles.filterTagsContainer}>
                                  {[
                                    { value: 'ev', label: 'Best EV' },
                                    { value: 'date', label: 'Date' },
                                    { value: 'price_desc', label: 'Highest Odds' },
                                    { value: 'price_asc', label: 'Lowest Odds' },
                                    { value: 'sportsbooks', label: 'Most Sportsbooks' },
                                    { value: 'market', label: 'Market A-Z' }
                                  ].map(sort => (
                                    <TouchableOpacity
                                      key={sort.value}
                                      onPress={() => {
                                        setSortBy(sort.value);
                                        // Update sortMethod to match if selecting ev or odds
                                        if (sort.value === 'ev') setSortMethod('ev');
                                        else if (sort.value === 'price_desc' || sort.value === 'price_asc') setSortMethod('odds');
                                      }}
                                      style={[
                                        styles.filterTag,
                                        sortBy === sort.value && styles.filterTagActive
                                      ]}
                                    >
                                      <Text style={[
                                        styles.filterTagText,
                                        sortBy === sort.value && styles.filterTagTextActive
                                      ]}>
                                        {sort.label}
                                      </Text>
                                    </TouchableOpacity>
                                  ))}
                                  
                                  {/* Deep Links Only Toggle - in Sort By for quick access */}
                                  <TouchableOpacity
                                    onPress={() => setFilterDeepLinksOnly(!filterDeepLinksOnly)}
                                    style={[
                                      styles.filterTag,
                                      { borderColor: filterDeepLinksOnly ? '#10B981' : 'rgba(16, 185, 129, 0.4)', borderWidth: 1, marginLeft: 8 },
                                      filterDeepLinksOnly && { backgroundColor: 'rgba(16, 185, 129, 0.2)' }
                                    ]}
                                  >
                                    <Text style={[
                                      styles.filterTagText,
                                      { color: filterDeepLinksOnly ? '#10B981' : 'rgba(16, 185, 129, 0.7)' }
                                    ]}>
                                      🔗 Direct Links Only
                                    </Text>
                                  </TouchableOpacity>
                                </View>
                              </View>

                            </View>

                            {/* Clear All Filters */}
                            {(filterSportsbooks.length > 0 || filterMarkets.length > 0 || filterDeepLinksOnly || searchText || sortMethod !== (userProfile?.preferences?.defaultSortMethod || 'ev')) && (
                              <TouchableOpacity onPress={clearAllFilters} style={styles.clearAllButton}>
                                <Text style={styles.clearAllButtonText}>✕ Clear All Filters</Text>
                              </TouchableOpacity>
                            )}
                          </View>
                        )}

                        {/* Guard: if no results yet and we aren't loading, show prompt to search */}
                        {!loading && !fetchedOptions.length && !noResults && !hasStartedSearch ? (
                        <View style={styles.betSlipSection}>
                        <View style={styles.betSlipHeader}>
                            <View style={styles.betSlipTitleContainer}>
                                <View style={styles.betSlipTitle}>
                                  <Text style={styles.betSlipTitleText}>Ready to search</Text>
                                  <Text style={styles.betSlipSubtitle}>Enter a query above to find betting odds.</Text>
                                </View>
                              </View>
                            </View>
                          </View>
                        ) : null}


                        {/* Suggested Legs (from bOPT AI) */}
                        {fetchedOptions.length ? (
                          <View style={styles.betSlipSection}>
                            <View style={styles.betSlipHeader}>
                              <View style={styles.betSlipTitleContainer}>
                                <View style={styles.betSlipTitle}>
                                  <Text style={styles.betSlipTitleText}>Suggested Legs</Text>
                                    <Text style={styles.betSlipSubtitle}>
                                    {pendingQuery ? `Query: ${pendingQuery}` : 'Select up to 3 legs'} (Saved {selectedLegs.length}/{MAX_LEGS}){didBroaden ? ' • broadened' : ''}
                                    </Text>
                                </View>
                                {/* Feedback buttons */}
                                <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', marginLeft: 12 }}>
                                  <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>How did AI do?</Text>
                                  <TouchableOpacity 
                                    onPress={() => handleQueryFeedback('up')}
                                    style={[styles.feedbackButton, queryFeedback === 'up' && styles.feedbackButtonActive]}
                                    activeOpacity={0.7}
                                  >
                                    <Text style={{ fontSize: 16 }}>{queryFeedback === 'up' ? '👍' : '👍🏻'}</Text>
                                  </TouchableOpacity>
                                  <TouchableOpacity 
                                    onPress={() => handleQueryFeedback('down')}
                                    style={[styles.feedbackButton, queryFeedback === 'down' && styles.feedbackButtonActive]}
                                    activeOpacity={0.7}
                                  >
                                    <Text style={{ fontSize: 16 }}>{queryFeedback === 'down' ? '👎' : '👎🏻'}</Text>
                                  </TouchableOpacity>
                                </View>
                            </View>
                            <GradientButton
                                text={`Save BetSlip (${selectedLegs.length}/${computeCap()})`}
                                onPress={handleSaveBetSlip}
                                style={styles.viewBetslipButton}
                                gradientColors={["#707070", "#FFFFFF"]}
                            />
                        </View>
                        <View style={styles.betCardsContainer}>
                              {(() => {
                                // Determine role from user profile
                                const isAdmin = (userProfile?.userType === 'admin');
                                const planType = userProfile?.subscription?.planType || (userProfile?.subscription?.subscriptionStatus === 'active' ? 'ACTIVE' : 'FREE');
                                const isSub = planType && planType !== 'FREE';
                                const canShowMath = true; // Show math for all users
                                const showAllMath = showAllMathLocal || (userProfile?.preferences?.showAllMath || false);
                                
                                // Pagination logic (use filtered and sorted options)
                                const totalItems = filteredAndSortedOptions.length;
                                const totalPages = Math.ceil(totalItems / itemsPerPage);
                                const startIndex = (currentPage - 1) * itemsPerPage;
                                const endIndex = startIndex + itemsPerPage;
                                const paginatedOptions = filteredAndSortedOptions.slice(startIndex, endIndex);
                                
                                // Check if results are SGPs
                                const isSgpResults = fetchedOptions.length > 0 && fetchedOptions[0]?._isCachedSGP;
                                
                                return (
                                  <>
                                    {/* TOP CONTROLS: Pagination + Show All Math Toggle */}
                                    {totalItems > 0 && (
                                      <View style={styles.topControlsContainer}>
                                        {/* Show All/Hide All Math Toggle */}
                                        <TouchableOpacity
                                          onPress={() => setShowAllMathLocal(prev => !prev)}
                                          style={[
                                            styles.showAllMathButton,
                                            showAllMathLocal && styles.showAllMathButtonActive
                                          ]}
                                        >
                                          <Text style={[
                                            styles.showAllMathButtonText,
                                            showAllMathLocal && styles.showAllMathButtonTextActive
                                          ]}>
                                            {showAllMathLocal ? '📊 Hide All Math' : '📊 Show All Math'}
                                          </Text>
                                        </TouchableOpacity>
                                        
                                        {/* Top Pagination */}
                                        {totalPages > 1 && (
                                          <View style={styles.topPaginationContainer}>
                                            <TouchableOpacity 
                                              onPress={() => {
                                                setCurrentPage(p => Math.max(1, p - 1));
                                                if (Platform.OS === 'web') {
                                                  try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
                                                }
                                              }}
                                              disabled={currentPage === 1}
                                              style={[styles.topPaginationButton, currentPage === 1 && styles.topPaginationButtonDisabled]}
                                            >
                                              <Text style={[styles.topPaginationButtonText, currentPage === 1 && styles.topPaginationButtonTextDisabled]}>‹ Prev</Text>
                                            </TouchableOpacity>
                                            <Text style={styles.topPaginationText}>
                                              {currentPage} / {totalPages}
                                            </Text>
                                            <TouchableOpacity 
                                              onPress={() => {
                                                setCurrentPage(p => Math.min(totalPages, p + 1));
                                                if (Platform.OS === 'web') {
                                                  try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
                                                }
                                              }}
                                              disabled={currentPage === totalPages}
                                              style={[styles.topPaginationButton, currentPage === totalPages && styles.topPaginationButtonDisabled]}
                                            >
                                              <Text style={[styles.topPaginationButtonText, currentPage === totalPages && styles.topPaginationButtonTextDisabled]}>Next ›</Text>
                                            </TouchableOpacity>
                                          </View>
                                        )}
                                      </View>
                                    )}
                                    
                                    {/* SGP Results Header */}
                                    {isSgpResults && paginatedOptions.length > 0 && (
                                      <View style={{
                                        backgroundColor: 'rgba(74, 222, 128, 0.1)',
                                        borderWidth: 1,
                                        borderColor: 'rgba(74, 222, 128, 0.3)',
                                        borderRadius: 12,
                                        padding: 16,
                                        marginBottom: 16,
                                      }}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                                          <Text style={{ fontSize: 20, marginRight: 8 }}>🎯</Text>
                                          <Text style={{ color: '#4ADE80', fontSize: 18, fontWeight: '700' }}>
                                            Same Game Parlays
                                          </Text>
                                        </View>
                                        <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
                                          Found {fetchedOptions.length} top SGP combinations
                                          {sgpGenerationStats?.combinationsTested > 0 && (
                                            <Text style={{ color: 'rgba(255,255,255,0.5)' }}>
                                              {' '}from {sgpGenerationStats.combinationsTested.toLocaleString()} analyzed
                                        </Text>
                                          )}
                                          . Each card shows a complete parlay you can bet on.
                                        </Text>
                                        {sgpGenerationStats?.marketsUsed > 0 && (
                                          <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 4 }}>
                                            📊 {sgpGenerationStats.marketsUsed} betting markets evaluated
                                          </Text>
                                        )}
                                        <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 8 }}>
                                          💡 Click "Place Bet" to open the sportsbook with your parlay pre-loaded
                                        </Text>
                                      </View>
                                    )}
                                    
                                    {/* Show all API results if filters would eliminate them */}
                                    {filteredAndSortedOptions.length === 0 && fetchedOptions.length > 0 && !didBroaden && (() => {
                                      // ⭐ Special case: Deep links filter active but no results have deep links
                                      if (filterDeepLinksOnly) {
                                        return (
                                          <View style={{ padding: 24, alignItems: 'center', backgroundColor: 'rgba(16, 185, 129, 0.08)', borderRadius: 12, marginBottom: 16, borderWidth: 1, borderColor: 'rgba(16, 185, 129, 0.2)' }}>
                                            <Text style={{ color: '#10B981', fontSize: 16, textAlign: 'center', marginBottom: 8, fontWeight: '600' }}>
                                              🔗 No Direct Bet Links Available
                                            </Text>
                                            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, textAlign: 'center', marginBottom: 12, lineHeight: 20 }}>
                                              This data was cached before direct bet links were stored.{'\n'}
                                              Trigger a cache refresh from Admin Panel to get direct links.
                                            </Text>
                                            <TouchableOpacity
                                              onPress={() => setFilterDeepLinksOnly(false)}
                                              style={{ paddingHorizontal: 16, paddingVertical: 8, backgroundColor: 'rgba(16, 185, 129, 0.2)', borderRadius: 6 }}
                                            >
                                              <Text style={{ color: '#10B981', fontSize: 14, fontWeight: '500' }}>
                                                Show All Results ({fetchedOptions.length})
                                              </Text>
                                            </TouchableOpacity>
                                          </View>
                                        );
                                      }
                                      
                                      // Default: Just show all results without filtering (don't call API again)
                                      setTimeout(() => {
                                        if (filteredAndSortedOptions.length === 0 && fetchedOptions.length > 0 && !didBroaden) {
                                          console.log('[SGP] Filters eliminated all results, showing all', fetchedOptions.length, 'available');
                                          setDidBroaden(true);
                                        }
                                      }, 100);
                                      return (
                                        <View style={{ padding: 24, alignItems: 'center' }}>
                                          <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 16, textAlign: 'center', marginBottom: 12 }}>
                                            Showing all available results
                                          </Text>
                                          <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, textAlign: 'center' }}>
                                            {isSgpResults ? 'Cache' : 'API'} returned {fetchedOptions.length} results, displaying all matches
                                          </Text>
                                        </View>
                                      );
                                    })()}
                                    
                                    {/* Results - Use SGPCard for cached SGPs, LegBetCard for straight bets */}
                                    {paginatedOptions.map((option) => (
                                        option._isCachedSGP ? (
                                            // Render SGPCard for Same Game Parlays
                                            <SGPCard
                                                key={option.id}
                                                sgp={option}
                                                isSelected={!!selectedLegs.find(l => l.id === option.id)}
                                                onSelect={(opt) => handleAddFetchedLeg(opt)}
                                                onAddToBetSlip={(opt) => handleAddFetchedLeg(opt)}
                                                unitSize={userProfile?.preferences?.unitSize || 10}
                                            />
                                        ) : (
                                            // Render LegBetCard for straight bets
                                <LegBetCard
                                    key={option.id}
                                    option={option}
                                        index={0}
                                        isSelected={!!selectedLegs.find(l => l.id === option.id)}
                                        onSelect={(opt) => handleAddFetchedLeg(opt)}
                                        onWatchFixture={(opt) => handleWatchFixture(opt)}
                                        onUnwatchFixture={(opt) => handleUnwatchFixture(opt)}
                                        isWatching={watchedFixtures.has(option?._fixtureId || option?.raw?.fixture_id)}
                                        kellyCriterion={userProfile?.preferences?.kellyCriterion || 0.25}
                                                userSelectedSportsbooks={userProfile?.preferences?.selectedSportsbooks || []}
                                        marketPeers={getMarketPeers(option)}
                                        onOpenDeepLink={(opt) => {
                                          console.log('[DeepLink] 🔗 Button clicked!', {
                                            hasDeepLink: !!opt?._deepLink,
                                            deepLinkValue: opt?._deepLink,
                                            selectedBook: opt?._selectedBook,
                                            rawUrls: opt?.raw?.urls
                                          });
                                          try {
                                            // ⭐ FIX: Use _deepLink passed from LegBetCard (already extracted)
                                            let url = opt?._deepLink;
                                            
                                            // Handle object format { desktop, ios, android }
                                            if (url && typeof url === 'object') {
                                              url = url.desktop || url.ios || url.android || null;
                                            }
                                            
                                            // Fallback to legacy urls object
                                            if (!url) {
                                            const u = opt?.raw?.urls || {};
                                              url = u.offer || u.deeplink || u.event || u.market || null;
                                            }
                                            
                                            console.log('[DeepLink] 🔗 Final URL to open:', url);
                                            
                                            if (url && typeof url === 'string' && url.startsWith('http')) {
                                              window.open(url, '_blank', 'noopener,noreferrer');
                                            } else {
                                              console.warn('[DeepLink] ⚠️ No valid URL to open:', url);
                                            }
                                          } catch (err) {
                                            console.error('[DeepLink] ❌ Error opening deep link:', err);
                                          }
                                        }}
                                        canShowMath={canShowMath}
                                        showAllMath={showAllMath}
                                        unitSize={userProfile?.preferences?.unitSize || 10}
                                        sportsbookLogoUrl={(option?.sportsbookLogoUrl || option?.raw?.sportsbook_logo || option?.raw?.book_logo || null)}
                                        homeLogoUrl={(option?.homeLogoUrl || option?.raw?.home_logo || option?.raw?.home_team_logo || null)}
                                        awayLogoUrl={(option?.awayLogoUrl || option?.raw?.away_logo || option?.raw?.away_team_logo || null)}
                                        teamLogoUrl={(option?.teamLogoUrl || option?.raw?.team_logo || null)}
                                />
                                        )
                            ))}
                                    
                                    {/* Pagination Footer */}
                                    {totalItems > itemsPerPage && (
                                      <View style={styles.paginationContainer}>
                                    <TouchableOpacity 
                                          onPress={() => {
                                            setCurrentPage(p => Math.max(1, p - 1));
                                            // Scroll to top of page when paginating
                                            if (Platform.OS === 'web') {
                                              try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
                                            }
                                          }}
                                          disabled={currentPage === 1}
                                          style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
                                        >
                                          <Text style={[styles.paginationButtonText, currentPage === 1 && styles.paginationButtonTextDisabled]}>Previous</Text>
                                    </TouchableOpacity>
                                        <Text style={styles.paginationText}>
                                          Page {currentPage} of {totalPages} ({totalItems} total legs)
                                        </Text>
                                        <TouchableOpacity 
                                          onPress={() => {
                                            setCurrentPage(p => Math.min(totalPages, p + 1));
                                            // Scroll to top of page when paginating
                                            if (Platform.OS === 'web') {
                                              try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
                                            }
                                          }}
                                          disabled={currentPage === totalPages}
                                          style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
                                        >
                                          <Text style={[styles.paginationButtonText, currentPage === totalPages && styles.paginationButtonTextDisabled]}>Next</Text>
                                        </TouchableOpacity>
                            </View>
                                    )}
                                  </>
                                );
                              })()}
                            </View>
                            <View style={{ alignSelf: 'stretch', marginTop: 8, flexDirection: 'row', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
                              <TouchableOpacity onPress={() => setShowRawLog(v => !v)}>
                                <Text style={{ color: 'rgba(255,255,255,0.6)' }}>{showRawLog ? 'Hide' : 'Show'} raw log</Text>
                              </TouchableOpacity>
                              {noResults ? (
                                <View style={{ flexDirection: 'row', gap: 12 }}>
                                  <GradientButton text="Broaden Search" onPress={requestBroaden} style={styles.footerButton} />
                                </View>
                              ) : (
                                <GradientButton text={`Review BetSlip (${selectedLegs.length})`} onPress={() => setShowBetSlipModal(true)} style={styles.footerButton} />
                              )}
                            </View>

                            {/* Sticky Builder Bar */}
                            {/* sticky footer moved outside */}
                            {showRawLog ? (
                              <View style={{ alignSelf: 'stretch', backgroundColor: 'rgba(255,255,255,0.04)', padding: 12, borderRadius: 8 }}>
                                <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                                  {String(lastRawRef.current).slice(0, 4000)}
                                </Text>
                              </View>
                            ) : null}
                          </View>
                        ) : hasStartedSearch ? (
                          // No results state (only show if we've actually searched)
                          <View style={styles.betSlipSection}>
                            <View style={styles.betSlipHeader}>
                              <View style={styles.betSlipTitleContainer}>
                                <View style={styles.betSlipTitle}>
                                  <Text style={styles.betSlipTitleText}>
                                    {fixturesFoundNoOdds ? `Found ${fixturesFoundNoOdds.teamName || 'games'}` : 'No legs found'}
                                  </Text>
                                  <Text style={styles.betSlipSubtitle}>
                                    {fixturesFoundNoOdds 
                                      ? `We found ${fixturesFoundNoOdds.fixtureCount} upcoming game${fixturesFoundNoOdds.fixtureCount !== 1 ? 's' : ''}, but no odds posted yet. Please check back later.`
                                      : 'We didn\'t find odds matching your query. Would you like us to broaden automatically?'
                                    }
                                  </Text>
                                </View>
                              </View>
                              {fixturesFoundNoOdds ? (
                                <GradientButton
                                  text="Save Query"
                                  onPress={() => handleSaveQuery(pendingQuery)}
                                  style={styles.viewBetslipButton}
                                  gradientColors={["#8b5cf6", "#a78bfa"]}
                                />
                              ) : (
                                    <GradientButton
                                text="Broaden Search"
                                onPress={requestBroaden}
                                style={styles.viewBetslipButton}
                                gradientColors={["#707070", "#FFFFFF"]}
                              />
                              )}
                            </View>
                            <View style={{ alignSelf: 'stretch', marginTop: 8, flexDirection: 'row', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
                              <TouchableOpacity onPress={() => setShowRawLog(v => !v)}>
                                <Text style={{ color: 'rgba(255,255,255,0.6)' }}>{showRawLog ? 'Hide' : 'Show'} raw log</Text>
                              </TouchableOpacity>
                              <GradientButton text="Back to Search" onPress={goBack} style={styles.footerButton} />
                        </View>
                            {showRawLog ? (
                              <View style={{ alignSelf: 'stretch', backgroundColor: 'rgba(255,255,255,0.04)', padding: 12, borderRadius: 8 }}>
                                <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                                  {String(lastRawRef.current).slice(0, 4000)}
                                </Text>
                    </View>
                            ) : null}
                          </View>
                        ) : null}

                    </BlurView>
                </Animated.View>
            </View>

            {/* Sticky footer (fixed to viewport bottom) */}
            {selectedLegs.length > 0 && !loading ? (
              <View style={{ position: 'fixed', bottom: 0, left: 0, right: 0, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.12)', paddingTop: 8, paddingBottom: 10, paddingHorizontal: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#000', zIndex: 50 }}>
                <View style={{ flex: 1, flexDirection: 'row', gap: 16, alignItems: 'center' }}>
                  <Text style={{ color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}>Selected: {selectedLegs.length} / {computeCap()}</Text>
                  {(() => {
                    const math = calculateSgpMath(selectedLegs, builderStake, betMode);
                    if (!math || selectedLegs.length < 2) return null;
                    return (
                      <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
                        {math.combinedAmerican !== null ? (
                          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
                            {math.betType}: <Text style={{ fontWeight: '700' }}>{math.combinedAmerican > 0 ? '+' : ''}{math.combinedAmerican}</Text>
                            {math.betType === 'SGPx' && (
                              <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                                {' '}({math.sgpLegs}SGP+{math.straightLegs}Straight)
                              </Text>
                            )}
                          </Text>
                        ) : null}
                        {math.payout !== null ? (
                          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
                            Payout: <Text style={{ fontWeight: '700', color: '#4ade80' }}>${math.payout.toFixed(2)}</Text>
                          </Text>
                        ) : null}
                        {math.evPercent !== null ? (
                          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
                            EV: <Text style={{ fontWeight: '700', color: math.evPercent >= 0 ? '#4ade80' : '#f87171' }}>{math.evPercent >= 0 ? '+' : ''}{math.evPercent.toFixed(2)}%</Text>
                          </Text>
                        ) : null}
                      </View>
                    );
                  })()}
                  <TouchableOpacity onPress={() => setShowRawLog(v => !v)}>
                    <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>{showRawLog ? 'Hide' : 'Show'} raw log</Text>
                  </TouchableOpacity>
                </View>
                <View style={{ flexDirection: 'row', gap: 12 }}>
                  <GradientButton text="Review BetSlip" onPress={() => setShowBetSlipModal(true)} style={[styles.footerButton, { flex: 1 }]} />
                  <GradientButton text="Share" onPress={handleShareBetSlip} style={[styles.footerButton, { flex: 1, backgroundColor: 'rgba(139, 92, 246, 0.15)', borderColor: '#8b5cf6' }]} />
                </View>
              </View>
            ) : null}
            
            <BetSlipModal
                visible={showBetSlipModal}
                onClose={() => setShowBetSlipModal(false)}
                onSubmit={handleBetSlipSubmission}
                selectedOptions={selectedOptions}
                selectedLegs={selectedLegs}
                stake={builderStake}
            />
            
            <QueryLimitModal
                visible={showQueryLimitModal}
                onClose={() => setShowQueryLimitModal(false)}
                planType={queryUsage.planType}
                queriesUsed={queryUsage.queriesUsed}
                queryLimit={queryUsage.queryLimit}
            />
            
            {showSubmissionScreen && (
                <BetSlipSubmissionScreen
                    onComplete={handleSubmissionComplete}
                    onSave={handleSaveBetSlip}
                    selectedLegs={selectedLegs}
                />
            )}
            
            <SavedRequestsModal
                visible={showSavedRequests}
                onClose={() => setShowSavedRequests(false)}
                onApplyRequest={handleApplySavedRequest}
                onSaveRequest={handleSaveRequest}
                userProfile={userProfile}
                currentQuery={pendingQuery}
            />
            
            {/* Share Modal */}
            {showShareModal && (
                <Modal
                    visible={showShareModal}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => setShowShareModal(false)}
                >
                    <View style={styles.shareModalOverlay}>
                        <BlurView intensity={60} tint="dark" style={styles.shareModalBlur}>
                            <View style={styles.shareModalContainer}>
                                <GlassBackgroundGradient borderRadius={20} />
                                <GradientOverlay opacity={0.3} borderRadius={20} />
                                
                                <View style={styles.shareModalContent}>
                                    <GradientText style={styles.shareModalTitle}>
                                        Share Your BetSlip
                                    </GradientText>
                                    <Text style={styles.shareModalDescription}>
                                        Copy this link to share your betslip with others
                                    </Text>
                                    
                                    <View style={styles.shareUrlContainer}>
                                        <TextInput
                                            style={styles.shareUrlInput}
                                            value={shareUrl}
                                            editable={false}
                                            multiline
                                        />
                                        <TouchableOpacity
                                            onPress={handleCopyShareUrl}
                                            style={styles.copyButton}
                                        >
                                            <Text style={styles.copyButtonText}>Copy</Text>
                                        </TouchableOpacity>
                                    </View>
                                    
                                    <TouchableOpacity
                                        onPress={() => setShowShareModal(false)}
                                        style={styles.shareCloseButton}
                                    >
                                        <Text style={styles.shareCloseButtonText}>Close</Text>
                                    </TouchableOpacity>
                                </View>
                            </View>
                        </BlurView>
                    </View>
                </Modal>
            )}
        </View>
    );
};

const { width: screenWidth } = Dimensions.get('window');
const isMobile = screenWidth < 768;
const isTablet = screenWidth >= 768 && screenWidth < 1024;

// Responsive helper function
const responsive = (mobile, tablet, desktop) => {
  if (isMobile) return mobile;
  if (isTablet) return tablet;
  return desktop;
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#131313',
        minHeight: Platform.OS === 'web' ? '110vh' : '100%',
    },
    background: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#131313',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
    },
    splashContainer: {
        flex: 1,
        width: '100%',
        height: '100%',
        paddingHorizontal: responsive(12, 24, Platform.OS === 'web' ? 48 : 24),
        paddingTop: responsive(12, 20, Platform.OS === 'web' ? 32 : 16),
        paddingBottom: responsive(16, 32, Platform.OS === 'web' ? 48 : 24),
        gap: responsive(16, 32, 48),
    },
    mainContent: {
        alignItems: 'center',
        width: '100%',
        gap: responsive(12, 18, 24),
        flex: 1,
        borderRadius: responsive(12, 16, 20),
        backgroundColor: '#000',
        borderWidth: 1,
        borderBottomWidth: 2,
        borderTopColor: 'rgba(255,255,255,0.25)',
        borderLeftColor: 'rgba(255,255,255,0.18)',
        borderRightColor: 'rgba(255,255,255,0.12)',
        borderBottomColor: 'rgba(255,255,255,0.08)',
    },
    blurContent: {
        alignItems: 'center',
        width: '100%',
        gap: responsive(12, 18, 24),
        flex: 1,
        borderRadius: responsive(12, 16, 20),
        paddingHorizontal: responsive(12, 24, 48),
        paddingVertical: responsive(12, 16, 20),
    },
    modeChip: {
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.18)'
    },
    modeChipActive: {
        backgroundColor: 'rgba(255,255,255,0.18)',
        borderColor: 'rgba(255,255,255,0.28)'
    },
    modeChipText: { color: 'rgba(255,255,255,0.85)', fontWeight: '600' },
    modeChipTextActive: { color: '#000' },
    backButtonContainer: {
        alignSelf: 'flex-start',
        marginBottom: 8,
    },
    backButton: {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 8,
        paddingBottom: 8,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.20)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    // Bet Slip Section Styles
    betSlipSection: {
        width: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        borderRadius: 20,
        gap: 32,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    betSlipHeader: {
        alignSelf: 'stretch',
        flex: 1,
        paddingTop: 24,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 32,
    },
    betSlipTitleContainer: {
        alignSelf: 'stretch',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 32,
        flexDirection: 'row',
    },
    betSlipTitle: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 5,
    },
    betSlipTitleText: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 20,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 24,
    },
    betSlipSubtitle: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 16,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 24,
    },
    viewBetslipButton: {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 8,
        paddingBottom: 8,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.20)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    betCardsContainer: {
        alignSelf: 'stretch',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 16
    },
    selectedButton: {
        backgroundColor: 'rgba(0, 0, 0, 0)',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        borderRadius: 4,
        borderWidth: 2,
        borderColor: 'rgba(255, 255, 255, 0)',
    },
    betSlipFooter: {
        alignSelf: 'stretch',
        height: 36,
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexDirection: 'row',
    },
    legNumbers: {
        flex: 1,
        maxWidth: 900,
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        flexDirection: 'row',
    },
    legNumber: {
        position: 'relative',
        padding: 8,
        borderRadius: 4,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: 'transparent',
        overflow: 'hidden',
    },
    activeLegNumber: {
        // background gradient handled via overlays
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25) inset',
        borderTopColor: 'rgba(255, 255, 255, 0.20)',
        borderLeftColor: 'rgba(255, 255, 255, 0.09)',
        borderRightColor: 'rgba(255, 255, 255, 0.12)',
        borderBottomColor: 'rgba(255, 255, 255, 0.06)',
    },
    legNumberText: {
        width: 20,
        textAlign: 'center',
        color: '#707070',
        fontSize: 15,
        fontFamily: 'ShantellSans',
        fontWeight: '500',
        lineHeight: 20,
        zIndex: 1,
    },
    addLegButton: {
        padding: 8,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderRadius: 4,
        justifyContent: 'center',
        alignItems: 'center',
    },
    addLegIcon: {
        width: 20,
        height: 20,
        textAlign: 'center',
        color: 'rgba(255, 255, 255, 0.40)',
        fontSize: 15,
        fontFamily: 'Shantell Sans',
        fontWeight: '500',
        lineHeight: 20,
    },
    footerActions: {
        width: 319,
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 20,
        flexDirection: 'row',
    },
    footerButton: {
        // GradientButton handles its own styling
    },
    paginationContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 16,
        marginTop: 16,
        marginBottom: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        borderRadius: 8,
        alignSelf: 'stretch',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    paginationButton: {
        paddingVertical: 8,
        paddingHorizontal: 16,
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    paginationButtonDisabled: {
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        borderColor: 'rgba(255, 255, 255, 0.05)',
    },
    paginationButtonText: {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '500',
    },
    paginationButtonTextDisabled: {
        color: 'rgba(255, 255, 255, 0.3)',
    },
    paginationText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '500',
    },
    // ⭐ TOP CONTROLS: Pagination + Show All Math
    topControlsContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginBottom: 12,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        borderRadius: 10,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        gap: 12,
    },
    showAllMathButton: {
        paddingVertical: 8,
        paddingHorizontal: 14,
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    showAllMathButtonActive: {
        backgroundColor: 'rgba(74, 222, 128, 0.2)',
        borderColor: 'rgba(74, 222, 128, 0.5)',
    },
    showAllMathButtonText: {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: 13,
        fontWeight: '600',
    },
    showAllMathButtonTextActive: {
        color: '#4ADE80',
    },
    topPaginationContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    topPaginationButton: {
        paddingVertical: 6,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    topPaginationButtonDisabled: {
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        borderColor: 'rgba(255, 255, 255, 0.05)',
    },
    topPaginationButtonText: {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: 13,
        fontWeight: '500',
    },
    topPaginationButtonTextDisabled: {
        color: 'rgba(255, 255, 255, 0.3)',
    },
    topPaginationText: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: 13,
        fontWeight: '500',
        minWidth: 50,
        textAlign: 'center',
    },
    rocketImage: {
        width: 400,
        height: 400,
        position: 'absolute',
        right: -50,
        top: -50,
        transform: [{ rotate: '-16deg' }],
    },
    feedbackButton: {
        padding: 8,
        borderRadius: 6,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        minWidth: 36,
        alignItems: 'center',
        justifyContent: 'center',
    },
    feedbackButtonActive: {
        backgroundColor: 'rgba(74, 222, 128, 0.15)',
        borderColor: '#4ade80',
    },
    // Filter Panel Styles
    filterPanel: {
        alignSelf: 'stretch',
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
        padding: 16,
        marginBottom: 16,
        gap: 16,
    },
    filterHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.08)',
    },
    filterTitle: {
        color: '#fff',
        fontSize: 18,
        fontWeight: '700',
    },
    filterSubtitle: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 12,
    },
    searchedBookTag: {
        color: '#4ade80',
        fontSize: 11,
        fontWeight: '600',
        backgroundColor: 'rgba(74, 222, 128, 0.12)',
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.12)',
        paddingHorizontal: 12,
        paddingVertical: 10,
    },
    searchInput: {
        flex: 1,
        color: '#fff',
        fontSize: 14,
        fontWeight: '500',
    },
    filtersRow: {
        gap: 16,
    },
    filterSection: {
        gap: 8,
    },
    filterSectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    filterLabel: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 13,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    clearButton: {
        color: '#f87171',
        fontSize: 11,
        fontWeight: '600',
    },
    filterTagsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
    },
    filterTag: {
        paddingVertical: 7,
        paddingHorizontal: 12,
        borderRadius: 6,
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    filterTagActive: {
        backgroundColor: 'rgba(74, 222, 128, 0.2)',
        borderColor: '#4ade80',
    },
    filterTagText: {
        color: 'rgba(255, 255, 255, 0.85)',
        fontSize: 12,
        fontWeight: '600',
    },
    filterTagTextActive: {
        color: '#4ade80',
    },
    noFiltersText: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 12,
        fontStyle: 'italic',
    },
    clearAllButton: {
        alignSelf: 'center',
        paddingVertical: 8,
        paddingHorizontal: 16,
        backgroundColor: 'rgba(248, 113, 113, 0.1)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(248, 113, 113, 0.3)',
    },
    clearAllButtonText: {
        color: '#f87171',
        fontSize: 13,
        fontWeight: '700',
    },
    // Book Override Styles
    bookOverrideBtn: {
        paddingVertical: 6,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    bookOverrideBtnActive: {
        backgroundColor: 'rgba(74, 222, 128, 0.15)',
        borderColor: '#4ade80',
    },
    bookOverrideBtnText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 13,
        fontWeight: '500',
    },
    bookOverrideBtnTextActive: {
        color: '#4ade80',
        fontWeight: '600',
    },
    clearOverrideBtn: {
        paddingVertical: 4,
        paddingHorizontal: 8,
        backgroundColor: 'rgba(248, 113, 113, 0.1)',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: 'rgba(248, 113, 113, 0.3)',
    },
    clearOverrideBtnText: {
        color: '#f87171',
        fontSize: 16,
        fontWeight: '700',
    },
    bookSelectorDropdown: {
        position: 'absolute',
        top: 60,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
        padding: 12,
        zIndex: 1000,
        maxHeight: 300,
    },
    bookSelectorTitle: {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: 14,
        fontWeight: '600',
        marginBottom: 8,
    },
    bookList: {
        maxHeight: 200,
    },
    bookOption: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 6,
        marginBottom: 4,
    },
    bookOptionActive: {
        backgroundColor: 'rgba(74, 222, 128, 0.15)',
        borderWidth: 1,
        borderColor: '#4ade80',
    },
    bookOptionText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 13,
    },
    bookOptionTextActive: {
        color: '#4ade80',
        fontWeight: '600',
    },
    savedRequestsBtn: {
        paddingVertical: 6,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(139, 92, 246, 0.15)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#8b5cf6',
    },
    savedRequestsBtnText: {
        color: '#8b5cf6',
        fontSize: 13,
        fontWeight: '600',
    },
    // Share Modal Styles
    shareModalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    shareModalBlur: {
        width: '100%',
        maxWidth: 400,
        borderRadius: 20,
        overflow: 'hidden',
    },
    shareModalContainer: {
        position: 'relative',
        padding: 24,
    },
    shareModalContent: {
        position: 'relative',
        zIndex: 2,
    },
    shareModalTitle: {
        fontSize: 24,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 8,
    },
    shareModalDescription: {
        fontSize: 16,
        color: 'rgba(255, 255, 255, 0.8)',
        textAlign: 'center',
        marginBottom: 20,
    },
    shareUrlContainer: {
        marginBottom: 20,
    },
    shareUrlInput: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        fontSize: 12,
        color: 'rgba(255, 255, 255, 0.8)',
        marginBottom: 12,
        minHeight: 60,
    },
    copyButton: {
        alignSelf: 'center',
        paddingVertical: 8,
        paddingHorizontal: 16,
        backgroundColor: 'rgba(74, 222, 128, 0.15)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#4ade80',
    },
    copyButtonText: {
        color: '#4ade80',
        fontSize: 14,
        fontWeight: '600',
    },
    shareCloseButton: {
        height: 48,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    shareCloseButtonText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 16,
        fontWeight: '500',
    },
    searchBarContainer: {
        width: '100%',
        paddingHorizontal: isMobile ? 8 : (Platform.OS === 'web' ? 16 : 12),
        marginBottom: isMobile ? 12 : 16,
    },
    searchInputWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: isMobile ? 8 : 12,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        borderRadius: isMobile ? 10 : 12,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.15)',
        paddingHorizontal: isMobile ? 12 : 16,
        paddingVertical: isMobile ? 10 : 12,
    },
    searchInput: {
        flex: 1,
        fontFamily: 'Slate',
        fontSize: Platform.OS === 'web' ? 16 : 15,
        fontWeight: '500',
        color: 'rgba(255, 255, 255, 0.9)',
        ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
    },
    searchButton: {
        paddingVertical: 8,
        paddingHorizontal: 16,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    searchButtonText: {
        fontFamily: 'Slate',
        fontSize: 14,
        fontWeight: '700',
        color: 'rgba(255, 255, 255, 0.9)',
    },
    searchButtonTextDisabled: {
        color: 'rgba(255, 255, 255, 0.3)',
    },
});

export default SgpFlowScreen;
